import type { AgentAchievementsResponse } from '../dialogs/admin/agentAchievementTypes';
import { isDevLoginMode } from '../../config/env';
import { ensureScrapeGrant } from '../../project-essential/services/api';
import type { SitePlanFloorGroups } from '../components/siteplan/sitePlanFloorGroups';
import type { PublicAgent } from '../../share-public/sharePublicApi';

// API routing modes:
// - Cloud Functions direct URL: REACT_APP_API_URL=https://...cloudfunctions.net/api
//   -> API_BASE=https://.../api/api, AUTH_BASE=https://.../api
// - Hosting proxy (same-origin): REACT_APP_API_URL=/
//   -> API_BASE=/api, AUTH_BASE=
const RAW_API_URL = process.env.REACT_APP_API_URL || '/';
const API_ROOT = RAW_API_URL.replace(/\/+$/, '');
const USE_HOSTING_PROXY = API_ROOT === '' || API_ROOT === '/';
const FUNCTION_BASE = USE_HOSTING_PROXY ? '' : (API_ROOT.endsWith('/api') ? API_ROOT : `${API_ROOT}/api`);
const API_BASE = USE_HOSTING_PROXY ? '/api' : `${FUNCTION_BASE}/api`;
const AUTH_BASE = USE_HOSTING_PROXY ? '' : FUNCTION_BASE;

type ApiError = Error & { code?: string; status?: number };

function createApiError(status: number, payload: any, fallbackMessage: string): ApiError {
  const message = typeof payload?.error === 'string' ? payload.error : fallbackMessage;
  const error = new Error(message) as ApiError;
  if (typeof payload?.code === 'string') {
    error.code = payload.code;
  }
  error.status = status;
  return error;
}

type UnauthorizedReason = 'invalid_user' | 'no_access' | 'token_expired' | 'unauthorized';
export type DevLoginApp = 'super-picker' | 'super-calculator' | 'super-connector' | 'communication' | 'project-essential' | 'super-personalized-psf';

const SUPER_CALCULATOR_ROUTE_ALIASES = new Set(['purchase-planner', 'mortgage-loan', 'profile-manager', 'affordability']);
const DEV_LOGIN_SESSION_LOSS_CODES = new Set([
  'ACCESS_TOKEN_MISSING',
  'ACCESS_TOKEN_EXPIRED',
  'REFRESH_TOKEN_EXPIRED',
  'REFRESH_TOKEN_REVOKED',
  'REFRESH_TOKEN_MISSING',
]);

function mapCodeToUnauthorizedReason(code?: string): UnauthorizedReason {
  switch (code) {
    case 'INVALID_USER':
      return 'invalid_user';
    case 'NO_SUPER_PICKER_ACCESS':
    case 'NO_SUPER_CALCULATOR_ACCESS':
    case 'NO_SUPER_CONNECTOR_ACCESS':
    case 'NO_COMMUNICATION_ACCESS':
    case 'NO_PROJECT_ESSENTIAL_ACCESS':
    case 'NO_PROJECT_DECK_ACCESS':
    case 'NO_SUPER_PERSONALIZED_PSF_ACCESS':
      return 'no_access';
    case 'SSO_TOKEN_EXPIRED':
    case 'ACCESS_TOKEN_MISSING':
    case 'ACCESS_TOKEN_EXPIRED':
    case 'REFRESH_TOKEN_EXPIRED':
    case 'REFRESH_TOKEN_REVOKED':
    case 'REFRESH_TOKEN_MISSING':
      return 'token_expired';
    default:
      return 'unauthorized';
  }
}

export function isDevLoginSessionLossCode(code?: string): boolean {
  if (typeof code !== 'string') return false;
  return DEV_LOGIN_SESSION_LOSS_CODES.has(code.trim().toUpperCase());
}

export function normalizeDevLoginApp(app?: string | null): DevLoginApp | null {
  if (typeof app !== 'string') return null;
  const normalized = app.trim().toLowerCase().replace(/^\/+/, '');
  const routeKey = normalized.split(/[?#]/, 1)[0];

  if (
    routeKey === 'super-calculator' ||
    routeKey === 'super_calculator' ||
    SUPER_CALCULATOR_ROUTE_ALIASES.has(routeKey) ||
    routeKey.startsWith('super-calculator/')
  ) {
    return 'super-calculator';
  }

  if (routeKey === 'super-connector' || routeKey === 'super_connector' || routeKey.startsWith('super-connector/')) {
    return 'super-connector';
  }

  // Super Pipeline rides on the Super Picker entitlement (the backend maps
  // Project Deck uses the Super Picker session entitlement while its own API
  // routes perform the stricter deck-admin check.
  if (
    routeKey === 'project-deck'
    || routeKey === 'project_deck'
    || routeKey.startsWith('project-deck/')
  ) {
    return 'super-picker';
  }

  if (routeKey === 'super-picker' || routeKey === 'super_picker' || routeKey.startsWith('super-picker/')) {
    return 'super-picker';
  }

  if (routeKey === 'communication' || routeKey.startsWith('communication/')) {
    return 'communication';
  }

  if (
    routeKey === 'project-essential' ||
    routeKey === 'project_essential' ||
    routeKey.startsWith('project-essential/')
  ) {
    return 'project-essential';
  }

  if (
    routeKey === 'super-personalized-psf' ||
    routeKey === 'super_personalized_psf' ||
    routeKey.startsWith('super-personalized-psf/')
  ) {
    return 'super-personalized-psf';
  }

  return null;
}

export function resolveProtectedAppFromPath(pathname: string): DevLoginApp {
  return normalizeDevLoginApp(pathname) ?? 'super-picker';
}

export function getDefaultAppPath(app: DevLoginApp): string {
  if (app === 'super-calculator') return '/super-calculator';
  if (app === 'super-connector') return '/super-connector';
  if (app === 'communication') return '/communication';
  if (app === 'project-essential') return '/project-essential';
  if (app === 'super-personalized-psf') return '/super-personalized-psf';
  return '/super-picker';
}

export function normalizeDevLoginRedirect(redirect?: string | null, fallbackApp?: DevLoginApp): string {
  const resolvedApp = fallbackApp ?? 'super-picker';
  const fallbackPath = getDefaultAppPath(resolvedApp);
  if (typeof redirect !== 'string' || redirect.trim().length === 0) {
    return fallbackPath;
  }

  const trimmed = redirect.trim();
  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const lowercasePath = normalized.toLowerCase();
  if (
    normalized.startsWith('//') ||
    lowercasePath === '/sso' ||
    lowercasePath.startsWith('/sso?') ||
    lowercasePath === '/unauthorized' ||
    lowercasePath.startsWith('/unauthorized?') ||
    lowercasePath === '/dev-login' ||
    lowercasePath.startsWith('/dev-login?')
  ) {
    return fallbackPath;
  }

  const resolvedRedirectApp = normalizeDevLoginApp(normalized);
  if (!resolvedRedirectApp) {
    return fallbackPath;
  }
  if (resolvedRedirectApp !== resolvedApp) {
    return fallbackPath;
  }

  return normalized;
}

export function buildUnauthorizedUrl(code?: string, extraParams?: Record<string, string | null | undefined>): string {
  const reason = mapCodeToUnauthorizedReason(code);
  const params = new URLSearchParams();
  params.set('reason', reason);
  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (typeof value === 'string' && value.length > 0) {
        params.set(key, value);
      }
    });
  }
  return `/unauthorized?${params.toString()}`;
}

function getCurrentRedirectPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function buildDevLoginUrl(extraParams?: { app?: string | null | undefined; redirect?: string | null | undefined }): string {
  const app = normalizeDevLoginApp(extraParams?.app) ?? resolveProtectedAppFromPath(window.location.pathname);
  const redirect = normalizeDevLoginRedirect(extraParams?.redirect ?? getCurrentRedirectPath(), app);
  const params = new URLSearchParams();
  params.set('app', app);
  params.set('redirect', redirect);
  return `/dev-login?${params.toString()}`;
}

function redirectToDevLogin(app?: string | null, redirect?: string | null): void {
  window.location.assign(buildDevLoginUrl({ app, redirect }));
}

function redirectToUnauthorized(code?: string): void {
  const app = resolveProtectedAppFromPath(window.location.pathname);
  window.location.assign(buildUnauthorizedUrl(code, { app }));
}

let refreshSessionInFlight: Promise<void> | null = null;

export function isUploadImageForAccessEnabled(): boolean {
  const raw = process.env.REACT_APP_UPLOAD_IMG_FOR_ACCESS ?? process.env.UPLOAD_IMG_FOR_ACCESS ?? '0';
  return String(raw).trim() === '1';
}

async function refreshSession(): Promise<void> {
  if (!refreshSessionInFlight) {
    refreshSessionInFlight = (async () => {
      const response = await fetch(`${AUTH_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw createApiError(response.status, errorData, 'Refresh failed');
      }
    })().finally(() => {
      refreshSessionInFlight = null;
    });
  }
  return refreshSessionInFlight;
}

export interface Project {
  name: string;
}

export interface CurrentUser {
  Id: string;
  Name?: string;
  CEANumber?: string;
  Mobile?: string;
  Email?: string;
  Photo?: string;
  role?: string;
  roles?: string[];
  /** True for every authenticated user. */
  canAccessPricePromo?: boolean;
  /** True for every authenticated user; enables Table and Share tools. */
  canAccessPricePromoShare?: boolean;
  /**
   * True for role-based grantees, NEARBY-DEAL training attendees, or Project
   * Verification responsible parties (Head/DHP, otherwise IC).
   */
  canAccessNearbyDeal?: boolean;
  /** Always true for authenticated users; retained for client compatibility. */
  canAccessSuperRatio?: boolean;
  /**
   * Always true — the Price Promo Watchlist menu is open to every authenticated
   * user. Kept on the payload so older deployed clients that still read it also
   * see the menu unlocked.
   */
  canAccessPricePromoWatchlist?: boolean;
  /**
   * True when the user's designation is one of the eligible district director
   * titles — unlocks the Leader Verifications module.
   */
  canAccessLeaderVerification?: boolean;
  /**
   * True for admins (who can view any team) and for the district directors whose
   * designation leads one — unlocks the Team CPD module.
   */
  canAccessTeamCpd?: boolean;
  /**
   * True for admins (who can view any team) and for eligible district directors —
   * unlocks Leader → Statistics, scoped to their own team.
   */
  canAccessTeamStatistics?: boolean;
  /**
   * True for admins only — the AI assistant is an admin-only feature (the
   * backend chat endpoints enforce the same rule).
   */
  canAccessSuperPickerChat?: boolean;
}

/** A bookable Super Connector setup-appointment slot (SGT labels precomputed). */
export interface ConnectorInterestSlot {
  startIso: string;
  endIso: string;
  durationMinutes: number;
  dateKey: string;
  dateLabel: string;
  timeLabel: string;
}

export interface ConnectorInterestRegisterResult {
  success: boolean;
  appointment: {
    startIso: string;
    endIso: string;
    label: string;
    durationMinutes: number;
  };
  calendarMethod: 'google' | 'ics';
}

/** One weekday's availability config (Mon=1 … Fri=5). Times are "HH:MM". */
export interface ConnectorScheduleDay {
  dayOfWeek: number;
  enabled: boolean;
  startTime: string;
  endTime: string;
  slotMinutes: number;
}

export interface ConnectorScheduleConfig {
  days: ConnectorScheduleDay[];
  excludedDates: string[];
}

export interface ConnectorAppointment {
  id: number;
  cea: string;
  agentName: string;
  mobile: string;
  email: string;
  appointmentAt: string;
  durationMinutes: number;
  calendarMethod: 'google' | 'ics' | null;
  createdAt: string | null;
}

/** Per-user daily "reveal" budget for the masked Price Promo table. */
export interface PricePromoRevealState {
  limit: number;
  used: number;
  remaining: number;
  /** Units already revealed today — re-opened for free. */
  revealedUnitIds: number[];
  date: string;
}

export interface PricePromoRevealResult extends PricePromoRevealState {
  /** False when the reveal was blocked because the daily cap is reached. */
  allowed: boolean;
}

export type AdminUserReviewStatus = 'pending' | 'approved' | 'rejected' | 'revoked';

export interface AdminUserAccess {
  id: string;
  ceaNumber: string;
  role: string;
  name: string | null;
  email: string | null;
  mobile: string | null;
  imgUrl: string | null;
  reviewedAt: string | null;
  defunctAt: string | null;
  reviewStatus: AdminUserReviewStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Row shape returned by `GET /api/admin/picker-external-users` — one entry
 * per `TransformationPickerExternalUser` row joined with the inviting agent's
 * `User` record (so the admin shell can render both the recipient and the
 * issuing agent on the same row).
 */
export interface AdminPickerExternalUser {
  id: number;
  /** Recipient display name as the issuing agent typed it. */
  name: string | null;
  /** Recipient mobile (unmasked — admin-only view). */
  mobile: string;
  /** Share uuid embedded in the public landing URL. */
  uuid: string;
  /** One of `SuperPickerExternalUserModule`. */
  module: string;
  /** Project this share is scoped to (only for `PICKER_PROJECT_DETAILS`). */
  projectId: string | null;
  maxTokens: number;
  remainingTokens: number;
  createdAt: string | null;
  updatedAt: string | null;
  /** Set once the recipient passes WhatsApp OTP; null = not yet activated. */
  activatedAt: string | null;
  /** Trial expiry derived from `activatedAt + trial duration`. */
  expiresAt: string | null;
  /** Effective trial length in minutes (`TrialDurationMinutes` on the share row). */
  trialDurationMinutes: number;
  /** Set when the share was manually revoked. */
  defunctAt: string | null;
  /** CEA number of the issuing agent. */
  invitedBy: string;
  /** Issuing agent's display name (from the `User` table). */
  invitedByName: string | null;
  /**
   * Pre-shortened share URL persisted at create time. `null` when the
   * upstream shortener was unavailable / unconfigured — callers should
   * fall back to building the URL from `uuid` in that case.
   */
  shortUrl: string | null;
}

export interface AnalyticsSeriesPoint {
  label: string;
  users: number;
}

export interface AnalyticsActivitySeriesPoint {
  label: string;
  activities: number;
}

export interface AnalyticsProjectStat {
  projectName: string;
  views: number;
}

export interface AnalyticsUserUsage {
  ceaNumber: string;
  totalEvents: number;
  eventUsage: Array<{ eventName: string; count: number }>;
  projectViews: AnalyticsProjectStat[];
}

export type AnalyticsSeriesGranularity = 'hour' | 'day' | 'month';

export interface AnalyticsDashboardResponse {
  windowHours: number;
  /** Bucket size of the returned series; labels are `MM/DD HH:00`, `YYYY-MM-DD`, or `YYYY-MM`. */
  granularity?: AnalyticsSeriesGranularity;
  uniqueUsers: number | null;
  newUsers: number | null;
  uniqueUserSeries: AnalyticsSeriesPoint[];
  signUpSeries: AnalyticsSeriesPoint[];
  activitySeries: AnalyticsActivitySeriesPoint[];
  topProjects: AnalyticsProjectStat[];
  userUsage: AnalyticsUserUsage | null;
  warnings: string[];
}

export interface AnalyticsDashboardQuery {
  startDate?: string;
  endDate?: string;
  userCea?: string;
  /** `month` requests month-on-month buckets; omit (or `day`) for the default daily/hourly buckets. */
  granularity?: 'day' | 'month';
}

// ── Super Calculator admin analytics ────────────────────────────────────────
// Same GA4 export as the Super Picker dashboard, scoped to `super_calculator_*`
// events; `calculator` is the dimension that replaces `project_name`.

export type SuperCalculatorAnalyticsGranularity = 'day' | 'month';

export interface SuperCalculatorUserPoint {
  label: string;
  users: number;
}

export interface SuperCalculatorActivityPoint {
  label: string;
  activities: number;
}

/**
 * One calculator's totals across the whole selected range. `views`/`activities`
 * are event counts; `viewers`/`agents` are distinct-agent counts and are valid
 * only for the window they were computed over — never sum them across buckets.
 */
export interface SuperCalculatorStat {
  calculator: string;
  /** `super_calculator_page_viewed` hits. */
  views: number;
  /** Every tracked event carrying this calculator. */
  activities: number;
  /** Distinct agents with at least one view. */
  viewers: number;
  /** Distinct agents with any tracked activity. */
  agents: number;
}

/** One (bucket, calculator) set of metrics, for the per-calculator time series. */
export interface SuperCalculatorSeriesPoint extends SuperCalculatorStat {
  label: string;
}

export interface SuperCalculatorOverviewResponse {
  startDate: string;
  endDate: string;
  granularity: SuperCalculatorAnalyticsGranularity;
  uniqueUsers: number | null;
  newUsers: number | null;
  uniqueUserSeries: SuperCalculatorUserPoint[];
  signUpSeries: SuperCalculatorUserPoint[];
  activitySeries: SuperCalculatorActivityPoint[];
  topCalculators: SuperCalculatorStat[];
  calculatorSeries: SuperCalculatorSeriesPoint[];
  warnings: string[];
}

export interface SuperCalculatorViewerRow {
  ceaNumber: string;
  name: string | null;
  mobile: string | null;
  /** `super_calculator_page_viewed` count for this calculator. */
  views: number;
  /** Every tracked event carrying this calculator (views, PDF exports, tab/mode switches). */
  activities: number;
  lastViewedOn: string | null;
}

export interface SuperCalculatorViewersResponse {
  calculator: string;
  startDate: string;
  endDate: string;
  /** True when the drill-down hit the BigQuery row cap, so `viewers` is a top-N slice. */
  truncated?: boolean;
  limit?: number;
  viewers: SuperCalculatorViewerRow[];
}

export type MaterialAppSource = 'all' | 'super_picker' | 'project_essential';

export interface MaterialEngagementSeriesPoint {
  date: string;
  spEdmPreviews: number;
  spEdmDownloads: number;
  spEbookPreviews: number;
  spEbookDownloads: number;
  spVideoPlayed: number;
  peEdmPreviews: number;
  peEdmDownloads: number;
  peEbookPreviews: number;
  peEbookDownloads: number;
  peVideoClicked: number;
}

export interface MaterialEngagementTopProject {
  projectName: string;
  totalInteractions: number;
}

export interface MaterialEngagementTopMaterial {
  fileId: string;
  type: 'edm' | 'ebook';
  previews: number;
  downloads: number;
  total: number;
}

export interface MaterialEngagementResponse {
  series: MaterialEngagementSeriesPoint[];
  topProjects: MaterialEngagementTopProject[];
  topProjectsSp: MaterialEngagementTopProject[];
  topProjectsPe: MaterialEngagementTopProject[];
  topMaterials: MaterialEngagementTopMaterial[];
  topMaterialsSp: MaterialEngagementTopMaterial[];
  topMaterialsPe: MaterialEngagementTopMaterial[];
}

export interface MaterialDetailItem {
  id: string;
  name: string;
  category: string | null;
  isEbook: boolean;
  thumbnail: string | null;
  downloadUrl: string | null;
  outputType: string | null;
  filename: string;
  createdAt: string | null;
}

export interface CommunicationDateRangeQuery {
  startDate?: string;
  endDate?: string;
}

export interface CommunicationOverviewResponse {
  channels: string[];
  series: Array<{ date: string } & Record<string, number | string>>;
  channelTotals: Array<{ channel: string; count: number }>;
  total: number;
}

export interface CommunicationCampaign {
  purpose: string;
  channel: string;
  count: number;
  recipients: number;
  firstSentAt: string;
  lastSentAt: string;
}

export interface CommunicationCampaignsResponse {
  campaigns: CommunicationCampaign[];
}

export interface CommunicationMessageItem {
  id: number;
  ceaNumber?: string;
  agentName?: string | null;
  channel: string;
  purpose: string;
  mobile: string | null;
  email: string | null;
  message: string | null;
  createdAt: string | null;
}

export interface CommunicationCampaignDetailsResponse {
  items: CommunicationMessageItem[];
}

export interface CommunicationAgentRow {
  ceaNumber: string;
  name: string | null;
  mobile: string | null;
  email: string | null;
  count: number;
  lastSentAt: string | null;
}

export interface CommunicationAgentsResponse {
  agents: CommunicationAgentRow[];
}

export interface CommunicationByAgentResponse {
  agent: {
    ceaNumber: string;
    name: string | null;
    mobile: string | null;
    email: string | null;
  };
  channelBreakdown: Array<{ channel: string; count: number }>;
  purposeBreakdown: Array<{ purpose: string; count: number }>;
  items: CommunicationMessageItem[];
}

export interface ProjectViewsByCeaQuery {
  projectName: string;
  startDate: string;
  endDate: string;
  selectedDate?: string;
}

export interface ProjectViewsByCeaResponse {
  ceaViews: Array<{ ceaNumber: string; views: number }>;
}

export interface ProjectAnalyticsQuery {
  projectName: string;
  startDate: string;
  endDate: string;
}

export interface ProjectAnalyticsTopUserRow {
  ceaNumber: string;
  /** From app User table when present */
  name?: string;
  mobile?: string;
  /** BigQuery: views for this project from backfill start through today */
  viewsAllTime: number;
  /** BigQuery: views for the selected date range */
  viewsPeriod: number;
  /** Secure transactions API: sold units for this project (all time) */
  soldAllTime: number;
  /** Secure transactions API: sold with option date in the selected analytics range */
  soldPeriod: number;
  /** New launch closes from 2025-03-01 through 2026-03-31 */
  closeNewLaunchCount: number;
  /**
   * "Resale Agent (JY rule)": true when, over the last 12 months, this active R%
   * agent's total GCE exceeds 50K and resale makes up at least 85% of that total.
   */
  isResaleAgent?: boolean;
}

export interface ProjectAnalyticsResponse {
  dailySeries: Array<{ label: string; count: number }>;
  eventBreakdown: Array<{ eventName: string; count: number }>;
  topUsers: ProjectAnalyticsTopUserRow[];
  soldCountsRateLimited?: boolean;
}

export interface ProjectMaterialDailyPoint {
  date: string;
  spEdmPreviews: number;
  spEdmDownloads: number;
  spEbookPreviews: number;
  spEbookDownloads: number;
  peEdmPreviews: number;
  peEdmDownloads: number;
  peEbookPreviews: number;
  peEbookDownloads: number;
}

export interface ProjectMaterialTopDownloaderRow {
  ceaNumber: string;
  name: string | null;
  mobile: string | null;
  downloads: number;
}

export interface ProjectMaterialFileRow {
  fileId: string;
  kind: 'edm' | 'ebook';
  previews: number;
  downloads: number;
  spPreviews: number;
  spDownloads: number;
  pePreviews: number;
  peDownloads: number;
  createdAt: string | null;
}

export interface ProjectMaterialAnalyticsResponse {
  projectId: string | null;
  dailySeries: ProjectMaterialDailyPoint[];
  topEdmDownloaders: ProjectMaterialTopDownloaderRow[];
  topEbookDownloaders: ProjectMaterialTopDownloaderRow[];
  topEdmDownloadersSp: ProjectMaterialTopDownloaderRow[];
  topEdmDownloadersPe: ProjectMaterialTopDownloaderRow[];
  topEbookDownloadersSp: ProjectMaterialTopDownloaderRow[];
  topEbookDownloadersPe: ProjectMaterialTopDownloaderRow[];
  edmFiles: ProjectMaterialFileRow[];
  ebookFiles: ProjectMaterialFileRow[];
}

export interface ProjectEoiAgentRow {
  ceaNumber: string;
  name: string;
  mobile: string;
  eoiCount: number;
  closeNewLaunchCount: number;
  engagedCount: number;
}

export interface ProjectEoiResponse {
  agents: ProjectEoiAgentRow[];
}

export interface ProjectEoiSendWhatsAppResponse {
  success: boolean;
  engagedCount: number;
}

export interface AgentSoldCountsResponse {
  soldCounts: Array<{ ceaNumber: string; agentName: string; soldAllTime: number; soldPeriod: number }>;
  /** True when the secure transactions API returned HTTP 429; sold counts are empty. */
  soldCountsRateLimited?: boolean;
}

export interface AgentNamesResponse {
  agentNames: Record<string, string>;
  agentMobiles: Record<string, string>;
}

export interface AgentProfile {
  name: string;
  ceaNumber: string;
  mobile: string;
  email: string;
  photo: string;
}

export interface AgentProfileResponse {
  profile: AgentProfile | null;
}

export interface AgentSoldUnit {
  reference: string;
  transactionNo: string;
  propertyType: string;
  saleType: string;
  optionDate: string;
  transactionPrice: number;
  address: string;
  unitNo: string;
  projectCode: string;
  status: string;
  agentName: string;
  agentRole: string;
  agentCEANo: string;
}

export interface AgentSoldUnitsResponse {
  units: AgentSoldUnit[];
}

export interface SuccessCloserTransaction {
  transactionNo: string;
  agentCEANo: string;
  agentName: string;
  reference: string;
  propertyType: string;
  saleType: string;
  optionDate: string;
  transactionPrice: number;
  address: string;
  unitNo: string;
  projectCode: string;
  status: string;
  agentRole: string;
  grouping: string;
  projectActivityCount: number;
}

export interface SuccessCloserRow {
  agentName: string;
  ceaRegNo: string;
  mobile: string;
  activityCount: number;
  closingCount: number;
  transactions: SuccessCloserTransaction[];
  agentRole: string;
  grouping: string;
}

export interface SuccessCloserResponse {
  rows: SuccessCloserRow[];
}

/** GET /api/admin/analytics/share-links — see backend `shareLinkAnalyticsService.ts`. */
export interface ShareLinkPeriodTotals {
  links: number;
  sharers: number;
  projects: number;
  leads: number;
  uniqueLeadMobiles: number;
  verificationsStarted: number;
  verificationsCompleted: number;
}

export interface ShareLinkSeriesPoint {
  date: string;
  links: number;
  sharers: number;
  leads: number;
  verificationsStarted: number;
  verificationsAbandoned: number;
}

/** Links shared and leads received in the range; opens are all-time on those links. */
export interface ShareLinkRollup {
  links: number;
  leads: number;
  opens: number;
  lastSharedAt: string | null;
  lastLeadAt: string | null;
}

export interface ShareLinkProjectStat extends ShareLinkRollup {
  projectId: string;
  projectName: string;
  sharers: number;
}

export interface ShareLinkAgentStat extends ShareLinkRollup {
  ceaNumber: string;
  name: string;
  mobile: string;
  projects: number;
}

export interface ShareLinkAgentProjectStat extends ShareLinkRollup {
  ceaNumber: string;
  projectId: string;
}

export interface ShareLinkAnalyticsLink {
  id: number;
  ceaNumber: string;
  projectId: string;
  linkType: string;
  description: string | null;
  shortUrl: string | null;
  createdAt: string | null;
  revokedAt: string | null;
  sharedInRange: boolean;
  opens: number;
  leadsInRange: number;
  leadsAllTime: number;
  lastLeadAt: string | null;
}

export interface ShareLinkAnalyticsResponse {
  range: { startDate: string; endDate: string };
  /** Consumers flagged as possible agents are left out of every consumer figure. */
  excludePossibleAgents: boolean;
  /** Leads in the range from possible agents (excluded or not). */
  possibleAgentLeads: number;
  /** Leads in the range whose mobile hasn't been checked yet. */
  uncheckedLeads: number;
  previousRange: { startDate: string; endDate: string };
  totals: ShareLinkPeriodTotals & { opens: number; revokedLinks: number };
  previousTotals: ShareLinkPeriodTotals;
  series: ShareLinkSeriesPoint[];
  projects: ShareLinkProjectStat[];
  agents: ShareLinkAgentStat[];
  pairs: ShareLinkAgentProjectStat[];
  links: ShareLinkAnalyticsLink[];
}

/** Query shared by the admin, team and project share-link analytics endpoints. */
export interface ShareLinkAnalyticsQuery {
  startDate: string;
  endDate: string;
  excludePossibleAgents?: boolean;
}

/** Leader → Statistics: the share-link analytics over one leader's team. */
export interface TeamShareLinkAnalyticsResponse extends ShareLinkAnalyticsResponse {
  scope: {
    kind: 'team';
    leader: { ceaNumber: string; name: string | null; designation: string | null; division: string | null };
    /** Agents on the team, the leader included. */
    teamSize: number;
  };
}

/** Project Head → Statistics: the share-link analytics over one project. */
export interface ProjectShareLinkAnalyticsResponse extends ShareLinkAnalyticsResponse {
  scope: { kind: 'project'; projectId: string; projectName: string };
}

/** A project the user may open in Project Head → Statistics. */
export interface StatisticsProject {
  projectId: string;
  projectName: string;
  /** The user's role on it; null for admins / Project Dept viewing any project. */
  role: string | null;
  /** All-time links shared for it. */
  links: number;
}

export interface AdminByDateAnalyticsRow {
  ceaNumber: string;
  name: string;
  mobile: string;
  email: string;
  activityCount: number;
  transactionCount: number;
  qualifyingTransactionCount: number;
  qualifyingActivityCount: number;
  qualifyingTransactionAmount: number;
  convertedTransactionCount: number;
  convertedActivityCount: number;
  convertedTransactionAmount: number;
  hasActivity: boolean;
  hasTransaction: boolean;
}

export interface AdminByDateAnalyticsSummary {
  uniqueTransactionCount: number;
  uniqueTransactedAmount: number;
  convertedTransactionCount: number;
  convertedTransactedAmount: number;
  convertedActivityCount: number;
  convertedAgentCount: number;
}

export interface AdminByDateAnalyticsResponse {
  rows: AdminByDateAnalyticsRow[];
  summary: AdminByDateAnalyticsSummary;
}

export interface AdminJyUserRow {
  name: string;
  ceaNumber: string;
  status: string;
  designation: string;
  mobile: string;
  email: string;
  newLaunchComm: number;
  resaleComm: number;
  resalePercent: number;
  activityCount: number;
  usedPicker: boolean;
}

export interface AdminJyUsersSummary {
  total: number;
  usedPicker: number;
  notUsedPicker: number;
}

export interface AdminJyUsersResponse {
  rows: AdminJyUserRow[];
  summary: AdminJyUsersSummary;
}

export interface AdminCheckUserRow {
  ceaNumber: string;
  found: boolean;
  name: string;
  mobile: string;
  status: string;
  designation: string;
  closeNewLaunchRecent: number;
  closeNewLaunchWindow: number;
  isResaleAgent: boolean;
}

export interface AdminCheckUsersResponse {
  rows: AdminCheckUserRow[];
  summary: {
    requested: number;
    unique: number;
    found: number;
    notFound: number;
  };
  recentRange: { months: number; startDate: string; endDate: string };
  newLaunchWindow: { startDate: string; endDate: string };
  resaleAgentRule: string;
}

export interface AdminByDateQualifyingTransactionDetail {
  transactionNo: string;
  projectName: string;
  projectCode: string;
  unitNo: string;
  optionDate: string;
  transactionPrice: number;
  agentRole: string;
}

export interface AdminByDateQualifyingActivityProjectDetail {
  projectName: string;
  activityCount: number;
  latestOptionDay: string;
}

export interface AdminByDateQualifyingDetailsResponse {
  qualifyingTransactions: AdminByDateQualifyingTransactionDetail[];
  qualifyingActivityByProject: AdminByDateQualifyingActivityProjectDetail[];
}

export interface AdminByDateQualifyingTransactionWithAgent extends AdminByDateQualifyingTransactionDetail {
  ceaNumber: string;
  agentName: string;
  isConverted: boolean;
}

export interface AdminByDateAllQualifyingTransactionsResponse {
  transactions: AdminByDateQualifyingTransactionWithAgent[];
}

// Success Closer tab types
export interface SuccessCloserMyTransaction {
  id: number;
  transactionNo: string;
  projectName: string;
  address: string;
  unitNo: string;
  transactionPrice: number | null;
  optionDate: string;
  transactionRole: string;
  activityCount: number;
  testimonial: string;
  badgeEarned: boolean;
}

export interface SuccessCloserMySummary {
  totalTransactions: number;
  transactionsWithActivity: number;
  badgesEarned: number;
  transactions: SuccessCloserMyTransaction[];
}

export interface SuccessCloserTestimonialResponse {
  success: boolean;
  badgeEarned: boolean;
  isNewBadge: boolean;
  transaction: {
    id: number;
    transactionNo: string;
    projectName: string;
    testimonial: string;
  };
}

export interface SuccessCloserLeaderboardEntry {
  transactionId: number;
  projectName: string;
  optionDate: string;
  ceaNumber: string;
  name: string;
  mobile: string;
  photo: string;
  hasTestimonial: boolean;
  testimonial: string;
  activityCount: number;
  isAccelerator: boolean;
}

export interface SuccessCloserLeaderboardResponse {
  leaderboard: SuccessCloserLeaderboardEntry[];
}

/** One agent portrait area on the Super App Accelerator poster (percent coords). */
export interface AcceleratorHotspot {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  hasVideo: boolean;
  /** Admin responses only. */
  videoUrl?: string;
  agent: {
    ceaNumber: string;
    name: string;
    designation: string;
    photo: string;
    mobile: string;
  };
}

export interface AcceleratorHotspotInput {
  posterKey: string;
  ceaNumber: string;
  x: number;
  y: number;
  width: number;
  height: number;
  videoUrl: string;
}

export interface AcceleratorAgentOption {
  ceaNumber: string;
  name: string;
  designation: string;
  photo: string;
  status: string;
}

export interface AcceleratorVideoSource {
  kind: 'hls' | 'mp4';
  /** Proxied stream URL (publicWebScrape), ready for <video> / hls.js. */
  url: string;
  /** The Vimeo link the admin saved, for "Open in Vimeo". */
  sourceUrl: string;
}

export interface RegistrationEventSummary {
  id: string;
  name: string;
  /**
   * Raw Huttons `Event.CategoryId` (e.g. 'C17041', 'C17042'). Used by the
   * agent-facing dialog to flip the "Mark attendance" button into a
   * "Clock out" button for clock-out-eligible categories.
   */
  categoryId?: string | null;
  startDate: string;
  endDate: string;
  venue: string | null;
  description: string | null;
  /** True if the current user already has an EventBooking row for this event. */
  registered: boolean;
  /**
   * Mirrors `Event.RegistrationRequired`. When explicitly `false`, the dialog
   * skips the Confirm Registration step and goes straight to Training Details.
   * `null`/`undefined` means unknown — treat as "registration required".
   */
  registrationRequired?: boolean | null;
  /** True if the current user has been marked as attended for this event. */
  attended?: boolean;
  review?: string | null;
  learningPoints?: string | null;
  reviewDateApproved?: string | null;
  reviews?: Array<{
    name: string | null;
    ceaNumber: string;
    mobile: string | null;
    review: string | null;
    learningPoints: string | null;
  }>;
  assignedCategories: string[];
  assignedProjects: Array<{ projectId: string; projectName: string | null }>;
  /**
   * From `TransformationTraining.LocationRequired`. `true` = attendance
   * requires device location; `false`/`null` = not required.
   */
  locationRequired?: boolean | null;
}

export interface UpcomingEventSummary {
  id: string;
  name: string;
  /**
   * Raw Huttons `Event.CategoryId` (e.g. 'C17041', 'C17042'). Used by the
   * admin UI to gate category-specific features such as the Clock-Out QR
   * button on Fast Track sessions. Distinct from `assignedCategories`,
   * which holds the Transformation-app's own categorisation (Super Picker
   * / Super Calculator / Recruitment).
   *
   * Always null for admin-managed Transformation events (`sourceType = TRANS`).
   */
  categoryId?: string | null;
  description: string | null;
  startDate: string;
  endDate: string;
  venue: string | null;
  /**
   * Mirrors `Event.RegistrationRequired`. When `false`, the admin Manage
   * Events dialog hides the Registered Users / ECB tabs.
   * `null`/`undefined` is treated as "registration required".
   */
  registrationRequired?: boolean | null;
  /** `HIP` = portal Event; `TRANS` = TransformationEvent. Defaults to HIP. */
  sourceType?: 'HIP' | 'TRANS';
  assignedCategories: string[];
  assignedProjects: Array<{ projectId: string; projectName: string | null }>;
  assignedTrainers?: Array<{ ceaNumber: string; name: string | null; mobile?: string | null }>;
  /**
   * From `TransformationTraining.LocationRequired`. `true` = attendance
   * requires device location; `false`/`null` = not required.
   */
  locationRequired?: boolean | null;
  /**
   * From `TransformationTraining.QrRefreshInterval` (seconds).
   * `0` = do not rotate the attendance QR while displayed.
   */
  qrRefreshIntervalSeconds?: number;
}

export interface ManagedTransformationEventSummary {
  id: number;
  listId: string;
  name: string;
  description: string;
  venue: string | null;
  startTime: string;
  endTime: string;
  createdAt: string | null;
  updatedAt: string | null;
  assignedCategories: string[];
  assignedProjects: Array<{ projectId: string; projectName: string | null }>;
  assignedTrainers?: Array<{ ceaNumber: string; name: string | null; mobile?: string | null }>;
  locationRequired?: boolean | null;
  qrRefreshIntervalSeconds?: number;
}

export interface AgentCeaSearchResult {
  ceaNumber: string;
  name: string | null;
}

/** A training → feature access mapping (Feature Access admin tab). */
// ── Accelerator Grant Access ─────────────────────────────────────────────────
export type AcceleratorGrantFeature = 'SUPER-MAP' | 'DECOUPLE-CALC';

export interface AcceleratorGrantAgent {
  ceaNumber: string;
  name: string | null;
  mobile: string | null;
  photo: string | null;
  designation: string | null;
}

export interface AcceleratorInvitee extends AcceleratorGrantAgent {
  features: { grantId: number; feature: AcceleratorGrantFeature; label: string; grantedAt: string }[];
}

export interface AcceleratorGrantsResponse {
  features: { key: AcceleratorGrantFeature; label: string }[];
  invitees: AcceleratorInvitee[];
}

export interface FeatureAccessMapping {
  id: number;
  feature: string;
  trainingId: number;
  eventName: string | null;
  startDate: string | null;
  endDate: string | null;
  type: string | null;
  createdAt: string | null;
}

/** A training available to map to a feature. */
export interface MappableTraining {
  trainingId: number;
  eventName: string | null;
  startDate: string | null;
  endDate: string | null;
  type: string | null;
}

export interface FeatureAccessData {
  mappings: FeatureAccessMapping[];
  trainings: MappableTraining[];
  features: string[];
}

export interface RegisteredEventMemberSummary {
  id: string;
  name: string | null;
  ceaNumber: string | null;
  mobile: string | null;
  registeredFromTransformationApp: boolean;
  ecbCount: number;
}

export interface AttendedEventMemberSummary {
  id: string | null;
  name: string | null;
  ceaNumber: string;
  mobile: string | null;
  attendedAt: string | null;
  createdAt: string | null;
  timeScanned: string | null;
  /** Clock-in timestamp (TimeScanned of the IsClockIn=1 attendance row). */
  clockedInAt: string | null;
  /**
   * Clock-out timestamp (TimeScanned of the IsClockIn=0 attendance row).
   * Only populated for event categories that support clock-out
   * (currently C17041 / C17042); always `null` otherwise.
   */
  clockedOutAt: string | null;
  clockInLatitude: number | null;
  clockInLongitude: number | null;
  clockOutLatitude: number | null;
  clockOutLongitude: number | null;
}

export interface TrainingECBInviteSummary {
  id: number;
  name: string;
  agency: string;
  mobile: string | null;
  invitedBy: string;
  invitedByName: string | null;
  createdAt: string | null;
}

export interface TrainingReviewSubmission {
  id: number;
  ceaNumber: string;
  eventId: string;
  review: string | null;
  learningPoints: string | null;
  createdAt: string;
}

export interface TrainingReviewEventSummary {
  eventId: string;
  name: string;
  startDate: string;
  endDate: string;
  venue: string | null;
  reviewCount: number;
  pendingReviewCount: number;
}

export interface AdminTrainingReviewSummary {
  id: number;
  eventId: string;
  ceaNumber: string;
  name: string | null;
  review: string | null;
  learningPoints: string | null;
  dateApproved: string | null;
  createdAt: string | null;
}

export interface ReferenceUnit {
  id: number;
  projectName: string;
  region?: string | null;
  unitNumber: string;
  blockNumber?: string | null;
  bedroom: number;
  bathroom: number;
  areaSqft: number;
  price: number;
  priceNps?: number | null;
  priceDps?: number | null;
  psf: number;
  psfNps?: number | null;
  psfDps?: number | null;
  outlay: number;
  installment: number;
  unitType?: string | null;
  unitStatus?: string | null;
  unitStatusSource?: 'live' | 'project' | 'matrix';
  unitStatusUpdatedAt?: string | Date | null;
  floorplan?: string | null;
}

export interface ComparableUnit {
  id: number;
  projectName: string;
  region?: string | null;
  unitNumber: string;
  blockNumber?: string | null;
  bedroom: number;
  bathroom: number;
  areaSqft: number;
  price: number;
  priceNps?: number | null;
  priceDps?: number | null;
  priceDiff: number;
  priceDiffPercent: number;
  priceChange?: number;
  priceChangePercent?: number;
  psf: number;
  psfNps?: number | null;
  psfDps?: number | null;
  psfDiff: number;
  psfDiffPercent: number;
  psfChange?: number;
  psfChangePercent?: number;
  outlay: number;
  outlayDiff: number;
  installment: number;
  installmentDiff: number;
  unitStatus?: string | null;
  unitStatusSource?: 'live' | 'project' | 'matrix';
  unitStatusUpdatedAt?: string | Date | null;
  floorplan?: string | null;
  hasHighestPriceDrop?: boolean;
  unitTypeDescription?: string | null;
}

export interface ComparableUnitsResponse {
  referenceUnit: ReferenceUnit;
  comparableUnits: ComparableUnit[];
}

/** Per-bedroom stats for project comparison (available units, lowest price, lowest psf). */
export interface ProjectDetailsBedroomStats {
  availableUnits: number | null;
  fromPrice: number | null;
  fromPsf: number | null;
}

export interface ProjectIndicativePriceRange {
  low: number | null;
  high: number | null;
}

export interface ProjectIndicativePrices {
  br1?: ProjectIndicativePriceRange | null;
  br2?: ProjectIndicativePriceRange | null;
  br3?: ProjectIndicativePriceRange | null;
  br4?: ProjectIndicativePriceRange | null;
  br5?: ProjectIndicativePriceRange | null;
  ph?: ProjectIndicativePriceRange | null;
}

export interface ProjectDetails {
  id?: string | null;
  name: string | null;
  projectCategory?: string | null;
  projectCategoryId?: string | null;
  priceMatrixSheetId?: string | null;
  developer: string | null;
  tenure: string | null;
  totalUnits: number | string | null;
  completedDate: Date | null;
  area: string | null;
  district: string | null;
  address: string | null;
  mapLongitude: number | null;
  mapLatitude: number | null;
  /** Absolute URL for Project.FeaturedImage when available. */
  featuredImage?: string | null;
  liveStatusEnabled?: boolean;
  liveStatusUpdatedAt?: string | Date | null;
  /** TOP date (Project.TOPDate). */
  topDate?: number | string | null;
  /** Sold units count (from ProjectUnit / ProjectUnitLiveStatusHistory when Live). */
  soldUnits?: number | null;
  /** District/Location from Ecoprop. */
  districtLocation?: string | null;
  /** Nearest MRT: EcopropTrainName2 ?? EcopropTrainName. */
  nearestMrt?: string | null;
  /** Walking time to MRT (minutes): EcopropTrainWalkTime2 ?? EcopropTrainWalkTime. */
  walkingTimeToMrt?: number | null;
  /** Indicative price ranges per bedroom from Ecoprop (low/high). */
  indicativePrices?: ProjectIndicativePrices | null;
  /** Free-text per-bedroom price estimates (e.g. "$1.4xx m"), keyed by bedroom number. */
  priceEstimates?: Record<string, string> | null;
  /** 1BR–5BR and PH stats (available units, from price, from psf). */
  br1?: ProjectDetailsBedroomStats | null;
  br2?: ProjectDetailsBedroomStats | null;
  br3?: ProjectDetailsBedroomStats | null;
  br4?: ProjectDetailsBedroomStats | null;
  br5?: ProjectDetailsBedroomStats | null;
  ph?: ProjectDetailsBedroomStats | null;
}

export interface TowerViewUnit {
  id: number;
  projectName: string;
  region?: string | null;
  unitNumber: string;
  blockNumber: string | null;
  /** Raw FloorNumber from ProjectUnit (e.g. "01", "G", "B1"). */
  floorNumber?: string | null;
  bedroom: number;
  bathroom: number;
  areaSqft: number;
  price: number;
  priceNps?: number | null;
  priceDps?: number | null;
  psf: number;
  psfNps?: number | null;
  psfDps?: number | null;
  priceChange?: number;
  unitStatus: string | null;
  unitStatusSource?: 'live' | 'project' | 'matrix';
  unitStatusUpdatedAt?: string | Date | null;
  floorplan?: string | null;
  isBestBuy?: boolean;
  transactedBy?: string | null;
  unitType?: string | null;
  unitTypeDescription?: string | null;
  projectUnitTypeId?: number | null;
}

export type TopViewingReferenceUnit = (TowerViewUnit | ComparableUnit) & {
  referenceSource?: 'indicative' | 'guesstimation';
  indicativeSizeRange?: { min: number; max: number };
};

/** Developer Insights — see backend/src/services/developerInsightsService.ts. */
export interface DeveloperInsightsMrt {
  stationName: string;
  /** Metres from the land-lot edge to the nearest exit, as the amenities panel shows. */
  distanceM: number;
}

export interface DeveloperInsightsMrtCalibration {
  /** PSF premium for every 100 m closer to an MRT exit, up to the distance cap. */
  premiumPer100Pct: number;
  sePct: number;
  n: number;
  r2: number;
  valid: boolean;
}

export interface DeveloperInsightsTypeSummary {
  bedroom: number;
  unitCount: number;
  availableCount: number;
  medianSizeSqft: number | null;
  /** Indicative "from" prices quote the smallest unit of the type. */
  minSizeSqft: number | null;
  listMinPsf: number | null;
  listAvgPsf: number | null;
  indicativeLowPrice: number | null;
  indicativeHighPrice: number | null;
}

export interface DeveloperInsightsLaunch {
  id: string;
  name: string;
  distanceKm: number;
  bearingDeg: number;
  tenure: string | null;
  freehold: boolean;
  topYear: number | null;
  totalUnits: number;
  soldUnits: number;
  availableUnits: number;
  /** Land price per sqft of GFA the developer paid, when recorded. */
  landPsfPpr: number | null;
  mrt: DeveloperInsightsMrt | null;
}

export type DeveloperInsightsLandSaleKind = 'GLS' | 'Collective sale' | 'Land sale';

export interface DeveloperInsightsLandSale {
  siteName: string;
  projectName: string | null;
  projectId: string | null;
  psfPpr: number;
  tenderPrice: number | null;
  awardDate: string | null;
  tenderer: string | null;
  kind: DeveloperInsightsLandSaleKind;
  freehold: boolean;
  distanceKm: number;
  bearingDeg: number;
}

export interface DeveloperInsightsReferenceLand {
  siteName: string | null;
  /** Null when the land row exists but no price was recorded. */
  psfPpr: number | null;
  tenderPrice: number | null;
  awardDate: string | null;
  tenderer: string | null;
  kind: DeveloperInsightsLandSaleKind;
}

export interface DeveloperInsightsUnsoldUnit {
  launchId: string;
  unitNumber: string;
  bedroom: number;
  sizeSqft: number;
  price: number;
  psf: number;
}

export interface DeveloperInsightsTxProject {
  name: string;
  distanceKm: number;
  bearingDeg: number;
  completionYear: number | null;
  freehold: boolean;
  /** Nearest block to an MRT exit, in metres. */
  mrtM: number | null;
}

/** N = new sale, R = resale, S = sub sale. */
export type DeveloperInsightsTxKind = 'N' | 'R' | 'S';

/** [txProjectIndex, 'YYYY-MM-DD', kind, bedroom, sizeSqft, price, psf] */
export type DeveloperInsightsTxRow = [number, string, DeveloperInsightsTxKind, number, number, number, number];

export interface DeveloperInsightsResponse {
  asOf: string;
  maxRadiusKm: number;
  resaleLookbackMonths: number;
  newSaleLookbackMonths: number;
  reference: {
    projectId: string;
    projectName: string;
    tenure: string | null;
    freehold: boolean;
    isExecutiveCondo: boolean;
    topYear: number | null;
    types: DeveloperInsightsTypeSummary[];
    land: DeveloperInsightsReferenceLand | null;
    mrt: DeveloperInsightsMrt | null;
  };
  mrtCalibration: {
    distanceCapM: number;
    local: DeveloperInsightsMrtCalibration | null;
    island: DeveloperInsightsMrtCalibration | null;
  };
  launches: DeveloperInsightsLaunch[];
  landSales: DeveloperInsightsLandSale[];
  unsold: DeveloperInsightsUnsoldUnit[];
  txProjects: DeveloperInsightsTxProject[];
  transactions: DeveloperInsightsTxRow[];
}

export interface PriceHistoryPoint {
  price: number;
  pricePSF: number;
  date: string | Date;
  /**
   * True for the synthetic "listed price" (OtherPrice1) point, which has no real
   * date. The chart renders its X-axis label as "Listed Date".
   */
  isListedPrice?: boolean;
}

export interface PriceHistoryResponse {
  unitId: number;
  unitNumber: string;
  projectName: string;
  /** Unit's OtherPrice1 ("listed price"), used to build the "Listed Date" point. */
  otherPrice1?: number | null;
  /** Unit size (sqft), used to derive the listed point's PSF. */
  size?: number | null;
  history: PriceHistoryPoint[];
}

export interface SitePlanBlock {
  blockNumber?: string;
  stack?: string;
  facing?: string;
  x: number; // Percentage of image width (0-100)
  y: number; // Percentage of image height (0-100)
  width: number; // Percentage of image width
  height: number; // Percentage of image height
}

/**
 * Hosts that serve site-plan images with `Access-Control-Allow-Origin`, so the
 * 3D renderer can texture from them directly.
 */
const CORS_CLEAN_IMAGE_HOSTS = ['firebasestorage.googleapis.com', 'storage.googleapis.com'];

/** Same-origin proxy for a project's site-plan image (see the backend route). */
export function buildSitePlanImageProxyUrl(projectId: string | null | undefined): string | null {
  const id = (projectId ?? '').trim();
  if (!id) return null;
  return `${API_BASE}/projects/site-plan-image?projectId=${encodeURIComponent(id)}`;
}

/** Admin-only same-origin texture URL for a separately uploaded floor-group diagram. */
export function buildSitePlanDiagramImageProxyUrl(projectId: string, groupId: string, storagePath: string): string {
  return `${API_BASE}/projects/site-plan-diagram-image?${new URLSearchParams({ projectId, groupId, storagePath })}`;
}

/**
 * Which URL the 3D site plan should texture from, and what to fall back to.
 *
 * WebGL needs a CORS-clean image. Plans uploaded through Super Picker sit on
 * Firebase Storage and load directly; the ones still served from HIP's own
 * media host send no CORS headers, so those go through `proxyUrl` instead of
 * failing into the "could not be loaded into 3D" warning. Either way the other
 * URL is kept as a fallback, so an unknown host that turns out to be fine — or
 * a proxy hiccup — still renders.
 *
 * `proxyUrl` is passed in rather than derived: it must resolve to *this* image,
 * and not every 3D site plan comes from the project's preferred media row.
 */
export function resolveSitePlanTextureUrls(
  imageUrl: string | null | undefined,
  proxyUrl: string | null | undefined,
): { primary: string; fallback: string | null } {
  const direct = (imageUrl ?? '').trim();
  const proxy = (proxyUrl ?? '').trim() || null;
  if (!proxy) return { primary: direct, fallback: null };
  let corsClean = false;
  try {
    corsClean = CORS_CLEAN_IMAGE_HOSTS.includes(new URL(direct, window.location.origin).hostname);
  } catch {
    corsClean = false;
  }
  return corsClean ? { primary: direct, fallback: proxy } : { primary: proxy, fallback: direct };
}

export interface SitePlanData {
  imageUrl: string;
  /** The developer's original plan, when an edited Super Picker plan replaces it. */
  originalImageUrl?: string | null;
  blocks: SitePlanBlock[];
  blockNumbers?: string[];
  bounds?: { x: number; y: number; width: number; height: number } | null;
  mediaSource?: 'projectPickerMedia' | 'projectMedia' | null;
  projectMediaId?: number | null;
  projectPickerMediaId?: number | null;
}

/** One stored Nearby 3D Map stack footprint. Rings are [lng, lat]. */
export interface AutoSiteMapFootprint {
  key: string; block: string; stack: string; ring: Array<[number, number]>;
  storeys: number; heightM: number; storeysSource: 'units' | 'plotted' | 'ai' | 'default';
}
export interface AutoSiteMapTransform {
  originLng: number; originLat: number; imageWidth: number; imageHeight: number;
  scale: number; rotation: number; east: number; north: number;
}
export interface AutoSiteMapLot { lotNumber: string | null; ring: Array<[number, number]> }
export interface AutoSiteMapAiExtract {
  northAngle: number | null;
  boundary: Array<{ x: number; y: number }> | null;
  blocks: Array<{ name: string; storeys: number | null; footprint: Array<{ x: number; y: number }> | null }>;
  model: string; extractedAt: string;
}
/** `GET /api/picker-auto-site-map/projects/:id` — admin-only. */
export interface AutoSiteMapRecord {
  available: boolean;
  status?: 'DRAFT' | 'APPROVED';
  imageUrl?: string;
  lots?: AutoSiteMapLot[];
  lotSource?: string | null;
  crop?: Array<{ x: number; y: number }> | null;
  transform?: AutoSiteMapTransform | null;
  fitScore?: number | null;
  fitMethod?: 'PIXEL' | 'AI' | 'ADJUSTED' | null;
  footprints?: AutoSiteMapFootprint[] | null;
  aiExtract?: AutoSiteMapAiExtract | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  updatedAt?: string | null;
}
export interface AutoSiteMapNeighbours {
  hdb: Array<{ id: string; block: string; street: string; storeys: number; yearCompleted: number | null; carPark: boolean; lat: number; lng: number; distanceM: number }>;
  projects: Array<{ id: string; name: string; storeys: number; lat: number; lng: number; distanceM: number }>;
}

export interface FloorPlanCrop { x: number; y: number; w: number; h: number }
export type FloorPlanNorthSource = 'stack' | 'block' | 'type' | 'sitePlan' | null;

/** `GET /api/units/:id/floor-plan-solar` — the calibration a unit's 3D floor plan renders with. */
export interface FloorPlanSolarResolution {
  projectId: string;
  imageUrl: string | null;
  unitTypeId: number | null;
  unitType: string | null;
  block: string;
  stack: string;
  northAngle: number | null;
  source: FloorPlanNorthSource;
  mirror: boolean;
  crop: FloorPlanCrop | null;
  wallHeight: number | null;
  wallThreshold: number | null;
  typeNorthAngle: number | null;
  sitePlanNorthAngle: number | null;
  latitude: number | null;
  longitude: number | null;
}

export interface FloorPlanOrientationRow {
  block: string;
  stack: string;
  northAngle: number;
  mirror: boolean;
  crop: FloorPlanCrop | null;
  wallHeight: number | null;
  wallThreshold: number | null;
  imageUrl: string;
  stale: boolean;
  updatedByCEANumber: string | null;
  updatedAt: string | Date | null;
}

export interface FloorPlanOrientationType {
  id: number;
  unitType: string;
  description: string | null;
  bedroom: number | null;
  size: number | null;
  imageUrl: string | null;
  unitCount: number;
  blocks: Array<{ block: string; stacks: string[] }>;
  rows: FloorPlanOrientationRow[];
  status: 'calibrated' | 'stale' | 'estimated' | 'missing';
}

export interface FloorPlanOrientationsResponse {
  projectId: string;
  sitePlanNorthAngle: number | null;
  unitTypes: FloorPlanOrientationType[];
}

export interface SaveFloorPlanOrientationInput {
  unitTypeId: number;
  imageUrl: string;
  block?: string;
  stack?: string;
  northAngle: number;
  mirror?: boolean;
  crop?: FloorPlanCrop | null;
  wallHeight?: number | null;
  wallThreshold?: number | null;
}

export interface TopViewingSitePlan {
  projectId: string;
  projectName: string;
  coordinateCount: number;
  thumbnailUrl?: string;
  topDate?: string;
  district?: string;
  region?: string;
  area?: string;
  location?: string;
  mapLatitude?: number | null;
  mapLongitude?: number | null;
  upcoming?: boolean;
  upcomingSeq?: number | null;
  weeklySoldCount?: number;
  weeklyRank?: number | null;
}

/**
 * EDM row associated with a project via the `EDMProject` join table.
 * Returned by `getProjectEdms`. Shape mirrors the wire response from
 * the project-essential `/edms/by-project` endpoint so the data can
 * be dropped straight into the shared `DownloadFileRow` visuals.
 */
export interface ProjectEdmFile {
  id: string;
  title: string;
  category: string | null;
  projectId: string | null;
  /** Curated thumbnail URL (EDM.Thumbnail). Used as `previewImage`. */
  cover: string | null;
  /** Portal download URL with agent details. `#` when CEA missing. */
  url: string;
  /** Portal download URL without agent details. Optional. */
  urlPublic: string | null;
  /** Suggested save-as filename (e.g. `<slug>-<id>.pdf`). */
  filename: string;
  createdAt: string | null;
  downloads: number;
}

/**
 * Launcher / eBook row associated with a project via the
 * `ProjectLauncherProject` join table. Returned by `getProjectEbooks`.
 * Shape mirrors the project-essential `/launcher-ebooks` endpoint.
 */
export interface ProjectEbookFile {
  id: string;
  title: string;
  type: string | null;
  createdAt: string | null;
  projectId: string | null;
  /** Canonical PDF location on the portal CDN. */
  url: string;
  /** Curated thumbnail — `ProjectLauncher.ThumbnailPath` or `EDM.Thumbnail`. */
  cover: string | null;
  downloads: number;
}

/**
 * Curated external link surfaced under a project's "Agent Access Folder".
 * Created / edited / deleted by the project's Head / Deputy Head; visible
 * to any authenticated agent on the project.
 */
export interface ProjectResourceLink {
  id: number;
  projectId: string;
  url: string;
  label: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Commission package for one project (Commission Structure dialog).
 * `commissionDirect` / `commissionECB` / `commissionSvc` are percentages;
 * `commission` is the free-text incentive blurb shown as the "Incentive" tag.
 */
export interface ProjectCommissionStructure {
  projectId: string;
  projectName: string;
  district: string | null;
  region: string | null;
  /** Calendar date the package was last confirmed, as `YYYY-MM-DD`. */
  commissionCorrectAsAt: string | null;
  commissionDirect: number | null;
  commissionECB: number | null;
  commissionSvc: number | null;
  commission: string | null;
  /**
   * Whether the stored Direct/ECB/Svc rates still agree with the latest EcoProp
   * snapshot (`TransformationPickerEcoPropData`). `null` = no snapshot on file,
   * so there is nothing to compare against.
   *
   * The whole `ecoProp*` block is admin-only — the server omits every one of
   * these fields for a COMM-STRUCTURE session, which is why they are optional.
   */
  ecoPropMatched?: boolean | null;
  /** EcoProp's own rates, shown in the comparison hint. */
  ecoPropDirect?: number | null;
  ecoPropECB?: number | null;
  ecoPropSvc?: number | null;
  /**
   * EcoProp's commission blurb, as the HTML they publish it in. `null` = they
   * publish none; absent = the server predates this field (an API build older
   * than the dialog), which the hint reports differently.
   */
  ecoPropCommissionDesc?: string | null;
}

export interface ProjectFactsheet {
  mediaNumber: number;
  type: string | null;
  url: string | null;
  externalLink: string | null;
  description: string | null;
  imageSmall: string | null;
  imageMedium: string | null;
  imageLarge: string | null;
  updatedAt: string | null;
}

export interface ProjectBrochure {
  mediaNumber: number;
  type: string | null;
  url: string | null;
  externalLink: string | null;
  description: string | null;
  imageSmall: string | null;
  imageMedium: string | null;
  imageLarge: string | null;
  updatedAt: string | null;
}

export interface ProjectMediaItem {
  mediaNumber: number;
  type: string | null;
  url: string | null;
  externalLink: string | null;
  description: string | null;
  size?: number | null;
  imageSmall: string | null;
  imageMedium: string | null;
  imageLarge: string | null;
  videoThumbnail?: string | null;
  updatedAt: string | null;
}

export interface ProjectFileTag {
  mediaNumber: number;
  tagId: number;
}

export interface ProjectFileConfig {
  media: ProjectMediaItem[];
  tags: {
    factsheet: ProjectFileTag | null;
    brochure: ProjectFileTag | null;
  };
}

export interface ProjectPriceEstimate {
  bedroom: number;
  estPrice: string;
}

export interface ProjectPriceEstimateConfig {
  /** Available bedrooms for the project (derived from its units). */
  bedrooms: number[];
  /** Active per-bedroom estimates. */
  estimates: ProjectPriceEstimate[];
}

export interface ClientChoiceUnit {
  projectId?: string | null;
  projectName?: string | null;
  unitNumber: string;
  unitId?: number | null;
  status?: string | null;
  bedroom?: number | null;
  areaSqft?: number | null;
  seq?: number | null;
  type?: 'SHEET' | 'PICKER' | string | null;
}

export interface ClientProfile {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  eoiNo?: string | null;
  ballotNo?: string | null;
  queueNo?: string | null;
  agency?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  choiceUnits: ClientChoiceUnit[];
}

export interface ClientListResponse {
  clients: ClientProfile[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ProjectSheetConfigItem {
  projectId: string;
  projectName?: string | null;
  sheetId?: string | null;
  priceMatrixSheetId?: string | null;
  indexSheetId?: string | null;
  importChoiceUnitAt?: string | null;
  importPriceAt?: string | null;
  importMasterAt?: string | null;
  importIndexAt?: string | null;
  liveStatus?: boolean;
  upcoming?: boolean;
  upcomingSeq?: number | null;
  showPricePromo?: boolean;
  showFacing?: boolean;
  showPsfInTowerView?: boolean;
  showPriceInTowerView?: boolean;
  showVVIPPrice?: boolean;
  sitePlanUrl?: string | null;
  sitePlanSource?: 'projectPickerMedia' | 'projectMedia' | null;
  sitePlanPickerUrl?: string | null;
  sitePlanPickerUpdatedAt?: string | null;
  sitePlanFallbackUrl?: string | null;
}

export interface LiveQueueSummary {
  projectId: string;
  liveStatusEnabled: boolean;
  updatedAt: string | null;
  rows: [
    { label: string | null; value: string | null },
    { label: string | null; value: string | null },
  ];
}

export interface PriceMatrixSimulationUnit {
  unitId: number;
  unitNumber: string;
  price: number | null;
  psf: number | null;
  status: string | null;
}

export interface PriceMatrixSimulationResponse {
  projectId: string;
  sheetId: string;
  sheetUrl: string | null;
  sheetName?: string | null;
  matchedUnits: number;
  unmatchedUnits: string[];
  units: PriceMatrixSimulationUnit[];
  updatedAt: string;
}

export interface TrainStation {
  id: string;
  /** e.g. `King Albert Park MRT Station (DT6)`. */
  name: string;
  chineseName?: string;
  /** Station code for this row's line, e.g. `DT6`. */
  code?: string;
  /** Line id, e.g. `DT`. */
  mrtLine?: string;
  /** Official line colour, e.g. `#3860bf`. */
  mrtColorCode?: string;
  /** `MRT` or `LRT`. */
  stationType?: string;
  /** False for stations announced but not yet operating. */
  isOpen?: boolean;
  latitude: number;
  longitude: number;
  distance: number; // in meters
}

export interface School {
  id: string;
  name?: string;
  type?: string;
  category?: string;
  address?: string;
  postcode?: string;
  latitude: number;
  longitude: number;
  distance: number; // in meters
}

export interface Preschool {
  id: string;
  name?: string;
  type?: string;
  category?: string;
  address?: string;
  postcode?: string;
  latitude: number;
  longitude: number;
  distance: number; // in meters
}

export interface Supermarket {
  id: string;
  name?: string;
  address?: string;
  postcode?: string;
  latitude: number;
  longitude: number;
  distance: number; // in meters
}

export interface AmenitiesResponse {
  project: {
    name: string;
    latitude: number;
    longitude: number;
    /** Land-lot rings of [longitude, latitude] — the site outline. Null when OneMap has no lot. */
    boundary?: Array<Array<[number, number]>> | null;
    lotNumber?: string | null;
    /**
     * Outline of the area the amenities were filtered against: the site grown
     * outwards by the search radius. Drawn instead of a circle round the pin.
     */
    searchArea?: Array<[number, number]> | null;
  };
  radius: number; // in meters
  amenities: {
    mrtLrt: TrainStation[];
    schools: School[];
    preschools: Preschool[];
    supermarkets: Supermarket[];
  };
}

export interface DemandSupplyTimelinePoint {
  month: string; // YYYY-MM
  hdbMop: number; // aggregate: BTO + resale
  hdbBtoMop: number;
  hdbResaleMop: number;
  condoSsd: number;
  newLaunch: number;
  gls: number;
}

export interface DemandSupplyHdbBlock {
  id: number;
  address: string;
  postal: string | null;
  latitude: number;
  longitude: number;
  units: number;
  completedYear: number;
  mopYear: number;
  distance: number; // in meters
}

// One year of units coming off their SSD/MOP lock, split by quarter.
export interface DemandSupplyOffByYearRow {
  year: number;
  q1: number;
  q2: number;
  q3: number;
  q4: number;
  total: number;
}

export interface DemandSupplyHdbResaleBlock {
  id: number;
  address: string;
  postal: string | null;
  latitude: number;
  longitude: number;
  units: number;
  blockUnits: number | null;
  firstMopMonth: string; // YYYY-MM
  lastMopMonth: string; // YYYY-MM
  lockedUnits: number;
  offMopUnits: number;
  firstLockedMopMonth: string | null; // YYYY-MM
  offByYear: DemandSupplyOffByYearRow[];
  distance: number; // in meters
}

export interface DemandSupplyCondoAddress {
  addressId: number;
  spProjectId: number | null;
  name: string;
  projectName: string | null;
  latitude: number;
  longitude: number;
  units: number;
  firstExpiryMonth: string; // YYYY-MM
  lastExpiryMonth: string; // YYYY-MM
  distance: number; // in meters
}

// Per-project SSD lock-up summary shown in the map detail card. Keyed by the
// superapp projectId; linked to the portal project (HipId) when it is also a
// Huttons new launch.
export interface DemandSupplyCondoProjectCard {
  projectId: number;
  name: string;
  totalUnits: number | null;
  completedYear: number | null;
  trackedUnits: number;
  lockedUnits: number;
  offSsdUnits: number;
  firstLockedExpiryMonth: string | null; // YYYY-MM
  offByYear: DemandSupplyOffByYearRow[];
  hipId: string | null;
  availableUnits: number | null;
  topYear: number | null;
}

export interface DemandSupplyNewLaunch {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  availableUnits: number;
  totalUnits: number | null;
  launchDate: string | null;
  topYear: number | null;
  spProjectId: number | null;
  isSubject: boolean;
  distance: number; // in meters
}

export interface DemandSupplyGls {
  id: string;
  siteName: string | null;
  location: string | null;
  latitude: number;
  longitude: number;
  units: number | null;
  dateOfAward: string | null;
  supplyMonth: string | null; // YYYY-MM when units count as supply
  awarded: boolean;
  siteStatus: string | null;
  landUse: string | null;
  expectedTop: string | null;
  tenderer: string | null;
  distance: number; // in meters
}

// One competitive series for one year, named — what sits behind a bar when an
// agent taps it. `other` rolls up everything past the server's name cap.
export interface DemandSupplyNamedUnits {
  name: string;
  units: number;
}

export interface DemandSupplyNamedGroup {
  items: DemandSupplyNamedUnits[];
  other: { count: number; units: number } | null;
}

export interface DemandSupplyYearBreakdown {
  year: number;
  condo: DemandSupplyNamedGroup;
  newLaunch: DemandSupplyNamedGroup;
  gls: DemandSupplyNamedGroup;
}

export interface DemandSupplyResponse {
  project: {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
  };
  radius: number; // in meters
  /** Inner edge of the scan ring in meters; 0 means a plain disc from the centre. */
  minRadius?: number;
  /** True when the scan covered the whole island instead of a radius. */
  nationwide?: boolean;
  /** True when the marker lists were thinned for the map; `totals` still count every unit. */
  markersCapped?: boolean;
  /** Marker cap per demand series applied to a whole-island scan. */
  markerLimit?: number;
  /** Series unit totals, counted before any marker cap. */
  totals?: {
    hdbBtoUnits: number;
    hdbResaleUnits: number;
    condoUnits: number;
    newLaunchUnits: number;
    glsUnits: number;
  };
  window: { start: string; end: string }; // YYYY-MM
  asOf: string;
  timeline: DemandSupplyTimelinePoint[];
  /** Named competitive projects per year, for the chart's tap/hover detail. */
  breakdown?: DemandSupplyYearBreakdown[];
  demand: {
    hdb: DemandSupplyHdbBlock[];
    hdbResale?: DemandSupplyHdbResaleBlock[];
    condo: DemandSupplyCondoAddress[];
    condoProjects?: Record<string, DemandSupplyCondoProjectCard>;
  };
  supply: {
    newLaunch: DemandSupplyNewLaunch[];
    gls: DemandSupplyGls[];
  };
  rules: {
    hdbMopYears: number;
    ssdYearsBeforeChange: number;
    ssdYearsAfterChange: number;
    ssdChangeDate: string;
    ecNewSaleYears: number;
    glsLeadMonths: number;
  };
}

export interface ProjectMemberInfo {
  userId: string;
  name: string | null;
  ceaNumber: string | null;
  mobile: string | null;
  photo?: string | null;
  /** Taggers whose ManagerId points at this Lead IC / IC. */
  taggers?: ProjectMemberInfo[];
  /** PCs whose ManagerId points at this Lead IC / IC. */
  pcs?: ProjectMemberInfo[];
}

export interface ProjectMembersResponse {
  members: Array<{
    role: string;
    members: ProjectMemberInfo[];
  }>;
}

export interface ProjectVerificationRecord {
  id: number;
  projectId: string;
  userId: string;
  userName: string | null;
  ceaNumber: string | null;
  role: string | null;
  siteMapVerified: boolean | null;
  unitsVerified: boolean | null;
  priceListVerified: boolean | null;
  takenAction: boolean | null;
  projectEssentialVerified: boolean | null;
  agentAccessFolderLink: string | null;
  remarks: string | null;
  adminRemark: string | null;
  verifiedAt: string | null;
}

export interface MyProjectVerificationRow {
  projectId: string;
  projectName: string | null;
  myRole: string | null;
  myVerification: ProjectVerificationRecord | null;
  agentAccessFolderLink: string | null;
  verifications: ProjectVerificationRecord[];
}

export interface AllProjectVerificationRow {
  projectId: string;
  projectName: string | null;
  agentAccessFolderLink: string | null;
  verifications: ProjectVerificationRecord[];
}

export interface ProjectVerificationSubmission {
  projectId: string;
  siteMapVerified: boolean;
  unitsVerified: boolean;
  priceListVerified: boolean;
  takenAction: boolean;
  projectEssentialVerified: boolean;
  agentAccessFolderLink?: string | null;
  remarks?: string | null;
}

export interface ResponsiblePartyProject {
  projectId: string;
  projectName: string | null;
  role: string | null;
  verified: boolean;
}

export interface ResponsiblePartyRow {
  userId: string;
  name: string | null;
  ceaNumber: string | null;
  mobile: string | null;
  projects: ResponsiblePartyProject[];
  pricePromoActivated: boolean;
  reason: string | null;
}

/** An agent pulled by a Project Action list's SELECT, before anything is stored. */
export interface CallListAgent {
  name: string | null;
  cea: string;
  mobile: string | null;
  remarks: string | null;
}

/** An admin-authored cohort query bound to one project. */
export interface ProjectActionList {
  id: number;
  projectId: string;
  projectName: string | null;
  title: string;
  sqlStatement: string;
  whatsappMessage: string | null;
  taskCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ProjectActionCoreTeamMember {
  ceaNumber: string;
  name: string | null;
  role: string | null;
  mobile: string | null;
}

/** One agent to call, as the project's lead sees it. */
export interface ProjectActionTask {
  id: number;
  listId: number;
  listTitle: string | null;
  agentCEANumber: string;
  agentName: string | null;
  agentMobile: string | null;
  remarks: string | null;
  assignedToCEANumber: string | null;
  assignedToName: string | null;
  assignedAt: string | null;
  leadRemark: string | null;
  callRemark: string | null;
  calledAt: string | null;
  linkSentCount: number;
  lastLinkSentAt: string | null;
}

export interface ProjectActionProjectTasks {
  projectId: string;
  projectName: string | null;
  coreTeam: ProjectActionCoreTeamMember[];
  tasks: ProjectActionTask[];
}

export interface ProjectActionProjectSummary {
  projectId: string;
  projectName: string | null;
  total: number;
  assigned: number;
  called: number;
  linksSent: number;
}

/** One agent on a core team member's own list, with the wa.me link pre-built. */
export interface MyCallListAgent {
  id: number;
  listId: number;
  listTitle: string | null;
  agentCEANumber: string;
  agentName: string | null;
  agentMobile: string | null;
  remarks: string | null;
  /** The lead's brief, written when the call was handed over. Read-only here. */
  leadRemark: string | null;
  callRemark: string | null;
  calledAt: string | null;
  linkSentCount: number;
  lastLinkSentAt: string | null;
  whatsappMessage: string;
  whatsappLink: string | null;
}

export interface MyCallListProject {
  projectId: string;
  projectName: string | null;
  pending: number;
  agents: MyCallListAgent[];
}

export interface ProjectActionCallerStat {
  ceaNumber: string;
  name: string | null;
  assigned: number;
  called: number;
  linksSent: number;
  lastActivityAt: string | null;
}

export interface ProjectActionProgress {
  totals: { total: number; assigned: number; called: number; linksSent: number };
  projects: ProjectActionProjectSummary[];
  callers: ProjectActionCallerStat[];
}

export interface ProjectActionAccess {
  isAdmin: boolean;
  leadsProjects: boolean;
  hasTasks: boolean;
}

/** State every assignee-owned write echoes back. */
export interface ProjectActionTaskState {
  id: number;
  calledAt: string | null;
  callRemark: string | null;
  linkSentCount: number;
  lastLinkSentAt: string | null;
}

export interface LeaderVerificationRecord {
  id: number;
  actionId: number;
  userId: string;
  userName: string | null;
  ceaNumber: string | null;
  mobile: string | null;
  designation: string | null;
  takenAction: boolean | null;
  verifiedAt: string | null;
}

export interface MyLeaderVerificationRow {
  actionId: number;
  actionKey: string;
  label: string;
  description: string | null;
  myVerification: LeaderVerificationRecord | null;
}

export interface LeaderVerificationLeadersRow {
  actionId: number;
  actionKey: string;
  label: string;
  description: string | null;
  leaders: LeaderVerificationRecord[];
}

export type CpdStatusBucket = 'Passed' | 'Exempted' | 'Ongoing';

export interface CpdStatusCounts {
  passed: number;
  exempted: number;
  ongoing: number;
}

export interface CpdUserRow {
  ceaNumber: string;
  name: string | null;
  mobile: string | null;
  email: string | null;
  /** Division name; UI shows '-' when null/empty. */
  division: string | null;
  cpdCycle: string;
  status: CpdStatusBucket;
  outstandingPE: number;
  outstandingSL: number;
  outstandingSDL: number;
  totalOutstanding: number;
  hastors: boolean;
  huttons: boolean;
  others: boolean;
}

export interface CpdHistogramBucket {
  hours: number;
  count: number;
}

export interface CpdAnalyticsResponse {
  current: {
    statusCounts: CpdStatusCounts;
    lastUploadedAt: string | null;
  };
  lastWeek: {
    statusCounts: CpdStatusCounts;
    hasSnapshot: boolean;
  };
  trainingCenters: {
    hastors: CpdStatusCounts;
    huttons: CpdStatusCounts;
    others: CpdStatusCounts;
  };
  lastWeekTrainingCenters: {
    hastors: CpdStatusCounts;
    huttons: CpdStatusCounts;
    others: CpdStatusCounts;
  };
  /** Status counts per division from current requirements (excl. display of exempted in charts). */
  divisions: Array<CpdStatusCounts & { division: string }>;
  /** Precomputed outstanding-hours histograms (excludes 0h). */
  histogram: CpdHistogramBucket[];
  histogramPE: CpdHistogramBucket[];
  histogramSL: CpdHistogramBucket[];
  histogramSDL: CpdHistogramBucket[];
}

export type CpdHoursField = 'total' | 'pe' | 'sl' | 'sdl';

export interface CpdAnalyticsUsersQuery {
  status?: CpdStatusBucket;
  center?: 'hastors' | 'huttons' | 'others';
  /** Exact division name; use '-' for agents with no division. */
  division?: string;
  /** When true (and `hours` omitted), return the full filtered list. */
  includeAll?: boolean;
  /** Exact outstanding-hours bucket; omit to get filtered histogram only. */
  hours?: number;
  /**
   * When true (and `hours` omitted), return paginated users with outstanding
   * hours > 0 for the segment filters — used by the Agents drill-down tab.
   */
  anyOutstanding?: boolean;
  /** Which outstanding-hours field to bucket/filter by. Defaults to total. */
  hoursField?: CpdHoursField;
  /** Which requirements snapshot to use. Defaults to current (active) rows. */
  snapshot?: 'current' | 'last';
  /**
   * Team CPD: restrict to one leader's team, by the leader's CEA number. Admins
   * may pass any leader; a district director may only pass their own CEA (or omit
   * it, which the server reads as "my team").
   */
  team?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'total' | 'pe' | 'sl' | 'sdl';
  sortDir?: 'asc' | 'desc';
  /** When true, returns the full matching user list (for CSV/XLSX export). */
  exportAll?: boolean;
}

export interface CpdAnalyticsUsersResponse {
  users: CpdUserRow[];
  total: number;
  page: number;
  pageSize: number;
  histogram?: CpdHistogramBucket[];
}

export type CpdTrainingCenterLabel = 'Hastors' | 'Huttons' | 'Others';

/** Segment params shared by the CPD user list and the commission drill-down. */
export type CpdSegmentQuery = Pick<
  CpdAnalyticsUsersQuery,
  'status' | 'center' | 'division' | 'hours' | 'hoursField' | 'includeAll' | 'snapshot' | 'team'
>;

export interface CpdPerformanceQuery extends CpdSegmentQuery {
  /** Inclusive month, `YYYY-MM`. Defaults to January of the current year. */
  startDate?: string;
  /** Inclusive month, `YYYY-MM`. Defaults to the current month. */
  endDate?: string;
  /** $ income at or above which an agent counts as a performer. Defaults to 10000. */
  benchmark?: number;
}

export interface CpdIncomeBand {
  key: string;
  label: string;
  min: number;
  max: number | null;
  count: number;
}

export interface CpdPerformanceResponse {
  range: { startMonth: string; endMonth: string };
  benchmark: number;
  summary: {
    agents: number;
    performers: number;
    nonPerformers: number;
    zeroIncome: number;
    totalIncome: number;
    averageIncome: number;
    medianIncome: number;
    maxIncome: number;
  };
  bands: CpdIncomeBand[];
}

export interface CpdPerformanceUserRow extends CpdUserRow {
  /** Summed TotalGCE over the requested month range. */
  income: number;
  performer: boolean;
  /** Key of the fixed income band this agent falls in. */
  band: string;
  school: CpdTrainingCenterLabel;
}

export type CpdPerformanceSortBy =
  | 'name'
  | 'cea'
  | 'mobile'
  | 'division'
  | 'school'
  | 'total'
  | 'pe'
  | 'sl'
  | 'sdl'
  | 'income'
  | 'performer';

/**
 * Per-column filters. Text columns match on substring; the numeric columns take
 * a smart expression (`>25000`, `<=4`, `1000-5000`, `>1k <5k`) — see
 * `isValidNumericFilter` for the grammar the UI validates against.
 */
export interface CpdPerformanceColumnFilters {
  name?: string;
  cea?: string;
  mobile?: string;
  division?: string;
  school?: CpdTrainingCenterLabel;
  performer?: 'yes' | 'no';
  total?: string;
  pe?: string;
  sl?: string;
  sdl?: string;
  income?: string;
}

export interface CpdPerformanceUsersQuery extends CpdPerformanceQuery {
  /** Restrict to one side of the benchmark. */
  group?: 'performer' | 'non-performer';
  /** Restrict to one income band (`CpdIncomeBand.key`). */
  band?: string;
  search?: string;
  filters?: CpdPerformanceColumnFilters;
  page?: number;
  pageSize?: number;
  sortBy?: CpdPerformanceSortBy;
  sortDir?: 'asc' | 'desc';
  /** When true, returns the full matching list (for CSV export). */
  exportAll?: boolean;
}

export interface CpdPerformanceUsersResponse {
  users: CpdPerformanceUserRow[];
  total: number;
  page: number;
  pageSize: number;
  benchmark: number;
  range: { startMonth: string; endMonth: string };
}

/**
 * One clause of a smart numeric filter: a range (`1000-5000`, `1k..5k`) or a
 * comparison (`>=25k`, `!=0`, `16`). Mirrors the grammar the backend parses.
 */
const NUMERIC_FILTER_CLAUSE =
  /^(?:(?:-?\d+(?:\.\d+)?[km]?)(?:\.\.|-)(?:-?\d+(?:\.\d+)?[km]?)|(?:>=|<=|!=|<>|==|=|>|<)?-?\d+(?:\.\d+)?[km]?)$/;

/**
 * True when a numeric column filter is understood (empty counts as valid — it
 * just means "no filter"). Whitespace-separated clauses are ANDed, so
 * `>1000 <5000` is an exclusive band.
 */
export function isValidNumericFilter(raw: string): boolean {
  const cleaned = raw
    .replace(/[$,_]/g, '')
    .replace(/([<>=!]+)\s+/g, '$1')
    .trim()
    .toLowerCase();
  if (!cleaned) return true;
  return cleaned.split(/\s+/).every((token) => NUMERIC_FILTER_CLAUSE.test(token));
}

/** Segment + month-range + benchmark params, shared by both performance endpoints. */
function buildCpdPerformanceParams(query: CpdPerformanceQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.center) params.set('center', query.center);
  if (query.division) params.set('division', query.division);
  if (query.hours !== undefined && query.hours !== null) params.set('hours', String(query.hours));
  if (query.hoursField) params.set('hoursField', query.hoursField);
  if (query.includeAll) params.set('includeAll', 'true');
  if (query.snapshot) params.set('snapshot', query.snapshot);
  if (query.team) params.set('team', query.team);
  if (query.startDate) params.set('startDate', query.startDate);
  if (query.endDate) params.set('endDate', query.endDate);
  if (query.benchmark !== undefined) params.set('benchmark', String(query.benchmark));
  return params;
}

/** A district director eligible for Team CPD — the admin's team picker. */
export interface CpdTeamLeader {
  ceaNumber: string;
  name: string | null;
  designation: string | null;
  division: string | null;
  /** `User.DownlineCount` — the whole reporting line, before division scoping. */
  downlineCount: number;
}

export interface CpdTeamOverviewResponse {
  leader: {
    name: string | null;
    ceaNumber: string;
    designation: string | null;
    division: string | null;
  };
  team: {
    /** True when the team is the leader's whole division rather than their downline. */
    isDivisionHead: boolean;
    /**
     * Active agents on the team — the whole division for a division head,
     * otherwise the leader plus their downline within their division.
     */
    members: number;
    /** How many of them carry a CPD requirement row — the chart's denominator. */
    inSnapshot: number;
  };
  statusCounts: CpdStatusCounts;
  lastUploadedAt: string | null;
}

export interface CpdEmailTemplate {
  subject: string;
  messageBody: string;
  isDefault: boolean;
}

export interface CpdRecipientEmail {
  email: string;
  name: string | null;
  ceaNumber: string | null;
}

export interface CpdOutstandingReminderSendResult {
  sent: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export interface CpdRequirementUploadRow {
  ceaNumber: string;
  cpdCycle: string;
  cpdStatus: string;
  completedPE: number;
  completedSL: number;
  completedSDL: number;
  requiredPE: number;
  requiredSL: number;
  requiredSDL: number;
  outstandingPE: number;
  outstandingSL: number;
  outstandingSDL: number;
}

export interface CpdHastorUploadRow {
  ceaNumber: string;
  packagePlan: string;
  purchaseDateTime: string;
}

export interface CpdAttendanceUploadRow {
  ceaNumber?: string;
  nric?: string;
  /** ISO date string for the session day (from filename CPD-yyyymmdd). */
  attendedDate: string;
}

export interface CpdUploadResult {
  imported: number;
  skipped: number;
  /** Attendance upload: counts keyed by human-readable skip reason. */
  skipReasons?: Record<string, number>;
  part?: number;
  totalParts?: number;
}

export interface CpdUploadOptions {
  part?: number;
  totalParts?: number;
  replaceSnapshot?: boolean;
}

export interface ProjectConfigItem {
  projectId: string;
  projectName: string | null;
  liveUpdate: boolean;
}

export interface ProjectActivitiesQuery {
  startDate?: string;
  endDate?: string;
}

export interface ProjectActivitySeriesPoint {
  date: string;
  projectName: string;
  activities: number;
}

export interface ProjectActivityTotal {
  projectName: string;
  activities: number;
}

export interface ProjectActivitiesResponse {
  /** 'all' for Admin / Project Dept, 'led' for project leads (own projects only). */
  scope: 'all' | 'led';
  accessibleProjects: string[];
  startDate: string;
  endDate: string;
  series: ProjectActivitySeriesPoint[];
  projectTotals: ProjectActivityTotal[];
  warnings: string[];
}

export interface ProjectActivityUserRow {
  ceaNumber: string;
  name: string;
  mobile: string;
  viewsAllTime: number;
  viewsPeriod: number;
  soldAllTime: number;
  soldPeriod: number;
}

export interface ProjectActivityUsersResponse {
  projectName: string;
  startDate: string;
  endDate: string;
  topUsers: ProjectActivityUserRow[];
  soldCountsRateLimited: boolean;
}

export interface MasterToolSearchFilters {
  priceMin?: string;
  priceMax?: string;
  psfMin?: string;
  psfMax?: string;
  sizeMin?: string;
  sizeMax?: string;
  floorMin?: string;
  floorMax?: string;
  bathMin?: string;
  bathMax?: string;
  districts?: string[];
  regions?: string[];
  tenure?: string[];
  projectCategories?: string[];
  topYearMin?: string;
  topYearMax?: string;
  bedroomMin?: string;
  bedroomMax?: string;
  /** Restrict results to penthouse units (PH unit type / description, or >5 bedrooms). */
  phOnly?: boolean;
  /** Restrict results to dual key units (ProjectUnitType.Description like '%Dual%key%'). */
  dualKeyOnly?: boolean;
  locationLat?: string;
  locationLng?: string;
  locationLabel?: string;
  radiusKm?: string;
  /** Include active projects that have no AVAILABLE inventory (e.g. Super Map plot-all). */
  includeProjectsWithoutUnits?: boolean;
}

export interface ProjectCategoryOption {
  id: string;
  name: string | null;
}

export interface DistrictOption {
  no: string;
  name: string | null;
  generalLocation: string | null;
}

export interface MasterToolUnit {
  id: number;
  projectId?: string | null;
  projectName: string;
  unitNumber: string;
  blockNumber: string | null;
  bedroom: number;
  bathroom: number;
  areaSqft: number;
  price: number;
  priceDps?: number | null;
  psf: number;
  unitStatus: string | null;
  floorplan: string | null;
  unitTypeDescription?: string | null;
  computedFloor?: number | null;
  computedStack?: number | null;
}

export interface MasterToolMatchedByBedroom {
  br1: number;
  br2: number;
  br3: number;
  br4: number;
  br5: number;
  ph: number;
}

export interface MasterToolMatchedLowestPriceByBedroom {
  br1: number | null;
  br2: number | null;
  br3: number | null;
  br4: number | null;
  br5: number | null;
  ph: number | null;
}

export interface MasterToolProjectResult extends ProjectDetails {
  matchedUnitCount?: number;
  matchedByBedroom?: MasterToolMatchedByBedroom;
  matchedLowestPriceByBedroom?: MasterToolMatchedLowestPriceByBedroom;
  distanceFromReferenceKm?: number | null;
}

export interface MasterToolProjectSearchResponse {
  projects: MasterToolProjectResult[];
  totalProjects: number;
  totalMatchedUnits: number;
}

export interface MasterToolUnitSearchParams {
  filters: MasterToolSearchFilters;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  unitFilters?: Record<string, string | string[]>;
  unitRangeFilters?: Record<string, { min: string; max: string }>;
  loanToValue?: number;
  tenureYears?: number;
  interestRate?: number;
}

export interface MasterToolUnitSearchResponse {
  items: MasterToolUnit[];
  projects: MasterToolProjectResult[];
  page: number;
  pageSize: number;
  totalUnits: number;
  totalPages: number;
}

export interface ProjectUnitOption {
  unitId: number | null;
  unitNumber: string;
  status: string | null;
  statusSource?: 'live' | 'project';
  bedroom: number | null;
  areaSqft?: number | null;
}

export interface BestByUnitTypeUnit {
  unitNumber: string;
  blockNumber: string;
  type: string | null;
  totalAreaSqft: number | null;
  sellingPrice: number | null;
  psf: number | null;
  sellingPriceNps: number | null;
  sellingPriceDps: number | null;
  psfNps: number | null;
  psfDps: number | null;
}

export interface BestByUnitTypeRow {
  id: number;
  description: string | null;
  unitType: string;
  units: BestByUnitTypeUnit[];
}

export interface BestByUnitTypeResponse {
  unitTypes: BestByUnitTypeRow[];
  isExecutiveCondo?: boolean;
  /** When false, PSF columns are hidden (matches ProjectSheet.ShowPsfInTowerViewInd). */
  showPsfInTowerView?: boolean;
}

export interface ShortlistItem {
  id: number;
  userId: string | null;
  projectId: string | null;
  projectName: string | null;
  unitId: number | null;
  unitNumber: string | null;
  blockNumber: string | null;
  bedroom: number | null;
  bathroom: number | null;
  areaSqft: number | null;
  price: number | null;
  psf: number | null;
  priceSource?: 'live' | 'project';
  unitStatus: string | null;
  unitStatusSource?: 'live' | 'project' | 'matrix';
  unitStatusUpdatedAt?: string | Date | null;
  floorplan: string | null;
  remark: string | null;
  createdAt: string;
  updatedAt: string;
  defunctAt?: string | null;
}

export interface ThumbsupUnit {
  id: number;
  UnitNumber: string;
  ProjectId: string;
  thumbsupCount: number;
  bedroomNumber: number;
  projectName: string;
  isLive: number;
  /** Area in sqft when provided by the API */
  size?: number | string | null;
}

export interface PricePromoUnit {
  id: number;
  UnitNumber: string;
  ProjectId: string;
  projectName: string;
  blockNumber?: string | null;
  stackNumber?: string | null;
  bedroomNumber: number;
  /** Unit type code, e.g. "PH-3" (used to derive penthouse). */
  unitType?: string | null;
  /** Unit type description, e.g. "Penthouse (5 BR With Private Lift)". */
  unitTypeDescription?: string | null;
  size?: number | string | null;
  isLive: number;
  price: number;
  pricePSF: number;
  priceChange: number;
  priceChangePercent: number;
  /** True when priceChange was measured against OtherPrice1 (the listed price). */
  usesListedPrice?: boolean;
  priceChangeAt: string | null;
  /** Project market segment (contains CCR / RCR / OCR when set). */
  region?: string | null;
  /** Project district code, e.g. "D05". */
  district?: string | null;
}

/** CCR / RCR / OCR market segment filter on a Price Promo watchlist. */
export type PricePromoWatchlistRegion = 'CCR' | 'RCR' | 'OCR';

export interface PricePromoWatchlist {
  id: number;
  clientName: string;
  mobile: string;
  /** Bedroom counts to match (at least one). */
  bedrooms: number[];
  budget: number;
  /** Market segments to match; empty = any region. */
  regions: PricePromoWatchlistRegion[];
  /** District codes ("D1".."D28") to match; empty = any district. */
  districts: string[];
  /** When the agent last opened this watchlist's matches (null = never). */
  lastViewedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PricePromoWatchlistInput {
  clientName: string;
  mobile: string;
  bedrooms: number[];
  budget: number;
  regions?: PricePromoWatchlistRegion[];
  districts?: string[];
}

/**
 * An upcoming price promo posted by the project head (or admin). The project
 * and poster are only populated when the current user manages it (`canManage`)
 * — agents get a mystery promo with just the price and description.
 */
export interface AnticipatePricePromo {
  id: number;
  projectId: string | null;
  projectName: string | null;
  price: number;
  description: string;
  createdByName: string | null;
  interestCount: number;
  /** True when the current user already registered interest. */
  myInterestRegistered: boolean;
  /** True when the current user (project head / admin) can edit or take it down. */
  canManage: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AnticipatePricePromoListResponse {
  items: AnticipatePricePromo[];
  /** True when the current user may create one (project head or admin). */
  canCreate: boolean;
  /** Projects the current user may create an anticipate promo for. */
  manageableProjects: Array<{ projectId: string; projectName: string }>;
}

export interface AnticipatePricePromoInput {
  projectId: string;
  price: number;
  description: string;
}

export interface AnticipatePricePromoInterestInput {
  cea: string;
  name: string;
  mobile: string;
}

/** A registered interest row, visible to the project head / admin. */
export interface AnticipatePricePromoInterest {
  id: number;
  cea: string;
  name: string;
  mobile: string;
  createdAt: string | null;
}

/**
 * Get authentication headers for API requests
 * Authentication is cookie-based; no Bearer token is attached here.
 */
function getAuthHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

const SIGNATURE_SECRET = process.env.REACT_APP_SIGNATURE_HEADER || process.env.SIGNATURE_HEADER || '';
let signatureKeyPromise: Promise<CryptoKey> | null = null;

function getSignatureKey(): Promise<CryptoKey> | null {
  if (!SIGNATURE_SECRET || !window.crypto?.subtle) {
    return null;
  }

  if (!signatureKeyPromise) {
    const encoder = new TextEncoder();
    signatureKeyPromise = window.crypto.subtle.importKey(
      'raw',
      encoder.encode(SIGNATURE_SECRET),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
  }

  return signatureKeyPromise;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function getCanonicalQueryString(url: string): string {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) {
    return '';
  }

  const rawQuery = url.slice(queryIndex + 1);
  const source = new URLSearchParams(rawQuery);
  const keys = Array.from(new Set(Array.from(source.keys()))).sort((a, b) => a.localeCompare(b));
  const normalized = new URLSearchParams();
  for (const key of keys) {
    const values = source.getAll(key);
    for (const value of values) {
      normalized.append(key, value);
    }
  }
  return normalized.toString();
}

function getPayloadForSignature(url: string, method: string, body?: BodyInit | null): string {
  if (method === 'GET') {
    return getCanonicalQueryString(url);
  }

  if (typeof body === 'string') {
    return body;
  }

  if (body instanceof URLSearchParams) {
    return body.toString();
  }

  return '';
}

async function createSignature(payload: string): Promise<string | null> {
  const key = getSignatureKey();
  if (!key) {
    return null;
  }

  const cryptoKey = await key;
  const encoder = new TextEncoder();
  const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(payload));
  return toHex(new Uint8Array(signature));
}

export async function signedFetch(
  input: string,
  init?: RequestInit,
  allowRetry: boolean = true,
  // When false, a 401 is returned to the caller instead of redirecting the
  // window to /unauthorized (or /sso, /dev-login). Use for non-critical
  // fire-and-forget calls that must never hijack the user's navigation.
  redirectOnAuthError: boolean = true,
): Promise<Response> {
  const method = (init?.method || 'GET').toUpperCase();
  const payload = getPayloadForSignature(input, method, init?.body);
  const signature = await createSignature(payload);
  const headers = new Headers(init?.headers);

  if (signature) {
    headers.set('x-signature', `sha256=${signature}`);
  }

  const response = await fetch(input, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    const pathname = window.location.pathname;
    const isSsoPath = pathname === '/sso';
    const isUnauthorizedPath = pathname === '/unauthorized';
    const isDevLoginPath = pathname === '/dev-login';
    const requestedApp = resolveProtectedAppFromPath(pathname);
    const requestedRedirect = getCurrentRedirectPath();
    const errorData = await response.clone().json().catch(() => ({}));
    const errorCode = typeof errorData?.code === 'string' ? errorData.code : undefined;

    if (allowRetry && errorCode === 'ACCESS_TOKEN_EXPIRED') {
      try {
        await refreshSession();
        return signedFetch(input, init, false, redirectOnAuthError);
      } catch (refreshError) {
        const refreshCode = (refreshError as ApiError | undefined)?.code || errorCode;
        if (redirectOnAuthError && !isSsoPath && !isUnauthorizedPath && !isDevLoginPath) {
          if (isDevLoginMode && isDevLoginSessionLossCode(refreshCode)) {
            redirectToDevLogin(requestedApp, requestedRedirect);
          } else {
            redirectToUnauthorized(refreshCode);
          }
        }
        return response;
      }
    }

    if (redirectOnAuthError && !isSsoPath && !isUnauthorizedPath && !isDevLoginPath) {
      if (
        errorCode === 'INVALID_USER' ||
        errorCode === 'NO_SUPER_PICKER_ACCESS' ||
        errorCode === 'NO_SUPER_CALCULATOR_ACCESS' ||
        errorCode === 'NO_SUPER_CONNECTOR_ACCESS' ||
        errorCode === 'NO_COMMUNICATION_ACCESS' ||
        errorCode === 'NO_PROJECT_ESSENTIAL_ACCESS' ||
        errorCode === 'SSO_TOKEN_EXPIRED'
      ) {
        redirectToUnauthorized(errorCode);
      } else if (isDevLoginMode && isDevLoginSessionLossCode(errorCode)) {
        redirectToDevLogin(requestedApp, requestedRedirect);
      } else {
        const loginUrl = process.env.REACT_APP_MAIN_SYSTEM_LOGIN_URL || '/sso';
        window.location.assign(loginUrl);
      }
    }
  }

  return response;
}

export interface OnemapLandLotResponse {
  lotNumber: string | null;
  ownership: string | null;
  geojson: { type: string; coordinates: unknown } | null;
}

export interface OnemapSearchResult {
  SEARCHVAL: string;
  BLK_NO?: string;
  ROAD_NAME?: string;
  BUILDING?: string;
  ADDRESS?: string;
  POSTAL?: string;
  LATITUDE?: string;
  LONGITUDE?: string;
}

export async function fetchOnemapLocationSearch(searchVal: string, pageNum = 1): Promise<OnemapSearchResult[]> {
  const q = searchVal.trim();
  if (!q) return [];
  const params = new URLSearchParams({
    searchVal: q,
    pageNum: String(pageNum),
  });
  const url = `${API_BASE}/projects/onemap-search?${params.toString()}`;
  const response = await signedFetch(url);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw createApiError(response.status, payload, 'Failed to search locations');
  }
  return Array.isArray(payload?.results) ? payload.results : [];
}

export async function fetchOnemapLandLot(latitude: number, longitude: number): Promise<OnemapLandLotResponse> {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
  });
  const url = `${API_BASE}/projects/onemap-land-lot?${params.toString()}`;
  const response = await signedFetch(url);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw createApiError(response.status, payload, 'Failed to load land lot');
  }
  return payload as OnemapLandLotResponse;
}

export interface OnemapBulkLandLotPoint {
  id: string;
  latitude: number;
  longitude: number;
}

export interface OnemapBulkLandLotResultItem extends OnemapLandLotResponse {
  id: string;
  error?: string;
}

export async function fetchOnemapLandLotsBulk(
  points: OnemapBulkLandLotPoint[],
  concurrency?: number,
): Promise<OnemapBulkLandLotResultItem[]> {
  if (points.length === 0) return [];
  const url = `${API_BASE}/projects/onemap-land-lots/bulk`;
  const response = await signedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ points, ...(concurrency ? { concurrency } : {}) }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw createApiError(response.status, payload, 'Failed to load bulk land lots');
  }
  return Array.isArray(payload?.results) ? (payload.results as OnemapBulkLandLotResultItem[]) : [];
}

const PROJECT_CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;
let projectConfigCache: { items: ProjectConfigItem[]; expiresAt: number } | null = null;
let projectConfigInFlight: Promise<ProjectConfigItem[]> | null = null;

const BULK_PROJECT_DETAILS_CACHE_TTL_MS = 2 * 60 * 1000;
const MAX_BULK_PROJECT_DETAILS_CACHE_ENTRIES = 50;
const bulkProjectDetailsCache = new Map<string, { projects: ProjectDetails[]; expiresAt: number }>();
const bulkProjectDetailsInFlight = new Map<string, Promise<ProjectDetails[]>>();

const DISTRICT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
let districtCache: { items: DistrictOption[]; expiresAt: number } | null = null;
let districtInFlight: Promise<DistrictOption[]> | null = null;

const normalizeProjectIdsForCacheKey = (projectIds: string[]): string[] =>
  Array.from(new Set(projectIds.filter(Boolean))).sort();

const getCachedBulkProjectDetails = (cacheKey: string): ProjectDetails[] | null => {
  const cached = bulkProjectDetailsCache.get(cacheKey);
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    bulkProjectDetailsCache.delete(cacheKey);
    return null;
  }
  return cached.projects;
};

const setCachedBulkProjectDetails = (cacheKey: string, projects: ProjectDetails[]): void => {
  bulkProjectDetailsCache.set(cacheKey, {
    projects,
    expiresAt: Date.now() + BULK_PROJECT_DETAILS_CACHE_TTL_MS,
  });
  while (bulkProjectDetailsCache.size > MAX_BULK_PROJECT_DETAILS_CACHE_ENTRIES) {
    const oldestKey = bulkProjectDetailsCache.keys().next().value;
    if (!oldestKey) break;
    bulkProjectDetailsCache.delete(oldestKey);
  }
};

export interface ProjectVideoItem {
  id: string;
  title: string;
  views: number;
  durationSeconds: number;
  createdAt: string | null;
  thumbnail: string | null;
  streamUrl: string | null;
  notesUrl: string | null;
  notesFilename: string | null;
}


// ── Super Showcase ────────────────────────────────────────────────────────
// Admin-curated interactive images: each showcase is one uploaded image with
// clickable areas ("hotspots") mapped to a Super Picker function.

export type SuperShowcaseActionType =
  | 'PRICE_PROMO'
  | 'THUMBSUP'
  | 'PROJECT_RESEARCH'
  | 'LINK'
  | 'ANTICIPATE_BLINKER'
  | 'SUPER_RATIO'
  | 'REGION_MARKET_DATA';

export type SuperShowcaseRegion = 'NORTH' | 'NORTH_EAST' | 'CENTRAL' | 'EAST' | 'WEST';
export interface SuperShowcaseRegionConfig { region: SuperShowcaseRegion; }

export type SuperShowcaseHotspotShape = 'rect' | 'ellipse' | 'freehand';
export interface SuperShowcasePathPoint { x: number; y: number; }
export interface SuperShowcasePathData { rings: SuperShowcasePathPoint[][]; }
export type SuperShowcasePathValue = SuperShowcasePathPoint[] | SuperShowcasePathData;

/** Payload stored on a PROJECT_RESEARCH hotspot — a pre-filled search. */
export interface SuperShowcaseProjectResearchConfig {
  viewMode: 'unit' | 'project';
  filters: MasterToolSearchFilters;
}

/** Payload stored on a LINK hotspot — the page it opens in a new tab. */
export interface SuperShowcaseLinkConfig {
  /** Absolute http(s) URL; validated on both ends before it is saved. */
  url: string;
}

/** Whatever the hotspot's action needs; narrow it by `actionType`. */
export type SuperShowcaseActionConfig =
  | SuperShowcaseProjectResearchConfig
  | SuperShowcaseLinkConfig
  | SuperShowcaseRegionConfig;

export interface SuperShowcaseHotspot {
  id: number;
  showcaseId: number;
  label: string;
  shape: SuperShowcaseHotspotShape;
  /** Percentages of the image's width / height, so the overlay scales with it. */
  x: number | null;
  y: number | null;
  width: number | null;
  height: number | null;
  pathData?: SuperShowcasePathValue | null;
  actionType: SuperShowcaseActionType;
  actionConfig: SuperShowcaseActionConfig | null;
  accentColor: string | null;
  sortOrder: number;
}

export interface SuperShowcase {
  id: number;
  title: string;
  description: string | null;
  imageUrl: string;
  imageWidth: number | null;
  imageHeight: number | null;
  sortOrder: number;
  isPublished: boolean;
  /** Stored short share link; null until the showcase is first shared. */
  shortUrl: string | null;
  createdByCeaNumber: string;
  createdAt: string | null;
  updatedAt: string | null;
  hotspots: SuperShowcaseHotspot[];
}

/** All-time GA4 engagement for one showcase (admin only). */
export interface SuperShowcaseViewStat {
  showcaseId: number;
  views: number;
  /** Distinct agents with at least one view. */
  viewers: number;
  hotspotClicks: number;
  /** Distinct agents who shared the link. */
  sharers: number;
  /** Every share action (copy, WhatsApp, native share). */
  shares: number;
  lastViewedAt: string | null;
}

export interface SuperShowcaseViewStats {
  since: string;
  asOf: string;
  items: SuperShowcaseViewStat[];
}

export interface SuperShowcaseHotspotInput {
  label: string;
  shape: SuperShowcaseHotspotShape;
  x: number | null;
  y: number | null;
  width: number | null;
  height: number | null;
  pathData?: SuperShowcasePathValue | null;
  actionType: SuperShowcaseActionType;
  actionConfig: SuperShowcaseActionConfig | null;
  accentColor: string | null;
}

export interface SuperShowcaseInput {
  title: string;
  description?: string | null;
  /** Base64 data URL. Required on create, optional on update (replaces image). */
  imageDataUrl?: string;
  imageWidth?: number | null;
  imageHeight?: number | null;
  isPublished?: boolean;
  /** Gallery position; lower sorts first. */
  sortOrder?: number;
  hotspots?: SuperShowcaseHotspotInput[];
}


// --- My Shareable Links ------------------------------------------------------

export type ShareLinkType = 'MAP_3D' | 'SUPER_RATIO' | 'PRICE_PROMO' | 'THUMBSUP_UNIT';

/** Building blocks a 3D Map link can carry beside the map (backend `SHARE_LINK_FEATURES`). */
export type ShareLinkFeature = 'PRICE_PROMO_NEARBY' | 'THUMBSUP_NEARBY' | 'VALUATION' | 'PROJECT_REQUEST' | 'SUPER_RATIO';

export interface ShareLink {
  id: number;
  uuid: string;
  linkType: ShareLinkType;
  projectId: string | null;
  projectName: string | null;
  description: string | null;
  /** Sun & light tab on a project that hides it by default; served from the project's own domain. */
  includeSunlight?: boolean;
  /** Extra blocks on the consumer site beside the 3D map; empty = map only. */
  features?: ShareLinkFeature[];
  /** Whether the consumer site's agent card shows the agent's title (missing = shown). */
  showDesignation?: boolean;
  /** With the title hidden, the agent's tagline shown in its place; null = nothing. */
  customTitle?: string | null;
  /** Super Connector tag applied to every lead from this link; null if none (or deleted since). */
  connectorTag: { id: string; name: string; color: string | null } | null;
  shortUrl: string | null;
  fullUrl: string;
  /** Every landing-page load, verified or not (agent previews excluded). */
  viewCount: number;
  /** 3D-map loads by this link's verified leads. */
  loggedInViewCount: number;
  leadCount: number;
  lastLeadAt: string | null;
  createdAt: string | null;
  revokedAt: string | null;
}

export interface ShareLinkEligibleProject {
  projectId: string;
  name: string;
  district: string | null;
  area: string | null;
  address: string | null;
  thumbnailUrl: string | null;
  /** Project hides sunlight on shared links unless the agent opts in. */
  sunlightOption?: boolean;
}

export interface ShareLinkLead {
  id: number;
  mobile: string;
  verifiedAt: string | null;
  lastSeenAt: string | null;
  /** Times this consumer opened the link's 3D map. */
  viewCount: number;
  connectorClientId: string | null;
  /** When the agent denied this lead access to the link; null = allowed. */
  accessDeniedAt: string | null;
  /** Mobile listed as a property agent (Huttons user or CEA register); null otherwise. */
  possibleAgent: { source: 'HUTTONS' | 'CEA' | null; ceaNumber: string | null; name: string | null } | null;
}

/** A Super Connector client offered in the "Send to my clients" sheet. */
export interface ShareLinkRecipientClient {
  id: string;
  name: string;
  /** Display form, e.g. "+65 9123 4567". */
  mobile: string;
  /** Digits for wa.me, country code included. */
  waNumber: string;
  tags: Array<{ id: string; name: string; color: string | null }>;
  /** When the agent last sent this link to the client; null = never. */
  lastSentAt: string | null;
  sendCount: number;
}

/** Media identity every Nearby 3D Map call is keyed by, as the north calibration is. */
function autoSiteMapIdentity(site: SitePlanData): Record<string, string> {
  return { mediaSource: site.mediaSource || '', mediaId: String(site.projectPickerMediaId ?? site.projectMediaId ?? 0), imageUrl: site.imageUrl };
}

/**
 * URL to put in a virtual-tour iframe. Follows shorteners and, when the
 * page itself blocks framing, the tour that page embeds. Null when nothing
 * frameable is found. Copy-link must keep using the stored URL.
 */
export async function resolveVirtualTourEmbedUrl(storedUrl: string): Promise<string | null> {
  const trimmed = storedUrl.trim();
  if (!trimmed) return null;
  try {
    const grant = await ensureScrapeGrant();
    const params = new URLSearchParams({ url: trimmed });
    const response = await signedFetch(
      `${API_BASE}/virtual-tour-embed?${params.toString()}`,
      { headers: { 'X-Preview-Grant': grant } },
      true,
      false,
    );
    if (!response.ok) return null;
    const data = await response.json().catch(() => null);
    const embedUrl = typeof data?.embedUrl === 'string' ? data.embedUrl.trim() : '';
    return embedUrl || null;
  } catch {
    return null;
  }
}

/** GET one of the share-link analytics endpoints; `extra` adds its scope params. */
async function fetchShareLinkAnalytics<T extends ShareLinkAnalyticsResponse>(
  path: string,
  query: ShareLinkAnalyticsQuery,
  extra: Record<string, string> = {},
): Promise<T> {
  const params = new URLSearchParams();
  params.set('startDate', query.startDate);
  params.set('endDate', query.endDate);
  if (query.excludePossibleAgents) params.set('excludePossibleAgents', '1');
  Object.entries(extra).forEach(([key, value]) => params.set(key, value));

  const response = await signedFetch(`${API_BASE}/${path}?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response.status, errorData, 'Failed to fetch share link analytics');
  }
  return response.json();
}

export const api = {
  async ssoLogin(token: string, app?: string): Promise<{ success: boolean; redirect?: string }> {
    const response = await fetch(`${AUTH_BASE}/auth/sso`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ token, app }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'SSO login failed');
    }

    return response.json();
  },

  async externalRequestOtp(email: string): Promise<{ success: boolean }> {
    const response = await fetch(`${AUTH_BASE}/auth/external/request-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to send OTP');
    }

    return response.json();
  },

  async externalVerifyOtp(email: string, otp: string): Promise<{ success: boolean; redirect?: string }> {
    const response = await fetch(`${AUTH_BASE}/auth/external/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, otp }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'OTP verification failed');
    }

    return response.json();
  },

  async devLogin(cea: string, app: DevLoginApp): Promise<{ success: boolean; redirect?: string }> {
    const response = await fetch(`${AUTH_BASE}/auth/dev-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ cea, app }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Dev login failed');
    }

    return response.json();
  },

  async requestAccessUpload(
    token: string,
    imageDataUrl: string,
    app: 'super-picker' | 'super-calculator' = 'super-picker',
  ): Promise<{ success: boolean; reviewedAt?: string | null; imgUrl?: string | null }> {
    const response = await fetch(`${AUTH_BASE}/auth/request-access-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ token, imageDataUrl, app }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Access upload failed');
    }

    return response.json();
  },

  async logout(): Promise<{ success: boolean }> {
    const response = await fetch(`${AUTH_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Logout failed');
    }

    return response.json();
  },

  async getProjects(): Promise<string[]> {
    try {
      const response = await signedFetch(`${API_BASE}/projects`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch projects');
      }
      const data = await response.json();
      return data.projects;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getProjectsWithSetupStatus(): Promise<Array<{ name: string; hasSetup: boolean }>> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/with-setup-status`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch projects with setup status');
      }
      const data = await response.json();
      return data.projects || [];
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getTopViewingSitePlans(): Promise<TopViewingSitePlan[]> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/top-viewing-site-plans`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch top viewing site plans');
      }
      const data = await response.json();
      return data.sitePlans || [];
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getProjectCommissionStructure(): Promise<ProjectCommissionStructure[]> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/commission-structure`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch commission structure');
      }
      const data = await response.json();
      return data.projects || [];
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },
  async getClients(params?: {
    search?: string;
    projectName?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ClientListResponse> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.search) {
        searchParams.set('search', params.search);
      }
      if (params?.projectName) {
        searchParams.set('projectName', params.projectName);
      }
      if (params?.page) {
        searchParams.set('page', String(params.page));
      }
      if (params?.pageSize) {
        searchParams.set('pageSize', String(params.pageSize));
      }
      const queryString = searchParams.toString();
      const response = await signedFetch(`${API_BASE}/clients${queryString ? `?${queryString}` : ''}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to fetch clients');
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        return { clients: data as ClientProfile[], page: 1, pageSize: data.length, total: data.length };
      }
      if (Array.isArray(data?.clients)) {
        return data as ClientListResponse;
      }
      return { clients: [], page: 1, pageSize: 10, total: 0 };
    } catch (error) {
      console.error('Error fetching clients:', error);
      throw error;
    }
  },
  async createClient(payload: {
    projectId: string;
    name?: string | null;
    phone?: string | null;
    notes?: string | null;
    eoiNo?: string | null;
    ballotNo?: string | null;
    queueNo?: string | null;
    pickerChoices?: Array<{ unitNumber?: string | null }>;
  }): Promise<ClientProfile> {
    try {
      const response = await signedFetch(`${API_BASE}/clients`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create client');
      }

      const data = await response.json();
      return data.client as ClientProfile;
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  },
  async updateClient(
    clientId: string,
    payload: {
      name?: string | null;
      phone?: string | null;
      notes?: string | null;
      pickerChoices?: Array<{ unitNumber?: string | null }>;
    }
  ): Promise<ClientProfile> {
    try {
      const response = await signedFetch(`${API_BASE}/clients/${clientId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update client');
      }

      const data = await response.json();
      return data.client as ClientProfile;
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  },
  async getSheetConfig(): Promise<ProjectSheetConfigItem[]> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/config`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to fetch sheet config');
      }
      const data = await response.json();
      return Array.isArray(data?.items) ? (data.items as ProjectSheetConfigItem[]) : [];
    } catch (error) {
      console.error('Error fetching sheet config:', error);
      throw error;
    }
  },
  async getLiveQueueSummary(projectId: string): Promise<LiveQueueSummary> {
    const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/live-queue`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to fetch live queue summary');
    }
    return response.json() as Promise<LiveQueueSummary>;
  },
  async updateSheetConfig(
    projectId: string,
    sheetId: string | null,
    priceMatrixSheetId?: string | null,
    indexSheetId?: string | null
  ): Promise<ProjectSheetConfigItem> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, sheetId, priceMatrixSheetId, indexSheetId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update sheet config');
      }
      const data = await response.json();
      return data.sheet as ProjectSheetConfigItem;
    } catch (error) {
      console.error('Error updating sheet config:', error);
      throw error;
    }
  },
  async updateSheetLiveStatus(projectId: string, liveStatus: boolean): Promise<{ projectId: string; liveStatus: boolean }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/live-status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, liveStatus }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update sheet live status');
      }
      const data = await response.json();
      return {
        projectId: data.projectId,
        liveStatus: Boolean(data.liveStatus),
      };
    } catch (error) {
      console.error('Error updating sheet live status:', error);
      throw error;
    }
  },
  async updateSheetUpcoming(projectId: string, upcoming: boolean, upcomingSeq: number | null): Promise<{ projectId: string; upcoming: boolean; upcomingSeq: number | null }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/upcoming`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, upcoming, upcomingSeq }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update sheet upcoming settings');
      }
      const data = await response.json();
      return {
        projectId: data.projectId,
        upcoming: Boolean(data.upcoming),
        upcomingSeq: data.upcomingSeq ?? null,
      };
    } catch (error) {
      console.error('Error updating sheet upcoming settings:', error);
      throw error;
    }
  },
  async loadPriceMatrixSimulation(projectId: string): Promise<PriceMatrixSimulationResponse> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/price-matrix/load`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to load price matrix simulation');
      }
      return await response.json();
    } catch (error) {
      console.error('Error loading price matrix simulation:', error);
      throw error;
    }
  },
  async refreshPriceMatrixSimulation(projectId: string): Promise<PriceMatrixSimulationResponse> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/price-matrix/refresh`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to refresh price matrix simulation');
      }
      return await response.json();
    } catch (error) {
      console.error('Error refreshing price matrix simulation:', error);
      throw error;
    }
  },
  async updatePriceMatrixSimulationStatuses(
    projectId: string,
    updates: Array<{ unitId: number; status: string | null }>
  ): Promise<{ projectId: string; updatedAt: string; updates: Array<{ unitId: number; status: string }> }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/price-matrix/statuses`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, updates }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update price matrix simulation statuses');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating price matrix simulation statuses:', error);
      throw error;
    }
  },
  async updateSheetShowPricePromo(projectId: string, showPricePromo: boolean): Promise<{ projectId: string; showPricePromo: boolean }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/show-price-promo`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, showPricePromo }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update show price promo setting');
      }
      const data = await response.json();
      return {
        projectId: data.projectId,
        showPricePromo: Boolean(data.showPricePromo),
      };
    } catch (error) {
      console.error('Error updating show price promo setting:', error);
      throw error;
    }
  },
  async updateSheetShowFacing(projectId: string, showFacing: boolean): Promise<{ projectId: string; showFacing: boolean }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/show-facing`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, showFacing }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update show facing setting');
      }
      const data = await response.json();
      return {
        projectId: data.projectId,
        showFacing: Boolean(data.showFacing),
      };
    } catch (error) {
      console.error('Error updating show facing setting:', error);
      throw error;
    }
  },
  async updateSheetShowPsfInTowerView(projectId: string, showPsfInTowerView: boolean): Promise<{ projectId: string; showPsfInTowerView: boolean }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/show-psf-in-tower-view`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, showPsfInTowerView }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update show PSF in tower view setting');
      }
      const data = await response.json();
      return {
        projectId: data.projectId,
        showPsfInTowerView: Boolean(data.showPsfInTowerView),
      };
    } catch (error) {
      console.error('Error updating show PSF in tower view setting:', error);
      throw error;
    }
  },
  async updateSheetShowPriceInTowerView(projectId: string, showPriceInTowerView: boolean): Promise<{ projectId: string; showPriceInTowerView: boolean }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/show-price-in-tower-view`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, showPriceInTowerView }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update show price in tower view setting');
      }
      const data = await response.json();
      return {
        projectId: data.projectId,
        showPriceInTowerView: Boolean(data.showPriceInTowerView),
      };
    } catch (error) {
      console.error('Error updating show price in tower view setting:', error);
      throw error;
    }
  },
  async updateSheetShowVVIPPrice(projectId: string, showVVIPPrice: boolean): Promise<{ projectId: string; showVVIPPrice: boolean }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/show-vvip-price`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, showVVIPPrice }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update show VVIP price setting');
      }
      const data = await response.json();
      return {
        projectId: data.projectId,
        showVVIPPrice: Boolean(data.showVVIPPrice),
      };
    } catch (error) {
      console.error('Error updating show VVIP price setting:', error);
      throw error;
    }
  },
  async importSheetClients(projectId: string): Promise<{ importChoiceUnitAt?: string }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/import`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import sheet clients');
      }
      const data = await response.json();
      
      // Trigger CSV download
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `import-results-${projectId}-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return {
        importChoiceUnitAt: data.importChoiceUnitAt,
      };
    } catch (error) {
      console.error('Error importing sheet clients:', error);
      throw error;
    }
  },
  async importSheetMaster(projectId: string): Promise<{ imported: number; failed: number; importMasterAt?: string; results: Array<{ sheetLineNo: number; result: string }> }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/import-master`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import master sheet');
      }
      const data = await response.json();
      return {
        imported: data.imported ?? 0,
        failed: data.failed ?? 0,
        importMasterAt: data.importMasterAt,
        results: Array.isArray(data.results) ? data.results : [],
      };
    } catch (error) {
      console.error('Error importing master sheet:', error);
      throw error;
    }
  },
  async importSheetIndex(projectId: string): Promise<{
    imported: number;
    failed: number;
    importIndexAt?: string;
    transactionsInserted?: number;
    transactionsReplacedFromMaster?: number;
    transactionsSkippedExisting?: number;
    transactionsSkippedNoActivity?: number;
  }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/import-index`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import index sheet');
      }
      const data = await response.json();
      return {
        imported: data.imported ?? 0,
        failed: data.failed ?? 0,
        importIndexAt: data.importIndexAt,
        transactionsInserted: data.transactionsInserted ?? 0,
        transactionsReplacedFromMaster: data.transactionsReplacedFromMaster ?? 0,
        transactionsSkippedExisting: data.transactionsSkippedExisting ?? 0,
        transactionsSkippedNoActivity: data.transactionsSkippedNoActivity ?? 0,
      };
    } catch (error) {
      console.error('Error importing index sheet:', error);
      throw error;
    }
  },
  async importSheetPrice(projectId: string): Promise<{ imported: number; failed: number; importPriceAt?: string; results: Array<{ unitNo: string; result: string }> }> {
    try {
      const response = await signedFetch(`${API_BASE}/sheets/import-price`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to import sheet price');
      }
      const data = await response.json();
      return {
        imported: data.imported ?? 0,
        failed: data.failed ?? 0,
        importPriceAt: data.importPriceAt,
        results: Array.isArray(data.results) ? data.results : [],
      };
    } catch (error) {
      console.error('Error importing sheet price:', error);
      throw error;
    }
  },
  async getShortlist(): Promise<ShortlistItem[]> {
    try {
      const response = await signedFetch(`${API_BASE}/shortlist`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch shortlist');
      }
      const data = await response.json();
      return Array.isArray(data.items) ? (data.items as ShortlistItem[]) : [];
    } catch (error) {
      console.error('Error fetching shortlist:', error);
      throw error;
    }
  },
  async addShortlist(payload: { unitId: number; projectId?: string; remark?: string }): Promise<ShortlistItem> {
    try {
      const response = await signedFetch(`${API_BASE}/shortlist`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to add shortlist item');
      }
      const data = await response.json();
      return data.item as ShortlistItem;
    } catch (error) {
      console.error('Error adding shortlist item:', error);
      throw error;
    }
  },
  async removeShortlist(shortlistId: number): Promise<void> {
    try {
      const response = await signedFetch(`${API_BASE}/shortlist/${shortlistId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to remove shortlist item');
      }
    } catch (error) {
      console.error('Error removing shortlist item:', error);
      throw error;
    }
  },
  async updateShortlistRemark(shortlistId: number, remark: string | null): Promise<ShortlistItem> {
    try {
      const response = await signedFetch(`${API_BASE}/shortlist/${shortlistId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ remark }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update remark');
      }
      const data = await response.json();
      return data.item as ShortlistItem;
    } catch (error) {
      console.error('Error updating shortlist remark:', error);
      throw error;
    }
  },

  async getThumbsupUnits(): Promise<ThumbsupUnit[]> {
    try {
      const response = await signedFetch(`${API_BASE}/thumbsup-units`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch thumbsup units');
      }
      const data = await response.json();
      return Array.isArray(data.items) ? (data.items as ThumbsupUnit[]) : [];
    } catch (error) {
      console.error('Error fetching thumbsup units:', error);
      throw error;
    }
  },

  async getPricePromoUnits(): Promise<PricePromoUnit[]> {
    try {
      const response = await signedFetch(`${API_BASE}/price-promo-units`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch price promo units');
      }
      const data = await response.json();
      return Array.isArray(data.items) ? (data.items as PricePromoUnit[]) : [];
    } catch (error) {
      console.error('Error fetching price promo units:', error);
      throw error;
    }
  },

  async getProjectUnits(projectId: string): Promise<ProjectUnitOption[]> {
    try {
      const params = new URLSearchParams();
      params.append('projectId', projectId);
      const response = await signedFetch(`${API_BASE}/projects/units?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch project units');
      }
      const data = await response.json();
      return data.units;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async resolveProjectIdByEcoPropId(ecoPropId: string): Promise<string> {
    try {
      const params = new URLSearchParams({ ecoPropId });
      const response = await signedFetch(`${API_BASE}/projects/ecoprop-project-id?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to resolve Ecoprop project id');
      }
      const data = await response.json();
      return data.projectId;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getFloorPlanUnitDetails(unitId: number): Promise<TowerViewUnit> {
    try {
      const response = await signedFetch(`${API_BASE}/units/${encodeURIComponent(String(unitId))}/floor-plan-details`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch floor plan details');
      }
      const data = await response.json();
      return data.unit as TowerViewUnit;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getComparableUnits(
    projectId: string,
    unitNumber: string,
    loanToValue: number = 0.75,
    tenureYears: number = 30,
    interestRate: number = 0.0155,
    unitId?: number | null,
    areaTolerancePercent: number = 0
  ): Promise<ComparableUnitsResponse> {
    try {
      const params = new URLSearchParams({
        projectId,
        unitNumber,
        loanToValue: loanToValue.toString(),
        tenureYears: tenureYears.toString(),
        interestRate: interestRate.toString(),
      });
      if (unitId != null) {
        params.set('unitId', String(unitId));
      }
      if (areaTolerancePercent > 0) {
        params.set('areaTolerancePercent', String(areaTolerancePercent));
      }

      const response = await signedFetch(`${API_BASE}/units/comparable?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch comparable units');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getComparableUnitsIndicative(
    projectId: string,
    bedroom: number,
    referencePrice: number,
    sizeMin?: number,
    sizeMax?: number,
    loanToValue: number = 0.75,
    tenureYears: number = 30,
    interestRate: number = 0.03
  ): Promise<ComparableUnitsResponse> {
    try {
      const params = new URLSearchParams({
        projectId,
        bedroom: bedroom.toString(),
        referencePrice: referencePrice.toString(),
        loanToValue: loanToValue.toString(),
        tenureYears: tenureYears.toString(),
        interestRate: interestRate.toString(),
      });
      if (sizeMin != null && Number.isFinite(sizeMin)) {
        params.set('sizeMin', sizeMin.toString());
      }
      if (sizeMax != null && Number.isFinite(sizeMax)) {
        params.set('sizeMax', sizeMax.toString());
      }

      const response = await signedFetch(`${API_BASE}/units/comparable-indicative?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch comparable units');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getProjectDetails(projectId: string): Promise<ProjectDetails> {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await signedFetch(`${API_BASE}/projects/details?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch project details');
      }
      const data = await response.json();
      return data.project;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getBestByUnitType(projectId: string): Promise<BestByUnitTypeResponse> {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await signedFetch(`${API_BASE}/projects/best-by-unit-type?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch best by unit type');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getProjectConfig(): Promise<ProjectConfigItem[]> {
    if (projectConfigCache && projectConfigCache.expiresAt > Date.now()) {
      return projectConfigCache.items;
    }
    if (projectConfigInFlight) {
      return projectConfigInFlight;
    }

    const request = (async () => {
      try {
        const response = await signedFetch(`${API_BASE}/projects/config`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to fetch project config');
        }
        const data = await response.json();
        const items = Array.isArray(data.items) ? (data.items as ProjectConfigItem[]) : [];
        projectConfigCache = {
          items,
          expiresAt: Date.now() + PROJECT_CONFIG_CACHE_TTL_MS,
        };
        return items;
      } catch (error) {
        console.error('Error fetching project config:', error);
        throw error;
      } finally {
        projectConfigInFlight = null;
      }
    })();

    projectConfigInFlight = request;
    return request;
  },
  async updateProjectConfig(projectId: string, liveUpdate: boolean): Promise<ProjectConfigItem> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, liveUpdate }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update project config');
      }
      const data = await response.json();
      projectConfigCache = null;
      return data.config as ProjectConfigItem;
    } catch (error) {
      console.error('Error updating project config:', error);
      throw error;
    }
  },

  async getBulkProjectDetails(projectIds: string[]): Promise<ProjectDetails[]> {
    const normalizedProjectIds = normalizeProjectIdsForCacheKey(projectIds);
    if (normalizedProjectIds.length === 0) {
      return [];
    }
    const cacheKey = normalizedProjectIds.join(',');

    const cached = getCachedBulkProjectDetails(cacheKey);
    if (cached) {
      return cached;
    }

    const inFlight = bulkProjectDetailsInFlight.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const request = (async () => {
      try {
        const response = await signedFetch(`${API_BASE}/projects/bulk-details`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ projectIds: normalizedProjectIds }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to fetch project details');
        }
        const data = await response.json();
        const projects = Array.isArray(data.projects) ? (data.projects as ProjectDetails[]) : [];
        setCachedBulkProjectDetails(cacheKey, projects);
        return projects;
      } catch (error) {
        if (error instanceof TypeError && error.message.includes('fetch')) {
          throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
        }
        throw error;
      } finally {
        bulkProjectDetailsInFlight.delete(cacheKey);
      }
    })();

    bulkProjectDetailsInFlight.set(cacheKey, request);
    return request;
  },
  async searchProjects(filters: MasterToolSearchFilters): Promise<MasterToolProjectSearchResponse> {
    try {
      const params = new URLSearchParams();
      if (filters.priceMin) params.set('priceMin', filters.priceMin);
      if (filters.priceMax) params.set('priceMax', filters.priceMax);
      if (filters.psfMin) params.set('psfMin', filters.psfMin);
      if (filters.psfMax) params.set('psfMax', filters.psfMax);
      if (filters.sizeMin) params.set('sizeMin', filters.sizeMin);
      if (filters.sizeMax) params.set('sizeMax', filters.sizeMax);
      if (filters.floorMin) params.set('floorMin', filters.floorMin);
      if (filters.floorMax) params.set('floorMax', filters.floorMax);
      if (filters.bathMin) params.set('bathMin', filters.bathMin);
      if (filters.bathMax) params.set('bathMax', filters.bathMax);
      if (filters.districts && filters.districts.length > 0) params.set('districts', filters.districts.join(','));
      if (filters.regions && filters.regions.length > 0) params.set('regions', filters.regions.join(','));
      if (filters.tenure && filters.tenure.length > 0) params.set('tenure', filters.tenure.join(','));
      if (filters.projectCategories && filters.projectCategories.length > 0) params.set('projectCategories', filters.projectCategories.join(','));
      if (filters.topYearMin) params.set('topYearMin', filters.topYearMin);
      if (filters.topYearMax) params.set('topYearMax', filters.topYearMax);
      if (filters.bedroomMin) params.set('bedroomMin', filters.bedroomMin);
      if (filters.bedroomMax) params.set('bedroomMax', filters.bedroomMax);
      if (filters.phOnly) params.set('phOnly', 'true');
      if (filters.dualKeyOnly) params.set('dualKeyOnly', 'true');
      if (filters.locationLat) params.set('locationLat', filters.locationLat);
      if (filters.locationLng) params.set('locationLng', filters.locationLng);
      if (filters.radiusKm) params.set('radiusKm', filters.radiusKm);
      if (filters.includeProjectsWithoutUnits) params.set('includeProjectsWithoutUnits', 'true');
      const response = await signedFetch(`${API_BASE}/projects/search-projects?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to search projects');
      }
      const data = await response.json();
      return {
        projects: Array.isArray(data.projects) ? data.projects : [],
        totalProjects: Number.isFinite(Number(data.totalProjects)) ? Number(data.totalProjects) : 0,
        totalMatchedUnits: Number.isFinite(Number(data.totalMatchedUnits)) ? Number(data.totalMatchedUnits) : 0,
      };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async searchUnits(paramsInput: MasterToolUnitSearchParams): Promise<MasterToolUnitSearchResponse> {
    try {
      const params = new URLSearchParams();
      const filters = paramsInput.filters;
      if (filters.priceMin) params.set('priceMin', filters.priceMin);
      if (filters.priceMax) params.set('priceMax', filters.priceMax);
      if (filters.psfMin) params.set('psfMin', filters.psfMin);
      if (filters.psfMax) params.set('psfMax', filters.psfMax);
      if (filters.sizeMin) params.set('sizeMin', filters.sizeMin);
      if (filters.sizeMax) params.set('sizeMax', filters.sizeMax);
      if (filters.floorMin) params.set('floorMin', filters.floorMin);
      if (filters.floorMax) params.set('floorMax', filters.floorMax);
      if (filters.bathMin) params.set('bathMin', filters.bathMin);
      if (filters.bathMax) params.set('bathMax', filters.bathMax);
      if (filters.districts && filters.districts.length > 0) params.set('districts', filters.districts.join(','));
      if (filters.regions && filters.regions.length > 0) params.set('regions', filters.regions.join(','));
      if (filters.tenure && filters.tenure.length > 0) params.set('tenure', filters.tenure.join(','));
      if (filters.projectCategories && filters.projectCategories.length > 0) params.set('projectCategories', filters.projectCategories.join(','));
      if (filters.topYearMin) params.set('topYearMin', filters.topYearMin);
      if (filters.topYearMax) params.set('topYearMax', filters.topYearMax);
      if (filters.bedroomMin) params.set('bedroomMin', filters.bedroomMin);
      if (filters.bedroomMax) params.set('bedroomMax', filters.bedroomMax);
      if (filters.phOnly) params.set('phOnly', 'true');
      if (filters.dualKeyOnly) params.set('dualKeyOnly', 'true');
      if (filters.locationLat) params.set('locationLat', filters.locationLat);
      if (filters.locationLng) params.set('locationLng', filters.locationLng);
      if (filters.radiusKm) params.set('radiusKm', filters.radiusKm);
      params.set('page', String(paramsInput.page));
      params.set('pageSize', String(paramsInput.pageSize));
      if (paramsInput.sortBy) params.set('sortBy', paramsInput.sortBy);
      if (paramsInput.sortDir) params.set('sortDir', paramsInput.sortDir);
      if (paramsInput.loanToValue != null) params.set('loanToValue', String(paramsInput.loanToValue));
      if (paramsInput.tenureYears != null) params.set('tenureYears', String(paramsInput.tenureYears));
      if (paramsInput.interestRate != null) params.set('interestRate', String(paramsInput.interestRate));

      const unitFilters = paramsInput.unitFilters ?? {};
      const unitRangeFilters = paramsInput.unitRangeFilters ?? {};
      const toCsv = (value: string | string[] | undefined): string | null => {
        if (!Array.isArray(value)) return null;
        const cleaned = value.map((item) => item.trim()).filter(Boolean);
        return cleaned.length > 0 ? cleaned.join(',') : null;
      };
      const setIfPresent = (key: string, value: string | undefined) => {
        const trimmed = value?.trim();
        if (trimmed) params.set(key, trimmed);
      };
      const unitProjectNames = toCsv(unitFilters.projectName);
      if (unitProjectNames) params.set('unitProjectNames', unitProjectNames);
      const unitProjectCategories = toCsv(unitFilters.projectCategory);
      if (unitProjectCategories) params.set('unitProjectCategories', unitProjectCategories);
      const unitDistricts = toCsv(unitFilters.district);
      if (unitDistricts) params.set('unitDistricts', unitDistricts);
      const unitRegions = toCsv(unitFilters.region);
      if (unitRegions) params.set('unitRegions', unitRegions);
      const unitDistrictLocations = toCsv(unitFilters.districtLocation);
      if (unitDistrictLocations) params.set('unitDistrictLocations', unitDistrictLocations);
      const unitNearestMrts = toCsv(unitFilters.nearestMrt);
      if (unitNearestMrts) params.set('unitNearestMrts', unitNearestMrts);
      const unitTenure = toCsv(unitFilters.tenure);
      if (unitTenure) params.set('unitTenure', unitTenure);

      setIfPresent('unitNumber', typeof unitFilters.unitNumber === 'string' ? unitFilters.unitNumber : undefined);
      setIfPresent('unitBlockNumber', typeof unitFilters.blockNumber === 'string' ? unitFilters.blockNumber : undefined);
      setIfPresent('unitFloorMin', unitRangeFilters.floor?.min);
      setIfPresent('unitFloorMax', unitRangeFilters.floor?.max);
      setIfPresent('unitStackMin', unitRangeFilters.stack?.min);
      setIfPresent('unitStackMax', unitRangeFilters.stack?.max);
      setIfPresent('unitBedroomMin', unitRangeFilters.bedroom?.min);
      setIfPresent('unitBedroomMax', unitRangeFilters.bedroom?.max);
      setIfPresent('unitBathroomMin', unitRangeFilters.bathroom?.min);
      setIfPresent('unitBathroomMax', unitRangeFilters.bathroom?.max);
      setIfPresent('unitAreaMin', unitRangeFilters.areaSqft?.min);
      setIfPresent('unitAreaMax', unitRangeFilters.areaSqft?.max);
      setIfPresent('unitPriceMin', unitRangeFilters.price?.min);
      setIfPresent('unitPriceMax', unitRangeFilters.price?.max);
      setIfPresent('unitDpsMin', unitRangeFilters.dps?.min);
      setIfPresent('unitDpsMax', unitRangeFilters.dps?.max);
      setIfPresent('unitPsfMin', unitRangeFilters.psf?.min);
      setIfPresent('unitPsfMax', unitRangeFilters.psf?.max);
      setIfPresent('unitOutlayMin', unitRangeFilters.outlay?.min);
      setIfPresent('unitOutlayMax', unitRangeFilters.outlay?.max);
      setIfPresent('unitInstallmentMin', unitRangeFilters.installment?.min);
      setIfPresent('unitInstallmentMax', unitRangeFilters.installment?.max);
      setIfPresent('unitWalkingTimeToMrtMin', unitRangeFilters.walkingTimeToMrt?.min);
      setIfPresent('unitWalkingTimeToMrtMax', unitRangeFilters.walkingTimeToMrt?.max);
      setIfPresent('unitTopYearMin', unitRangeFilters.topYear?.min);
      setIfPresent('unitTopYearMax', unitRangeFilters.topYear?.max);

      const response = await signedFetch(`${API_BASE}/projects/search-units?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to search units');
      }
      const data = await response.json();
      return {
        items: Array.isArray(data.items) ? data.items : [],
        projects: Array.isArray(data.projects) ? data.projects : [],
        page: Number.isFinite(Number(data.page)) ? Number(data.page) : paramsInput.page,
        pageSize: Number.isFinite(Number(data.pageSize)) ? Number(data.pageSize) : paramsInput.pageSize,
        totalUnits: Number.isFinite(Number(data.totalUnits)) ? Number(data.totalUnits) : 0,
        totalPages: Number.isFinite(Number(data.totalPages)) ? Number(data.totalPages) : 0,
      };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getProjectCategories(): Promise<ProjectCategoryOption[]> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/categories`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch project categories');
      }
      const data = await response.json();
      return Array.isArray(data.items) ? data.items : [];
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getDistricts(): Promise<DistrictOption[]> {
    if (districtCache && districtCache.expiresAt > Date.now()) {
      return districtCache.items;
    }
    if (districtInFlight) {
      return districtInFlight;
    }

    const request = (async () => {
      try {
        const response = await signedFetch(`${API_BASE}/projects/districts`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to fetch districts');
        }
        const data = await response.json();
        const items: DistrictOption[] = Array.isArray(data.items) ? data.items : [];
        districtCache = {
          items,
          expiresAt: Date.now() + DISTRICT_CACHE_TTL_MS,
        };
        return items;
      } catch (error) {
        console.error('Error fetching districts:', error);
        throw error;
      } finally {
        districtInFlight = null;
      }
    })();

    districtInFlight = request;
    return request;
  },

  async getTowerViewUnits(projectId: string): Promise<{ units: TowerViewUnit[]; unitTypes: string[]; showPsfInTowerView?: boolean; showPriceInTowerView?: boolean }> {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await signedFetch(`${API_BASE}/projects/tower-view-units?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch tower view units');
      }
      const data = await response.json();
      return {
        units: data.units,
        unitTypes: data.unitTypes || [],
        showPsfInTowerView: data.showPsfInTowerView !== false,
        showPriceInTowerView: data.showPriceInTowerView !== false,
      };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getAvailableUnitsByBudget(
    minPrice: number,
    maxPrice: number,
    projectId?: string
  ): Promise<TowerViewUnit[]> {
    try {
      const params = new URLSearchParams({
        minPrice: String(minPrice),
        maxPrice: String(maxPrice),
      });
      if (projectId) {
        params.set('projectId', projectId);
      }
      const response = await signedFetch(`${API_BASE}/projects/available-units-by-budget?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch budget-matched units');
      }
      const data = await response.json();
      return data.units || [];
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getSitePlan(projectId: string): Promise<SitePlanData | null> {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await signedFetch(`${API_BASE}/projects/site-plan?${params}`, {
        cache: 'no-store',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch site plan');
      }
      const data = await response.json();
      return data.sitePlan;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async sitePlanFloorGroups(projectId: string): Promise<{ configuration: SitePlanFloorGroups | null; imageUrl: string }> {
    const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/site-plan-floor-groups`, { headers: getAuthHeaders() });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Unable to load floor groups');
    return data;
  },

  async saveSitePlanFloorGroups(projectId: string, site: SitePlanData, configuration: SitePlanFloorGroups): Promise<void> {
    const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/site-plan-floor-groups`, {
      method: 'PUT', headers: getAuthHeaders(),
      body: JSON.stringify({ configuration, imageUrl: site.imageUrl, mediaSource: site.mediaSource,
        mediaId: site.projectPickerMediaId ?? site.projectMediaId }),
    });
    if (!response.ok) { const data = await response.json(); throw new Error(data.error || 'Unable to save floor groups'); }
  },

  async uploadSitePlanFloorGroupDiagram(projectId: string, groupId: string, imageDataUrl: string): Promise<{
    imageUrl: string; storagePath: string; uploadedAt: string;
  }> {
    const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/site-plan-diagram-groups/${encodeURIComponent(groupId)}/image`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ imageDataUrl }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Could not upload the diagram');
    }
    return response.json();
  },

  async sitePlanSolar(projectId: string, site: SitePlanData, northAngle?: number): Promise<{ northAngle: number | null }> {
    const identity = { mediaSource: site.mediaSource || '', mediaId: String(site.projectPickerMediaId ?? site.projectMediaId ?? 0), imageUrl: site.imageUrl };
    const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/site-plan-solar${northAngle == null ? `?${new URLSearchParams(identity)}` : ''}`, {
      method: northAngle == null ? 'GET' : 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      ...(northAngle == null ? {} : { body: JSON.stringify({ ...identity, northAngle }) }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Unable to access north calibration');
    return data;
  },

  async autoSiteMap(projectId: string, site: SitePlanData): Promise<AutoSiteMapRecord> {
    const response = await signedFetch(`${API_BASE}/picker-auto-site-map/projects/${encodeURIComponent(projectId)}?${new URLSearchParams(autoSiteMapIdentity(site))}`,
      { headers: getAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to load the Nearby 3D Map');
    return data as AutoSiteMapRecord;
  },

  async saveAutoSiteMap(projectId: string, site: SitePlanData, fit: {
    crop: Array<{ x: number; y: number }>; transform: AutoSiteMapTransform; footprints: AutoSiteMapFootprint[];
    fitScore: number | null; fitMethod: 'PIXEL' | 'AI' | 'ADJUSTED'; lots?: AutoSiteMapLot[];
  }): Promise<AutoSiteMapRecord> {
    const response = await signedFetch(`${API_BASE}/picker-auto-site-map/projects/${encodeURIComponent(projectId)}`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...autoSiteMapIdentity(site), ...fit }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to save the Nearby 3D Map');
    return data as AutoSiteMapRecord;
  },

  async approveAutoSiteMap(projectId: string, site: SitePlanData, approved: boolean): Promise<AutoSiteMapRecord> {
    const response = await signedFetch(`${API_BASE}/picker-auto-site-map/projects/${encodeURIComponent(projectId)}/approve`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...autoSiteMapIdentity(site), approved }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to update the approval');
    return data as AutoSiteMapRecord;
  },

  async autoSiteMapAiExtract(projectId: string, site: SitePlanData, knownBlocks: string[],
    stackHints: Array<{ block: string | null; stack: string; x: number; y: number }>, refresh = false): Promise<AutoSiteMapAiExtract> {
    const response = await signedFetch(`${API_BASE}/picker-auto-site-map/projects/${encodeURIComponent(projectId)}/ai-extract`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...autoSiteMapIdentity(site), knownBlocks, stackHints, refresh }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to analyse the site plan');
    return data.aiExtract as AutoSiteMapAiExtract;
  },

  async autoSiteMapLots(projectId: string, site: SitePlanData, points: Array<{ lat: number; lng: number }>): Promise<{ lots: AutoSiteMapLot[]; added: number }> {
    const response = await signedFetch(`${API_BASE}/picker-auto-site-map/projects/${encodeURIComponent(projectId)}/lots`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...autoSiteMapIdentity(site), points }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to look up neighbouring lots');
    return data;
  },

  async autoSiteMapNeighbours(projectId: string): Promise<AutoSiteMapNeighbours> {
    const response = await signedFetch(`${API_BASE}/picker-auto-site-map/projects/${encodeURIComponent(projectId)}/neighbours`,
      { headers: getAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to load neighbouring buildings');
    return data as AutoSiteMapNeighbours;
  },

  /** Calibration one unit's floor plan should render with (3DS / admin). */
  async floorPlanSolar(unitId: number): Promise<FloorPlanSolarResolution> {
    const response = await signedFetch(`${API_BASE}/units/${encodeURIComponent(String(unitId))}/floor-plan-solar`, { headers: getAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to access floor-plan north calibration');
    return data as FloorPlanSolarResolution;
  },

  async adminFloorPlanOrientations(projectId: string): Promise<FloorPlanOrientationsResponse> {
    const response = await signedFetch(`${API_BASE}/admin/projects/${encodeURIComponent(projectId)}/floor-plan-orientations`, { headers: getAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to load floor-plan orientations');
    return data as FloorPlanOrientationsResponse;
  },

  async saveFloorPlanOrientation(projectId: string, input: SaveFloorPlanOrientationInput): Promise<{ row: FloorPlanOrientationRow | null }> {
    const response = await signedFetch(`${API_BASE}/admin/projects/${encodeURIComponent(projectId)}/floor-plan-orientations`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to save floor-plan north');
    return data;
  },

  async deleteFloorPlanOrientation(projectId: string, unitTypeId: number, block: string, stack: string): Promise<{ deleted: number }> {
    const query = new URLSearchParams({ unitTypeId: String(unitTypeId), block, stack });
    const response = await signedFetch(`${API_BASE}/admin/projects/${encodeURIComponent(projectId)}/floor-plan-orientations?${query}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to remove floor-plan north override');
    return data;
  },

  async applySitePlanNorthToFloorPlans(projectId: string): Promise<{ applied: number; skipped: number; sitePlanNorthAngle: number }> {
    const response = await signedFetch(`${API_BASE}/admin/projects/${encodeURIComponent(projectId)}/floor-plan-orientations/apply-site-plan-north`, {
      method: 'POST', headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to apply site-plan north');
    return data;
  },

  async getAmenities(projectId: string, radius: number = 1000): Promise<AmenitiesResponse> {
    try {
      const params = new URLSearchParams({ 
        projectId,
        radius: radius.toString(),
      });
      const response = await signedFetch(`${API_BASE}/projects/amenities?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch amenities');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getDemandSupply(
    projectId: string,
    radius: number = 5000,
    minRadius: number = 0,
    nationwide: boolean = false,
  ): Promise<DemandSupplyResponse> {
    try {
      const params = new URLSearchParams({
        projectId,
        radius: radius.toString(),
      });
      if (minRadius > 0) params.set('minRadius', minRadius.toString());
      // Whole-island scan — the backend drops both radius bounds.
      if (nationwide) params.set('nationwide', '1');
      const response = await signedFetch(`${API_BASE}/projects/demand-supply?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch demand/supply data');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  /** Super Ratio for an arbitrary map focus (Map Research). */
  async getDemandSupplyByLocation(
    latitude: number,
    longitude: number,
    radius: number = 5000,
    label?: string,
    minRadius: number = 0,
    nationwide: boolean = false,
  ): Promise<DemandSupplyResponse> {
    try {
      const params = new URLSearchParams({
        lat: String(latitude),
        lng: String(longitude),
        radius: radius.toString(),
      });
      if (minRadius > 0) params.set('minRadius', minRadius.toString());
      // Whole-island scan — the backend drops both radius bounds.
      if (nationwide) params.set('nationwide', '1');
      if (label?.trim()) params.set('label', label.trim());
      const response = await signedFetch(`${API_BASE}/projects/demand-supply?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch demand/supply data');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getPriceHistory(unitId: number): Promise<PriceHistoryResponse> {
    try {
      const response = await signedFetch(`${API_BASE}/units/${unitId}/price-history`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch price history');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getBlockCoordinates(projectId: string, projectMediaId?: number, projectPickerMediaId?: number): Promise<{ projectId: string; projectMediaId?: number; projectPickerMediaId?: number; coordinates: Array<{ id: number; blockNumber?: string; stack?: string; facing?: string; x: number; y: number; width: number; height: number }> }> {
    try {
      const params = new URLSearchParams();
      if (projectPickerMediaId != null) {
        params.set('projectPickerMediaId', String(projectPickerMediaId));
      } else if (projectMediaId != null) {
        params.set('projectMediaId', String(projectMediaId));
      }
      const url = `${API_BASE}/projects/${encodeURIComponent(projectId)}/block-coordinates${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await signedFetch(url, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch block coordinates');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async saveBlockCoordinates(projectId: string, coordinates: Array<{ blockNumber?: string; stack?: string; facing?: string; x: number; y: number; width: number; height: number }>, projectMediaId?: number, projectPickerMediaId?: number): Promise<{ success: boolean; message: string; projectMediaId?: number; projectPickerMediaId?: number }> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/block-coordinates`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ coordinates, projectMediaId, projectPickerMediaId }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save block coordinates');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async uploadProjectPickerSitePlan(projectId: string, imageDataUrl: string): Promise<{
    media: {
      id: number;
      projectId: string;
      type: string | null;
      url: string | null;
      description: string | null;
      createdAt: string | null;
      updatedAt: string | null;
    };
  }> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/picker-site-plan`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ imageDataUrl }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload site plan');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getStacks(projectId: string): Promise<{ projectId: string; stacks: string[] }> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/stacks`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch stacks');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getStackBedrooms(projectId: string): Promise<{ projectId: string; stackBedrooms: { [key: string]: number } }> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/stack-bedrooms`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch stack bedrooms');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getUnitTypes(projectId: string): Promise<{ projectId: string; unitTypes: Array<{ id: number; unitType?: string; description?: string; unitTypeCode?: string; bedroom?: number; size?: number }> }> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/unit-types`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch unit types');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getUnitTypeStacks(projectId: string): Promise<{ projectId: string; unitTypeStacks: { [key: string]: string[] } }> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/unit-type-stacks`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch unit type stacks');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getStackSoldPercentages(projectId: string): Promise<{ projectId: string; stackSoldPercentages: { [key: string]: number } }> {
    try {
      const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/stack-sold-percentages`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch stack sold percentages');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getStackAnalysis(projectId: string, stackNumber: string, blockNumber?: string): Promise<{
    projectId: string;
    projectName?: string | null;
    isExecutiveCondo?: boolean;
    liveStatusEnabled?: boolean;
    canViewFacing?: boolean;
    stackNumber: string;
    facing?: string | null;
    totalUnits: number;
    availableUnits: number;
    soldUnits: number;
    bedroomBreakdown: { [key: string]: { available: number; sold: number } };
    unitTypeBreakdown: { [key: string]: { available: number; sold: number } };
    pricePremiumByBedroom: {
      [bedroom: string]: Array<{ floor: string; avgPrice: number; premium: number | null }>;
    };
    units: Array<{
      id: number;
      unitNumber: string;
      floorNumber?: string;
      stackNumber?: string;
      blockNumber: string;
      unitType?: string;
      bedroomNumber?: number;
      size?: number | null;
      price?: number | null;
      pricePSF?: number | null;
      priceDps?: number | null;
      status?: string;
    }>;
  }> {
    try {
      const baseUrl = `${API_BASE}/projects/${encodeURIComponent(projectId)}/stack-analysis/${encodeURIComponent(stackNumber)}`;
      const url = blockNumber ? `${baseUrl}?blockNumber=${encodeURIComponent(blockNumber)}` : baseUrl;
      const response = await signedFetch(url, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch stack analysis');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  // Get current authenticated user from HttpOnly cookie session.
  async getCurrentUser(): Promise<CurrentUser> {
    try {
      const response = await signedFetch(`${API_BASE}/users/me`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch current user');
      }
      
      const data = await response.json();
      return data.user;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getProjectMembers(projectId: string): Promise<ProjectMembersResponse> {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await signedFetch(`${API_BASE}/projects/members?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch project members');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getMyProjectVerifications(): Promise<{ projects: MyProjectVerificationRow[] }> {
    const response = await signedFetch(`${API_BASE}/project-verification/my-projects`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project verifications');
    }
    return response.json();
  },

  async getProjectActivities(query: ProjectActivitiesQuery = {}): Promise<ProjectActivitiesResponse> {
    const params = new URLSearchParams();
    if (query.startDate) {
      params.set('startDate', query.startDate);
    }
    if (query.endDate) {
      params.set('endDate', query.endDate);
    }
    const response = await signedFetch(`${API_BASE}/project-activities?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project activities');
    }
    return response.json();
  },

  async getProjectActivityUsers(query: {
    projectName: string;
    startDate: string;
    endDate: string;
  }): Promise<ProjectActivityUsersResponse> {
    const params = new URLSearchParams();
    params.set('projectName', query.projectName);
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);
    const response = await signedFetch(`${API_BASE}/project-activities/users?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project activity users');
    }
    return response.json();
  },

  async getAllProjectVerifications(): Promise<{ projects: AllProjectVerificationRow[] }> {
    const response = await signedFetch(`${API_BASE}/project-verification/all`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project verifications');
    }
    return response.json();
  },

  async getResponsibleParties(): Promise<{ parties: ResponsiblePartyRow[] }> {
    const response = await signedFetch(`${API_BASE}/project-verification/responsible-parties`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch responsible parties');
    }
    return response.json();
  },

  async submitProjectVerification(
    payload: ProjectVerificationSubmission
  ): Promise<{ verification: ProjectVerificationRecord; resourceLinkAlreadyExists?: boolean }> {
    const response = await signedFetch(`${API_BASE}/project-verification`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to submit verification');
    }
    return response.json();
  },

  async updateProjectVerificationAdminRemark(
    verificationId: number,
    adminRemark: string | null
  ): Promise<{ verification: ProjectVerificationRecord }> {
    const response = await signedFetch(`${API_BASE}/project-verification/${verificationId}/admin-remark`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ adminRemark }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update admin remark');
    }
    return response.json();
  },

  async getProjectActionAccess(): Promise<ProjectActionAccess> {
    const response = await signedFetch(`${API_BASE}/project-action/access`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) return { isAdmin: false, leadsProjects: false, hasTasks: false };
    return response.json();
  },

  /** Omit `projectId` for every list across all projects. */
  async getProjectActionLists(projectId?: string): Promise<{ lists: ProjectActionList[] }> {
    const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    const response = await signedFetch(`${API_BASE}/project-action/lists${query}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load lists');
    }
    return response.json();
  },

  async createProjectActionList(payload: {
    projectId: string;
    title: string;
    sqlStatement: string;
    whatsappMessage: string;
  }): Promise<{ list: ProjectActionList }> {
    const response = await signedFetch(`${API_BASE}/project-action/lists`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to create list');
    }
    return response.json();
  },

  async updateProjectActionList(
    listId: number,
    payload: { title?: string; sqlStatement?: string; whatsappMessage?: string }
  ): Promise<{ list: ProjectActionList }> {
    const response = await signedFetch(`${API_BASE}/project-action/lists/${listId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update list');
    }
    return response.json();
  },

  async deleteProjectActionList(listId: number): Promise<{ ok: boolean }> {
    const response = await signedFetch(`${API_BASE}/project-action/lists/${listId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to delete list');
    }
    return response.json();
  },

  /** Runs a list's SELECT without storing anything — the admin's preview. */
  async previewProjectActionList(payload: {
    listId?: number;
    sqlStatement?: string;
  }): Promise<{ agents: CallListAgent[] }> {
    const response = await signedFetch(`${API_BASE}/project-action/preview`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to run query');
    }
    return response.json();
  },

  /** Puts the chosen agents on the list's project, unassigned. */
  async addProjectActionAgents(
    listId: number,
    agents: CallListAgent[]
  ): Promise<{ created: number; refreshed: number }> {
    const response = await signedFetch(`${API_BASE}/project-action/lists/${listId}/agents`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ agents }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to add agents');
    }
    return response.json();
  },

  async getProjectActionProjects(): Promise<{ projects: ProjectActionProjectSummary[] }> {
    const response = await signedFetch(`${API_BASE}/project-action/my-projects`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load projects');
    }
    return response.json();
  },

  async getProjectActionProjectTasks(projectId: string): Promise<ProjectActionProjectTasks> {
    const response = await signedFetch(
      `${API_BASE}/project-action/projects/${encodeURIComponent(projectId)}/tasks`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load the project list');
    }
    return response.json();
  },

  /** Pass `assignedToCEANumber: null` to hand a batch back to the pool. */
  async assignProjectActionTasks(payload: {
    taskIds: number[];
    assignedToCEANumber: string | null;
    leadRemark?: string | null;
  }): Promise<{ updated: number }> {
    const response = await signedFetch(`${API_BASE}/project-action/tasks/assign`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to assign');
    }
    return response.json();
  },

  /** The lead's brief on one task — separate from assigning it. */
  async setProjectActionLeadRemark(
    taskId: number,
    leadRemark: string | null
  ): Promise<{ id: number; leadRemark: string | null }> {
    const response = await signedFetch(`${API_BASE}/project-action/tasks/${taskId}/lead-remark`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ leadRemark }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save the note');
    }
    return response.json();
  },

  async getMyProjectActionTasks(): Promise<{ projects: MyCallListProject[] }> {
    const response = await signedFetch(`${API_BASE}/project-action/my-tasks`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load your list');
    }
    return response.json();
  },

  async setProjectActionCalled(
    taskId: number,
    called: boolean
  ): Promise<ProjectActionTaskState> {
    const response = await signedFetch(`${API_BASE}/project-action/tasks/${taskId}/called`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ called }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update call status');
    }
    return response.json();
  },

  async setProjectActionRemark(
    taskId: number,
    callRemark: string | null
  ): Promise<ProjectActionTaskState> {
    const response = await signedFetch(`${API_BASE}/project-action/tasks/${taskId}/remark`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ callRemark }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save comment');
    }
    return response.json();
  },

  /** Records that the assignee opened the WhatsApp deep link. */
  async recordProjectActionLinkSent(taskId: number): Promise<ProjectActionTaskState> {
    const response = await signedFetch(`${API_BASE}/project-action/tasks/${taskId}/link-sent`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to record the WhatsApp open');
    }
    return response.json();
  },

  async getProjectActionProgress(): Promise<ProjectActionProgress> {
    const response = await signedFetch(`${API_BASE}/project-action/progress`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load progress');
    }
    return response.json();
  },

  async getMyLeaderVerifications(): Promise<{ designation: string; actions: MyLeaderVerificationRow[] }> {
    const response = await signedFetch(`${API_BASE}/leader-verification/my`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch leader verifications');
    }
    return response.json();
  },

  async submitLeaderVerification(payload: {
    actionId: number;
    takenAction: boolean;
  }): Promise<{ verification: LeaderVerificationRecord }> {
    const response = await signedFetch(`${API_BASE}/leader-verification`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to submit leader verification');
    }
    return response.json();
  },

  async getLeaderVerificationLeaders(): Promise<{ actions: LeaderVerificationLeadersRow[] }> {
    const response = await signedFetch(`${API_BASE}/leader-verification/leaders`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch leader verification leaders');
    }
    return response.json();
  },

  async getCpdAnalytics(): Promise<CpdAnalyticsResponse> {
    const response = await signedFetch(`${API_BASE}/cpd/analytics`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch CPD analytics');
    }
    return response.json();
  },

  async getCpdAnalyticsUsers(query: CpdAnalyticsUsersQuery = {}): Promise<CpdAnalyticsUsersResponse> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.center) params.set('center', query.center);
    if (query.division) params.set('division', query.division);
    if (query.includeAll) params.set('includeAll', 'true');
    if (query.hours !== undefined && query.hours !== null) params.set('hours', String(query.hours));
    if (query.anyOutstanding) params.set('anyOutstanding', 'true');
    if (query.hoursField) params.set('hoursField', query.hoursField);
    if (query.snapshot) params.set('snapshot', query.snapshot);
    if (query.team) params.set('team', query.team);
    if (query.search) params.set('search', query.search);
    if (query.page !== undefined) params.set('page', String(query.page));
    if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDir) params.set('sortDir', query.sortDir);
    if (query.exportAll) params.set('exportAll', 'true');
    const qs = params.toString();
    const response = await signedFetch(`${API_BASE}/cpd/analytics/users${qs ? `?${qs}` : ''}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch CPD analytics users');
    }
    return response.json();
  },

  /** Eligible district directors an admin can view in Team CPD. */
  async getCpdTeamLeaders(): Promise<{ leaders: CpdTeamLeader[] }> {
    const response = await signedFetch(`${API_BASE}/cpd/team/leaders`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch team leaders');
    }
    return response.json();
  },

  /** CPD fulfilment across one leader's downline. Omit `team` for your own team. */
  async getCpdTeamOverview(team?: string): Promise<CpdTeamOverviewResponse> {
    const qs = team ? `?team=${encodeURIComponent(team)}` : '';
    const response = await signedFetch(`${API_BASE}/cpd/team/overview${qs}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch Team CPD overview');
    }
    return response.json();
  },

  async getCpdPerformance(query: CpdPerformanceQuery = {}): Promise<CpdPerformanceResponse> {
    const qs = buildCpdPerformanceParams(query).toString();
    const response = await signedFetch(`${API_BASE}/cpd/analytics/performance${qs ? `?${qs}` : ''}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch CPD performance analytics');
    }
    return response.json();
  },

  async getCpdPerformanceUsers(
    query: CpdPerformanceUsersQuery = {},
  ): Promise<CpdPerformanceUsersResponse> {
    const params = buildCpdPerformanceParams(query);
    if (query.group) params.set('group', query.group);
    if (query.band) params.set('band', query.band);
    if (query.search) params.set('search', query.search);
    const f = query.filters;
    if (f) {
      if (f.name) params.set('fName', f.name);
      if (f.cea) params.set('fCea', f.cea);
      if (f.mobile) params.set('fMobile', f.mobile);
      if (f.division) params.set('fDivision', f.division);
      if (f.school) params.set('fSchool', f.school);
      if (f.performer) params.set('fPerformer', f.performer);
      if (f.total) params.set('fTotal', f.total);
      if (f.pe) params.set('fPe', f.pe);
      if (f.sl) params.set('fSl', f.sl);
      if (f.sdl) params.set('fSdl', f.sdl);
      if (f.income) params.set('fIncome', f.income);
    }
    if (query.page !== undefined) params.set('page', String(query.page));
    if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDir) params.set('sortDir', query.sortDir);
    if (query.exportAll) params.set('exportAll', 'true');
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/cpd/analytics/performance/users${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch CPD performance users');
    }
    return response.json();
  },

  async uploadCpdRequirements(
    rows: CpdRequirementUploadRow[],
    options?: CpdUploadOptions,
  ): Promise<CpdUploadResult> {
    const response = await signedFetch(`${API_BASE}/cpd/requirements/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ rows, ...options }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to upload CPD requirements');
    }
    return response.json();
  },

  async uploadCpdHastor(
    rows: CpdHastorUploadRow[],
    options?: CpdUploadOptions,
  ): Promise<CpdUploadResult> {
    const response = await signedFetch(`${API_BASE}/cpd/hastor/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ rows, ...options }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to upload CPD hastor trainings');
    }
    return response.json();
  },

  async uploadCpdAttendance(
    rows: CpdAttendanceUploadRow[],
    options?: CpdUploadOptions,
  ): Promise<CpdUploadResult> {
    const response = await signedFetch(`${API_BASE}/cpd/attendance/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ rows, ...options }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to upload CPD attendance');
    }
    return response.json();
  },

  async getCpdEmailTemplate(): Promise<CpdEmailTemplate> {
    const response = await signedFetch(`${API_BASE}/cpd/email-template`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch CPD email template');
    }
    return response.json();
  },

  async saveCpdEmailTemplate(payload: {
    subject: string;
    messageBody: string;
  }): Promise<CpdEmailTemplate> {
    const response = await signedFetch(`${API_BASE}/cpd/email-template`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save CPD email template');
    }
    return response.json();
  },

  async searchCpdRecipientEmails(q: string): Promise<CpdRecipientEmail[]> {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    const response = await signedFetch(`${API_BASE}/cpd/recipient-emails?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to search recipient emails');
    }
    const data = await response.json();
    return data.recipients ?? [];
  },

  async sendCpdOutstandingReminder(payload: {
    recipients: string[];
    cc?: string[];
    subject: string;
    messageBody: string;
    attachmentBase64: string;
    attachmentFileName?: string;
  }): Promise<CpdOutstandingReminderSendResult> {
    const response = await signedFetch(`${API_BASE}/cpd/outstanding-reminder/send`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to send CPD outstanding reminder');
    }
    return response.json();
  },

  // Admin: User management
  async getAdminUsers(): Promise<{ users: AdminUserAccess[] }> {
    const response = await signedFetch(`${API_BASE}/admin/users`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch users');
    }
    return response.json();
  },

  async addAdminUser(ceaNumber: string, role: string): Promise<{ user: AdminUserAccess }> {
    const response = await signedFetch(`${API_BASE}/admin/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ceaNumber, role }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to add user');
    }
    return response.json();
  },

  async revokeAdminUser(ceaNumber: string): Promise<{ success: boolean }> {
    const response = await signedFetch(`${API_BASE}/admin/users/${encodeURIComponent(ceaNumber)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to revoke user');
    }
    return response.json();
  },

  async logoutAdminUser(ceaNumber: string): Promise<{ success: boolean; sessionsRevoked: number }> {
    const response = await signedFetch(`${API_BASE}/admin/users/${encodeURIComponent(ceaNumber)}/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to logout user');
    }
    return response.json();
  },

  /**
   * Admin: list every `TransformationPickerExternalUser` row joined with the
   * inviting agent's `User` record so the admin shell can audit share links
   * (recipient, mobile, uuid, token balances, activation timestamps).
   */
  async getAdminPickerExternalUsers(): Promise<{ users: AdminPickerExternalUser[] }> {
    const response = await signedFetch(`${API_BASE}/admin/picker-external-users`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch external users');
    }
    return response.json();
  },

  /**
   * Agent: list the share links the current agent has issued. Read-only —
   * agents cannot mutate token allowances; only admins can. The response
   * shape mirrors `getAdminPickerExternalUsers` minus the `invitedBy` /
   * `invitedByName` columns (always the caller, redundant to send back).
   */
  async getMyPickerExternalUsers(): Promise<{
    users: Array<Omit<AdminPickerExternalUser, 'invitedBy' | 'invitedByName'>>;
  }> {
    const response = await signedFetch(
      `${API_BASE}/transformation-picker-external-users/mine`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch your share links');
    }
    return response.json();
  },

  /** My Shareable Links: the agent's links, newest first, with lead counts, and whether the extra blocks are unlocked. */
  async getShareLinks(): Promise<{ links: ShareLink[]; unlocked: boolean }> {
    const response = await signedFetch(`${API_BASE}/share-links`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load your share links');
    }
    return response.json();
  },

  /** Projects ready to share as a 3D map (Top Viewing, north calibrated, stacks plotted). */
  async getShareLinkEligibleProjects(type: ShareLinkType = 'MAP_3D'): Promise<{ projects: ShareLinkEligibleProject[] }> {
    const response = await signedFetch(
      `${API_BASE}/share-links/eligible-projects?type=${encodeURIComponent(type)}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load projects');
    }
    return response.json();
  },

  /** Whether one project is ready to share as a 3D map — cheaper than the full eligible list. */
  async getShareLinkProjectEligibility(projectId: string): Promise<{ eligible: boolean; reason?: string }> {
    const response = await signedFetch(
      `${API_BASE}/share-links/eligible-projects/${encodeURIComponent(projectId)}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to check project');
    }
    return response.json();
  },

  /** The caller's agent card exactly as a link's visitors see it — the create dialog's preview. */
  async getShareLinkAgentCard(): Promise<{ agent: PublicAgent }> {
    const response = await signedFetch(`${API_BASE}/share-links/agent-card`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load your agent card');
    }
    return response.json();
  },

  async createShareLink(body: {
    linkType: ShareLinkType;
    projectId: string;
    description: string;
    includeSunlight?: boolean;
    connectorTagId?: string | null;
    features?: ShareLinkFeature[];
    showDesignation?: boolean;
    customTitle?: string | null;
  }): Promise<{ link: ShareLink; droppedFeatures?: ShareLinkFeature[] }> {
    const response = await signedFetch(`${API_BASE}/share-links`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to create share link');
    }
    return response.json();
  },

  async getShareLinkLeads(id: number): Promise<{ leads: ShareLinkLead[] }> {
    const response = await signedFetch(`${API_BASE}/share-links/${encodeURIComponent(String(id))}/leads`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load leads');
    }
    return response.json();
  },

  /** Deny (`denied: true`) or restore one lead's access to the link. */
  async setShareLinkLeadAccess(id: number, leadId: number, denied: boolean): Promise<{ id: number; accessDeniedAt: string | null }> {
    const response = await signedFetch(
      `${API_BASE}/share-links/${encodeURIComponent(String(id))}/leads/${encodeURIComponent(String(leadId))}/access`,
      {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ denied }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update access');
    }
    return response.json();
  },

  /** The agent's Super Connector clients with a mobile, and when each was last sent this link. */
  async getShareLinkRecipients(id: number): Promise<{ agentName: string; clients: ShareLinkRecipientClient[] }> {
    const response = await signedFetch(`${API_BASE}/share-links/${encodeURIComponent(String(id))}/recipients`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load your clients');
    }
    return response.json();
  },

  /** Record a WhatsApp send of this link to one client (the chat itself is opened by the caller). */
  async recordShareLinkRecipient(id: number, body: { clientId: string; message: string }): Promise<{
    clientId: string; lastSentAt: string | null; sendCount: number;
  }> {
    const response = await signedFetch(`${API_BASE}/share-links/${encodeURIComponent(String(id))}/recipients`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }, true, false);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to record the send');
    }
    return response.json();
  },

  /** Consumer-site URL for the owner to preview the link, skipping the WhatsApp OTP. Short-lived. */
  async getShareLinkPreviewUrl(id: number): Promise<{ url: string }> {
    const response = await signedFetch(`${API_BASE}/share-links/${encodeURIComponent(String(id))}/preview`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to open preview');
    }
    return response.json();
  },

  /** Edit a live link's description, tag and agent-card title; the project and blocks are fixed. */
  async updateShareLink(id: number, body: {
    description?: string;
    connectorTagId?: string | null;
    showDesignation?: boolean;
    customTitle?: string | null;
  }): Promise<{ link: ShareLink }> {
    const response = await signedFetch(`${API_BASE}/share-links/${encodeURIComponent(String(id))}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save changes');
    }
    return response.json();
  },

  /** Remove from view: revokes the link too; it leaves the list and every statistic. */
  async removeShareLink(id: number): Promise<{ id: number; removedAt: string | null }> {
    const response = await signedFetch(`${API_BASE}/share-links/${encodeURIComponent(String(id))}/remove`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to remove share link');
    }
    return response.json();
  },

  async revokeShareLink(id: number): Promise<{ id: number; revokedAt: string | null }> {
    const response = await signedFetch(`${API_BASE}/share-links/${encodeURIComponent(String(id))}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to revoke share link');
    }
    return response.json();
  },

  /**
   * Admin: bump `MaxTokens` / `RemainingTokens` on a single share invite.
   * Both fields are optional individually but at least one must be present.
   * Backend enforces `remainingTokens <= maxTokens` and returns 400 otherwise.
   */
  async updateAdminPickerExternalUserTokens(
    id: number,
    updates: { maxTokens?: number; remainingTokens?: number },
  ): Promise<{
    user: {
      id: number;
      maxTokens: number;
      remainingTokens: number;
      updatedAt: string | null;
    };
  }> {
    const response = await signedFetch(
      `${API_BASE}/admin/picker-external-users/${encodeURIComponent(String(id))}/tokens`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update tokens');
    }
    return response.json();
  },

  /**
   * Admin: set the trial period on a non-expired share invite.
   * Accepts either `trialDurationMinutes` or `{ value, unit }` where
   * `unit` is `days` or `hours`.
   */
  async updateAdminPickerExternalUserTrial(
    id: number,
    updates:
      | { trialDurationMinutes: number }
      | { value: number; unit: 'days' | 'hours' },
  ): Promise<{
    user: {
      id: number;
      trialDurationMinutes: number;
      expiresAt: string | null;
      updatedAt: string | null;
    };
  }> {
    const response = await signedFetch(
      `${API_BASE}/admin/picker-external-users/${encodeURIComponent(String(id))}/trial`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update trial period');
    }
    return response.json();
  },

  async getAdminAnalyticsDashboard(query: AnalyticsDashboardQuery): Promise<AnalyticsDashboardResponse> {
    const params = new URLSearchParams();
    if (query.startDate) {
      params.set('startDate', query.startDate);
    }
    if (query.endDate) {
      params.set('endDate', query.endDate);
    }
    if (query.userCea && query.userCea.trim().length > 0) {
      params.set('userCea', query.userCea.trim());
    }
    if (query.granularity) {
      params.set('granularity', query.granularity);
    }

    const response = await signedFetch(`${API_BASE}/admin/analytics/dashboard?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch analytics dashboard');
    }
    return response.json();
  },

  async getAdminProjectEssentialDashboard(query: AnalyticsDashboardQuery): Promise<AnalyticsDashboardResponse> {
    const params = new URLSearchParams();
    if (query.startDate) {
      params.set('startDate', query.startDate);
    }
    if (query.endDate) {
      params.set('endDate', query.endDate);
    }
    if (query.granularity) {
      params.set('granularity', query.granularity);
    }

    const response = await signedFetch(`${API_BASE}/admin/analytics/project-essential-dashboard?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch Project Essentials analytics dashboard');
    }
    return response.json();
  },

  async getAdminMaterialEngagement(query: { startDate: string; endDate: string; ceaNumber?: string }): Promise<MaterialEngagementResponse> {
    const params = new URLSearchParams();
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);
    if (query.ceaNumber) params.set('ceaNumber', query.ceaNumber);
    const response = await signedFetch(`${API_BASE}/admin/analytics/material-engagement?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch material engagement analytics');
    }
    return response.json();
  },

  async getAdminMaterialsByIds(ids: string[]): Promise<{ items: MaterialDetailItem[] }> {
    if (ids.length === 0) return { items: [] };
    const params = new URLSearchParams();
    params.set('ids', ids.join(','));
    const response = await signedFetch(`${API_BASE}/admin/analytics/materials-by-ids?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch materials by ids');
    }
    return response.json();
  },

  async getAdminCommunicationOverview(
    query: CommunicationDateRangeQuery
  ): Promise<CommunicationOverviewResponse> {
    const params = new URLSearchParams();
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    const response = await signedFetch(
      `${API_BASE}/admin/communication/overview?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch communication overview');
    }
    return response.json();
  },

  async getAdminCommunicationCampaigns(
    query: CommunicationDateRangeQuery & { channel?: string }
  ): Promise<CommunicationCampaignsResponse> {
    const params = new URLSearchParams();
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    if (query.channel) params.set('channel', query.channel);
    const response = await signedFetch(
      `${API_BASE}/admin/communication/campaigns?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch campaigns');
    }
    return response.json();
  },

  async getAdminCommunicationCampaignDetails(
    query: CommunicationDateRangeQuery & { purpose: string; channel?: string }
  ): Promise<CommunicationCampaignDetailsResponse> {
    const params = new URLSearchParams();
    params.set('purpose', query.purpose);
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    if (query.channel) params.set('channel', query.channel);
    const response = await signedFetch(
      `${API_BASE}/admin/communication/campaign-details?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch campaign details');
    }
    return response.json();
  },

  async getAdminCommunicationAgents(
    query: CommunicationDateRangeQuery & { q?: string }
  ): Promise<CommunicationAgentsResponse> {
    const params = new URLSearchParams();
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    if (query.q) params.set('q', query.q);
    const response = await signedFetch(
      `${API_BASE}/admin/communication/by-agent?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch agents');
    }
    return response.json();
  },

  async getAdminCommunicationByAgent(
    query: CommunicationDateRangeQuery & { ceaNumber: string }
  ): Promise<CommunicationByAgentResponse> {
    const params = new URLSearchParams();
    params.set('ceaNumber', query.ceaNumber);
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    const response = await signedFetch(
      `${API_BASE}/admin/communication/by-agent?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch agent communications');
    }
    return response.json();
  },

  async getAdminProjectAnalytics(query: ProjectAnalyticsQuery): Promise<ProjectAnalyticsResponse> {
    const params = new URLSearchParams();
    params.set('projectName', query.projectName);
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);

    const response = await signedFetch(
      `${API_BASE}/admin/analytics/project-analytics?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project analytics');
    }
    return response.json();
  },

  async getAdminProjectEssentialProjectAnalytics(query: ProjectAnalyticsQuery): Promise<ProjectAnalyticsResponse> {
    const params = new URLSearchParams();
    params.set('projectName', query.projectName);
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);

    const response = await signedFetch(
      `${API_BASE}/admin/analytics/project-essential-project-analytics?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch Project Essentials project analytics');
    }
    return response.json();
  },

  async getAdminProjectMaterialAnalytics(query: ProjectAnalyticsQuery): Promise<ProjectMaterialAnalyticsResponse> {
    const params = new URLSearchParams();
    params.set('projectName', query.projectName);
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);

    const response = await signedFetch(
      `${API_BASE}/admin/analytics/project-materials?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project material analytics');
    }
    return response.json();
  },

  async sendAdminProjectEoiWhatsApp(
    projectName: string,
    ceaNumber: string,
  ): Promise<ProjectEoiSendWhatsAppResponse> {
    const response = await signedFetch(
      `${API_BASE}/admin/analytics/project-eois/send-whatsapp`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectName, ceaNumber }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to send EOI WhatsApp');
    }
    return response.json();
  },

  async getAdminProjectEois(projectName: string): Promise<ProjectEoiResponse> {
    const params = new URLSearchParams();
    params.set('projectName', projectName);

    const response = await signedFetch(
      `${API_BASE}/admin/analytics/project-eois?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project EOIs');
    }
    return response.json();
  },

  async getAdminProjectViewsByCea(query: ProjectViewsByCeaQuery): Promise<ProjectViewsByCeaResponse> {
    const params = new URLSearchParams();
    params.set('projectName', query.projectName);
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);
    if (query.selectedDate && query.selectedDate.trim().length > 0) {
      params.set('selectedDate', query.selectedDate.trim());
    }

    const response = await signedFetch(`${API_BASE}/admin/analytics/project-views?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project views by CEA');
    }
    return response.json();
  },

  async getAdminAgentSoldCounts(projectName: string): Promise<AgentSoldCountsResponse> {
    const params = new URLSearchParams();
    params.set('projectName', projectName);

    const response = await signedFetch(`${API_BASE}/admin/analytics/agent-sold-counts?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch agent sold counts');
    }
    return response.json();
  },

  async getAdminAgentSoldUnits(
    projectName: string,
    ceaNumber: string,
    period?: { startDate: string; endDate: string },
  ): Promise<AgentSoldUnitsResponse> {
    const params = new URLSearchParams();
    params.set('projectName', projectName);
    params.set('ceaNumber', ceaNumber);
    const startDate = period?.startDate?.trim();
    const endDate = period?.endDate?.trim();
    if (startDate && endDate) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    }

    const response = await signedFetch(`${API_BASE}/admin/analytics/agent-sold-units?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch agent sold units');
    }
    return response.json();
  },

  async getAdminAgentNames(ceaNumbers: string[]): Promise<AgentNamesResponse> {
    const response = await signedFetch(`${API_BASE}/admin/analytics/agent-names`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ceaNumbers }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch agent names');
    }
    return response.json();
  },

  async getAdminAgentProfile(ceaNumber: string): Promise<AgentProfileResponse> {
    const params = new URLSearchParams();
    params.set('ceaNumber', ceaNumber);
    const response = await signedFetch(`${API_BASE}/admin/analytics/agent-profile?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch agent profile');
    }
    return response.json();
  },

  async getAdminAgentAchievements(): Promise<AgentAchievementsResponse> {
    const response = await signedFetch(`${API_BASE}/admin/agent-achievements`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load achievements');
    }
    return response.json();
  },

  async getAdminAgentAllSoldUnits(ceaNumber: string): Promise<AgentSoldUnitsResponse> {
    const params = new URLSearchParams();
    params.set('ceaNumber', ceaNumber);
    const response = await signedFetch(`${API_BASE}/admin/analytics/agent-all-sold-units?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch agent sold units');
    }
    return response.json();
  },

  async getAdminSuccessCloser(query: { startDate: string; endDate: string }): Promise<SuccessCloserResponse> {
    const params = new URLSearchParams();
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);

    const response = await signedFetch(`${API_BASE}/admin/analytics/success-closer?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch success closer data');
    }
    return response.json();
  },

  async getAdminShareLinkAnalytics(query: ShareLinkAnalyticsQuery): Promise<ShareLinkAnalyticsResponse> {
    return fetchShareLinkAnalytics('admin/analytics/share-links', query);
  },

  /** Leader → Statistics. Omit `team` for your own team; other teams are admin-only. */
  async getTeamShareLinkAnalytics(
    query: ShareLinkAnalyticsQuery & { team?: string },
  ): Promise<TeamShareLinkAnalyticsResponse> {
    return fetchShareLinkAnalytics('statistics/team/share-links', query, query.team ? { team: query.team } : {});
  },

  /** Project Head → Statistics: the projects the user may choose from. */
  async getStatisticsProjects(): Promise<{ projects: StatisticsProject[]; allAccess: boolean }> {
    const response = await signedFetch(`${API_BASE}/statistics/projects`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch projects');
    }
    return response.json();
  },

  /** Project Head → Statistics, for one project the user is responsible for. */
  async getProjectShareLinkAnalytics(
    query: ShareLinkAnalyticsQuery & { projectId: string },
  ): Promise<ProjectShareLinkAnalyticsResponse> {
    return fetchShareLinkAnalytics('statistics/project/share-links', query, { projectId: query.projectId });
  },

  async getAdminByDateAnalytics(query: { startDate: string; endDate: string }): Promise<AdminByDateAnalyticsResponse> {
    const params = new URLSearchParams();
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);

    const response = await signedFetch(`${API_BASE}/admin/analytics/by-date?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch by-date analytics');
    }
    return response.json();
  },

  async getAdminJyUsers(query: { startDate: string; endDate: string }): Promise<AdminJyUsersResponse> {
    const params = new URLSearchParams();
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);

    const response = await signedFetch(`${API_BASE}/admin/analytics/jy-users?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch JY users');
    }
    return response.json();
  },

  async checkAdminUsers(ceaNumbers: string[], months: number): Promise<AdminCheckUsersResponse> {
    const response = await signedFetch(`${API_BASE}/admin/analytics/check-users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ceaNumbers, months }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to check users');
    }
    return response.json();
  },

  async getAdminByDateQualifyingDetails(query: {
    cea: string;
    startDate: string;
    endDate: string;
  }): Promise<AdminByDateQualifyingDetailsResponse> {
    const params = new URLSearchParams();
    params.set('cea', query.cea);
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);

    const response = await signedFetch(`${API_BASE}/admin/analytics/by-date/qualifying-details?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch qualifying details');
    }
    return response.json();
  },

  async getAdminByDateAllQualifyingTransactions(query: {
    startDate: string;
    endDate: string;
  }): Promise<AdminByDateAllQualifyingTransactionsResponse> {
    const params = new URLSearchParams();
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);

    const response = await signedFetch(`${API_BASE}/admin/analytics/by-date/qualifying-transactions?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch qualifying transactions');
    }
    return response.json();
  },

  async getProjectFactsheet(projectId: string): Promise<ProjectFactsheet> {
    const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/factsheet`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch factsheet');
    }
    return response.json();
  },

  async getProjectMedia(projectId: string): Promise<{ items: ProjectMediaItem[]; total: number }> {
    const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/media`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project media');
    }
    return response.json();
  },

  buildFactsheetDownloadUrl(sourceUrl: string, filename?: string): string {
    const params = new URLSearchParams();
    params.set('url', sourceUrl);
    if (filename && filename.trim().length > 0) {
      params.set('filename', filename.trim());
    }
    return `${API_BASE}/download/factsheet?${params.toString()}`;
  },

  async getProjectBrochure(projectId: string): Promise<ProjectBrochure> {
    const response = await signedFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/brochure`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch brochure');
    }
    return response.json();
  },

  buildBrochureDownloadUrl(sourceUrl: string, filename?: string): string {
    const params = new URLSearchParams();
    params.set('url', sourceUrl);
    if (filename && filename.trim().length > 0) {
      params.set('filename', filename.trim());
    }
    return `${API_BASE}/download/factsheet?${params.toString()}`;
  },

  // ─── EDMs / eBooks scoped to a single project ───
  // Both endpoints are mapped directly under `/api/projects/:projectId`
  // and retrieve the EDMs/eBooks associated with the project via
  // joint tables. Returns standard shapes that feed directly into
  // the `DownloadFileRow` component.
  async getProjectEdms(projectId: string): Promise<{ items: ProjectEdmFile[]; total: number }> {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/edms`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project EDMs');
    }
    return response.json();
  },

  async getProjectEbooks(projectId: string): Promise<{ items: ProjectEbookFile[]; total: number }> {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/ebooks`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project eBooks');
    }
    return response.json();
  },

  // Grouped project files (per-category) sourced from ProjectFile table.
  // Same shape as project-essential's `/projects/:id/files` endpoint,
  // powering the Materials → Files sub-tab in the top-viewing detail.
  async getProjectFilesGrouped(projectId: string): Promise<{
    groups: Array<{
      category: string;
      items: Array<{
        id: number;
        filename: string;
        displayName: string;
        category: string;
        url: string | null;
        createdAt: string | null;
        updatedAt: string | null;
      }>;
    }>;
    total: number;
  }> {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/files`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project files');
    }
    return response.json();
  },

  // ─── Agent Access Folder (per-project curated links) ───
  // Read is available to any authenticated user on the project. Writes are
  // guarded server-side to Head / Deputy Head of Project.
  async getProjectResourceLinks(projectId: string): Promise<{ items: ProjectResourceLink[] }> {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/resource-links`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch resource links');
    }
    return response.json();
  },

  async getProjectResourceLinkPermissions(projectId: string): Promise<{ canManage: boolean }> {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/resource-links/permissions`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to resolve resource-link permissions');
    }
    return response.json();
  },

  async createProjectResourceLink(
    projectId: string,
    payload: { url: string; label?: string | null },
  ): Promise<{ item: ProjectResourceLink }> {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/resource-links`,
      {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: payload.url, label: payload.label || '' }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to create resource link');
    }
    return response.json();
  },

  async updateProjectResourceLink(
    projectId: string,
    id: number,
    payload: { url?: string; label?: string | null },
  ): Promise<{ item: ProjectResourceLink }> {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/resource-links/${encodeURIComponent(String(id))}`,
      {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update resource link');
    }
    return response.json();
  },

  async deleteProjectResourceLink(projectId: string, id: number): Promise<void> {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/resource-links/${encodeURIComponent(String(id))}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to delete resource link');
    }
  },

  async getProjectFileConfig(projectId: string): Promise<ProjectFileConfig> {
    const response = await signedFetch(`${API_BASE}/admin/project-files/${encodeURIComponent(projectId)}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project file config');
    }
    return response.json();
  },

  async setProjectFileTag(projectId: string, type: 'FACTSHEET' | 'BROCHURE', mediaNumber: number): Promise<void> {
    const response = await signedFetch(
      `${API_BASE}/admin/project-files/${encodeURIComponent(projectId)}/${type}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ mediaNumber }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to set project file tag');
    }
  },

  async clearProjectFileTag(projectId: string, type: 'FACTSHEET' | 'BROCHURE'): Promise<void> {
    const response = await signedFetch(
      `${API_BASE}/admin/project-files/${encodeURIComponent(projectId)}/${type}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to clear project file tag');
    }
  },

  async getProjectPriceEstimates(projectId: string): Promise<ProjectPriceEstimateConfig> {
    const response = await signedFetch(`${API_BASE}/admin/project-price-estimates/${encodeURIComponent(projectId)}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project price estimates');
    }
    return response.json();
  },

  async setProjectPriceEstimate(projectId: string, bedroom: number, estPrice: string): Promise<void> {
    const response = await signedFetch(
      `${API_BASE}/admin/project-price-estimates/${encodeURIComponent(projectId)}/${bedroom}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ estPrice }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to set project price estimate');
    }
  },

  async clearProjectPriceEstimate(projectId: string, bedroom: number): Promise<void> {
    const response = await signedFetch(
      `${API_BASE}/admin/project-price-estimates/${encodeURIComponent(projectId)}/${bedroom}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to clear project price estimate');
    }
  },

  buildCsvDownloadUrl(): string {
    return `${API_BASE}/download/csv`;
  },

  async reviewAdminUser(ceaNumber: string, approved: boolean): Promise<{ success: boolean; user: AdminUserAccess }> {
    const response = await signedFetch(`${API_BASE}/admin/users/${encodeURIComponent(ceaNumber)}/review`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ approved }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to review user');
    }
    return response.json();
  },

  // Super Calculator admin endpoints
  async getCalculatorAdminAnalyticsOverview(query: {
    startDate: string;
    endDate: string;
    granularity?: SuperCalculatorAnalyticsGranularity;
  }): Promise<SuperCalculatorOverviewResponse> {
    const params = new URLSearchParams();
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);
    if (query.granularity) {
      params.set('granularity', query.granularity);
    }
    const response = await signedFetch(
      `${API_BASE}/super-calculator/admin/analytics/overview?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch analytics overview');
    }
    return response.json();
  },

  async getCalculatorAdminCalculatorViewers(query: {
    calculator: string;
    startDate: string;
    endDate: string;
  }): Promise<SuperCalculatorViewersResponse> {
    const params = new URLSearchParams();
    params.set('calculator', query.calculator);
    params.set('startDate', query.startDate);
    params.set('endDate', query.endDate);
    const response = await signedFetch(
      `${API_BASE}/super-calculator/admin/analytics/calculator-viewers?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch calculator viewers');
    }
    return response.json();
  },

  async getCalculatorAdminUsers(): Promise<{ users: AdminUserAccess[] }> {
    const response = await signedFetch(`${API_BASE}/super-calculator/admin/users`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch users');
    }
    return response.json();
  },

  async addCalculatorAdminUser(ceaNumber: string, role: string): Promise<{ user: AdminUserAccess }> {
    const response = await signedFetch(`${API_BASE}/super-calculator/admin/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ceaNumber, role }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to add user');
    }
    return response.json();
  },

  async revokeCalculatorAdminUser(ceaNumber: string): Promise<{ success: boolean }> {
    const response = await signedFetch(`${API_BASE}/super-calculator/admin/users/${encodeURIComponent(ceaNumber)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to revoke user');
    }
    return response.json();
  },

  async logoutCalculatorAdminUser(ceaNumber: string): Promise<{ success: boolean; sessionsRevoked: number }> {
    const response = await signedFetch(
      `${API_BASE}/super-calculator/admin/users/${encodeURIComponent(ceaNumber)}/logout`,
      { method: 'POST', headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to logout user');
    }
    return response.json();
  },

  async reviewCalculatorAdminUser(ceaNumber: string, approved: boolean): Promise<{ success: boolean; user: AdminUserAccess }> {
    const response = await signedFetch(
      `${API_BASE}/super-calculator/admin/users/${encodeURIComponent(ceaNumber)}/review`,
      { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ approved }) },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to review user');
    }
    return response.json();
  },

  // ── Success Closer ──────────────────────────────────────────────────
  async getSuccessCloserMySummary(): Promise<SuccessCloserMySummary> {
    const response = await signedFetch(`${API_BASE}/success-closer/my-summary`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch success closer summary');
    }
    return response.json();
  },

  async saveSuccessCloserTestimonial(transactionId: number, testimonial: string): Promise<SuccessCloserTestimonialResponse> {
    const response = await signedFetch(`${API_BASE}/success-closer/transactions/${transactionId}/testimonial`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ testimonial }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save testimonial');
    }
    return response.json();
  },

  async getSuccessCloserLeaderboard(): Promise<SuccessCloserLeaderboardResponse> {
    const response = await signedFetch(`${API_BASE}/success-closer/leaderboard`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch leaderboard');
    }
    return response.json();
  },

  // ── Super App Accelerator poster ─────────────────────────────────────
  async listAcceleratorHotspots(posterKey: string): Promise<AcceleratorHotspot[]> {
    const response = await signedFetch(
      `${API_BASE}/super-accelerator/hotspots?poster=${encodeURIComponent(posterKey)}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load the poster areas');
    }
    const data = await response.json();
    return (data.hotspots || []) as AcceleratorHotspot[];
  },

  async getAcceleratorVideo(hotspotId: number): Promise<AcceleratorVideoSource> {
    const response = await signedFetch(`${API_BASE}/super-accelerator/hotspots/${hotspotId}/video`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Could not prepare the video');
    }
    const data = await response.json();
    return { kind: data.kind, url: `${API_BASE}/${data.path}`, sourceUrl: data.sourceUrl || '' };
  },

  async searchAcceleratorAgents(query: string): Promise<AcceleratorAgentOption[]> {
    const response = await signedFetch(
      `${API_BASE}/super-accelerator/admin/agents?q=${encodeURIComponent(query)}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to search agents');
    }
    const data = await response.json();
    return (data.agents || []) as AcceleratorAgentOption[];
  },

  async saveAcceleratorHotspot(id: number | null, input: AcceleratorHotspotInput): Promise<AcceleratorHotspot> {
    const response = await signedFetch(
      id ? `${API_BASE}/super-accelerator/admin/hotspots/${id}` : `${API_BASE}/super-accelerator/admin/hotspots`,
      { method: id ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(input) },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save the area');
    }
    const data = await response.json();
    return data.hotspot as AcceleratorHotspot;
  },

  async deleteAcceleratorHotspot(id: number): Promise<void> {
    const response = await signedFetch(`${API_BASE}/super-accelerator/admin/hotspots/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to delete the area');
    }
  },

  // ── Accelerator Grant Access ─────────────────────────────────────────
  async listAcceleratorGrants(): Promise<AcceleratorGrantsResponse> {
    const response = await signedFetch(`${API_BASE}/accelerator-grants`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load your invites');
    }
    const data = await response.json();
    return { features: data.features || [], invitees: data.invitees || [] };
  },

  async lookupAcceleratorGrantAgent(identifier: string): Promise<AcceleratorGrantAgent> {
    const response = await signedFetch(
      `${API_BASE}/accelerator-grants/lookup?q=${encodeURIComponent(identifier)}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to look up agent');
    }
    const data = await response.json();
    return data.agent as AcceleratorGrantAgent;
  },

  async grantAcceleratorFeatures(identifier: string, features: AcceleratorGrantFeature[]): Promise<{
    granted: AcceleratorGrantFeature[];
    alreadyInvited: AcceleratorGrantFeature[];
    invitees: AcceleratorInvitee[];
  }> {
    const response = await signedFetch(`${API_BASE}/accelerator-grants`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ identifier, features }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to grant access');
    }
    return response.json();
  },

  async revokeAcceleratorGrant(grantId: number): Promise<AcceleratorInvitee[]> {
    const response = await signedFetch(`${API_BASE}/accelerator-grants/${grantId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to revoke access');
    }
    const data = await response.json();
    return (data.invitees || []) as AcceleratorInvitee[];
  },

  // ── Event Registration ───────────────────────────────────────────────
  async getRegistrationEvents(category?: string, projectId?: string): Promise<RegistrationEventSummary[]> {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (projectId) params.set('projectId', projectId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await signedFetch(`${API_BASE}/events/registration${qs}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch events');
    }
    const data = await response.json();
    return (data.events || []) as RegistrationEventSummary[];
  },

  async getRegisteredTrainingEvents(category?: string, projectId?: string): Promise<RegistrationEventSummary[]> {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (projectId) params.set('projectId', projectId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await signedFetch(`${API_BASE}/events/registration/registered${qs}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch registered trainings');
    }
    const data = await response.json();
    return (data.events || []) as RegistrationEventSummary[];
  },

  async registerForEvent(
    eventId: string,
    ecbInvites: Array<{ name: string; agency: string; mobile?: string | null }> = [],
  ): Promise<{ id: number; eventId: string; createdAt: string }> {
    const response = await signedFetch(`${API_BASE}/events/registration`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ eventId, ecbInvites }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to register for event');
    }
    const data = await response.json();
    return data.booking as { id: number; eventId: string; createdAt: string };
  },

  /**
   * For agents without Price Promo access: records a "shown" notification in
   * Firestore (throttled server-side to once per 24h per CEA) and reports
   * whether the sidebar should auto-open now. The CEA is derived from the auth
   * token server-side, so no argument is needed.
   */
  async recordPricePromoEarlyAccessNotification(): Promise<{ shouldAutoOpen: boolean }> {
    // Non-critical background nudge: opt out of signedFetch's redirect-on-401
    // so a transient auth failure can't bounce the user to /unauthorized. Any
    // non-OK response degrades to "don't auto-open".
    //
    // Send NO body: the HMAC signature is computed over the request body, and
    // an empty JSON body (`{}`) is signed as `"{}"` on the client but as `""`
    // on the server (it treats an empty parsed object as no payload) — a
    // mismatch that yields INVALID_SIGNATURE. With no body, both sign `""`.
    const response = await signedFetch(
      `${API_BASE}/price-promo/early-access-notification`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      },
      true,
      false,
    );
    if (!response.ok) {
      return { shouldAutoOpen: false };
    }
    return response.json() as Promise<{ shouldAutoOpen: boolean }>;
  },

  /** Today's Price Promo reveal budget for the current user (SG-day window). */
  async getPricePromoReveals(): Promise<PricePromoRevealState> {
    const response = await signedFetch(`${API_BASE}/price-promo/reveals`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch price promo reveal state');
    }
    return response.json() as Promise<PricePromoRevealState>;
  },

  /**
   * Record a reveal of a masked Price Promo unit. The server enforces the daily
   * cap and returns `allowed: false` (without charging) once it's reached.
   */
  async recordPricePromoReveal(unitId: number): Promise<PricePromoRevealResult> {
    const response = await signedFetch(`${API_BASE}/price-promo/reveals`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ unitId }),
    });
    if (!response.ok) {
      throw new Error('Failed to record price promo reveal');
    }
    return response.json() as Promise<PricePromoRevealResult>;
  },

  /** Active Price Promo watchlists for the current agent (max 10). */
  async getPricePromoWatchlists(): Promise<PricePromoWatchlist[]> {
    const response = await signedFetch(`${API_BASE}/price-promo/watchlists`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch watchlists');
    }
    const data = await response.json();
    return Array.isArray(data.items) ? (data.items as PricePromoWatchlist[]) : [];
  },

  async createPricePromoWatchlist(input: PricePromoWatchlistInput): Promise<PricePromoWatchlist> {
    const response = await signedFetch(`${API_BASE}/price-promo/watchlists`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create watchlist');
    }
    const data = await response.json();
    return data.item as PricePromoWatchlist;
  },

  async updatePricePromoWatchlist(id: number, input: PricePromoWatchlistInput): Promise<PricePromoWatchlist> {
    const response = await signedFetch(`${API_BASE}/price-promo/watchlists/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to update watchlist');
    }
    const data = await response.json();
    return data.item as PricePromoWatchlist;
  },

  async deletePricePromoWatchlist(id: number): Promise<void> {
    const response = await signedFetch(`${API_BASE}/price-promo/watchlists/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to delete watchlist');
    }
  },

  /** Stamp LastViewedAt so the watchlist's "new match" indicators clear. */
  async markPricePromoWatchlistSeen(id: number): Promise<PricePromoWatchlist> {
    const response = await signedFetch(`${API_BASE}/price-promo/watchlists/${id}/seen`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to update watchlist');
    }
    const data = await response.json();
    return data.item as PricePromoWatchlist;
  },

  /** Active anticipate price promos + the current user's create permissions. */
  async getAnticipatePricePromos(): Promise<AnticipatePricePromoListResponse> {
    const response = await signedFetch(`${API_BASE}/price-promo/anticipate`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch anticipate price promos');
    }
    const data = await response.json();
    return {
      items: Array.isArray(data.items) ? (data.items as AnticipatePricePromo[]) : [],
      canCreate: !!data.canCreate,
      manageableProjects: Array.isArray(data.manageableProjects) ? data.manageableProjects : [],
    };
  },

  async createAnticipatePricePromo(input: AnticipatePricePromoInput): Promise<AnticipatePricePromo> {
    const response = await signedFetch(`${API_BASE}/price-promo/anticipate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create anticipate price promo');
    }
    const data = await response.json();
    return data.item as AnticipatePricePromo;
  },

  async updateAnticipatePricePromo(id: number, input: { price: number; description: string }): Promise<AnticipatePricePromo> {
    const response = await signedFetch(`${API_BASE}/price-promo/anticipate/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to update anticipate price promo');
    }
    const data = await response.json();
    return data.item as AnticipatePricePromo;
  },

  /** Take down an anticipate price promo (soft delete; interests are kept). */
  async deleteAnticipatePricePromo(id: number): Promise<void> {
    const response = await signedFetch(`${API_BASE}/price-promo/anticipate/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to take down anticipate price promo');
    }
  },

  async registerAnticipatePricePromoInterest(
    id: number,
    input: AnticipatePricePromoInterestInput,
  ): Promise<{ interestCount: number }> {
    const response = await signedFetch(`${API_BASE}/price-promo/anticipate/${id}/interest`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to register interest');
    }
    const data = await response.json();
    return { interestCount: Number(data.interestCount) || 0 };
  },

  /** Registered interests for one promo — project head / admin only. */
  async getAnticipatePricePromoInterests(id: number): Promise<AnticipatePricePromoInterest[]> {
    const response = await signedFetch(`${API_BASE}/price-promo/anticipate/${id}/interests`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch registered interests');
    }
    const data = await response.json();
    return Array.isArray(data.items) ? (data.items as AnticipatePricePromoInterest[]) : [];
  },

  /**
   * Mark the current agent as attended for an event. Requires a `token`
   * scanned from the admin's rotating QR (or a longer-lived claim exchanged
   * from one on deep-link open) — the backend verifies signature,
   * eventId match and expiry before upserting the attendance row.
   */
  async markTrainingAttendance(
    eventId: string,
    token: string,
    latitude: number,
    longitude: number,
  ): Promise<{
    id: number;
    eventId: string;
    ceaNumber: string;
    attended: boolean;
    /**
     * `true` only when the row's `Attended` flag flipped from false to
     * true on this request — i.e. the first successful scan for this
     * (CEA, Event, IsClockOut) tuple. Rescans return `false` so the UI
     * can show a distinct "already recorded" message.
     */
    newlyMarked: boolean;
    /** `true` when this scan recorded a clock-out (IsClockOut=1) row. */
    isClockOut: boolean;
  }> {
    const response = await signedFetch(
      `${API_BASE}/events/registration/${encodeURIComponent(eventId)}/attendance`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ token, latitude, longitude }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to mark attendance');
    }
    const data = await response.json();
    return data.attendance as {
      id: number;
      eventId: string;
      ceaNumber: string;
      attended: boolean;
      newlyMarked: boolean;
      isClockOut: boolean;
    };
  },

  /**
   * Admin: fetch a fresh short-lived QR token for an event. Backend rejects
   * with 403 `ATTENDANCE_WINDOW_CLOSED` when called outside the window
   * (defaults to 30min before to 30min after the event start).
   */
  async getEventAttendanceQrToken(
    eventId: string,
    options?: { clockIn?: boolean; clockOut?: boolean },
  ): Promise<{
    token: string;
    expiresAt: string;
    refreshIntervalSeconds: number;
    window: { opensAt: string; closesAt: string };
  }> {
    // `clockOut=1` signs a token whose payload tells the scan endpoint to
    // insert an IsClockIn=0 attendance row. Only relevant for events
    // whose category supports clock-out (C17041 / C17042 today).
    const qs = (options?.clockIn === false || options?.clockOut === true) ? '?clockOut=1' : '';
    const response = await signedFetch(
      `${API_BASE}/admin/events/${encodeURIComponent(eventId)}/attendance-qr-token${qs}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch attendance QR token');
    }
    return response.json();
  },

  async submitTrainingReview(
    eventId: string,
    review: string,
    learningPoints: string,
  ): Promise<TrainingReviewSubmission> {
    const response = await signedFetch(
      `${API_BASE}/events/registration/${encodeURIComponent(eventId)}/reviews`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ review, learningPoints }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to submit review');
    }
    const data = await response.json();
    return data.review as TrainingReviewSubmission;
  },

  // ── Manage Events (Admin) ─────────────────────────────────────────
  async getUpcomingEvents(scope?: {
    includePast?: boolean;
    includeUpcoming?: boolean;
    onlyCategorized?: boolean;
    includeHip?: boolean;
    includeTrans?: boolean;
  }): Promise<UpcomingEventSummary[]> {
    // Build the query string only when an explicit scope is provided so the
    // historical no-arg call keeps hitting the route exactly as before.
    const params = new URLSearchParams();
    if (scope?.includePast !== undefined) {
      params.set('includePast', scope.includePast ? 'true' : 'false');
    }
    if (scope?.includeUpcoming !== undefined) {
      params.set('includeUpcoming', scope.includeUpcoming ? 'true' : 'false');
    }
    if (scope?.onlyCategorized !== undefined) {
      params.set('onlyCategorized', scope.onlyCategorized ? 'true' : 'false');
    }
    if (scope?.includeHip !== undefined) {
      params.set('includeHip', scope.includeHip ? 'true' : 'false');
    }
    if (scope?.includeTrans !== undefined) {
      params.set('includeTrans', scope.includeTrans ? 'true' : 'false');
    }
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/admin/events/upcoming${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch upcoming events');
    }
    const data = await response.json();
    return (data.events || []) as UpcomingEventSummary[];
  },

  async listTransformationEvents(scope?: {
    includePast?: boolean;
    includeUpcoming?: boolean;
  }): Promise<ManagedTransformationEventSummary[]> {
    const params = new URLSearchParams();
    if (scope?.includePast !== undefined) {
      params.set('includePast', scope.includePast ? 'true' : 'false');
    }
    if (scope?.includeUpcoming !== undefined) {
      params.set('includeUpcoming', scope.includeUpcoming ? 'true' : 'false');
    }
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/admin/transformation-events${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to list transformation events');
    }
    const data = await response.json();
    return (data.events || []) as ManagedTransformationEventSummary[];
  },

  async createTransformationManagedEvent(input: {
    name: string;
    description: string;
    venue?: string | null;
    startTime: string;
    endTime: string;
    categories?: string[];
    projectIds?: string[];
    trainerCeaNumbers?: string[];
    locationRequired?: boolean | null;
    qrRefreshIntervalSeconds?: number;
  }): Promise<ManagedTransformationEventSummary> {
    const response = await signedFetch(`${API_BASE}/admin/transformation-events`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to create transformation event');
    }
    const data = await response.json();
    return data.event as ManagedTransformationEventSummary;
  },

  async updateTransformationManagedEvent(
    id: number,
    input: {
      name: string;
      description: string;
      venue?: string | null;
      startTime: string;
      endTime: string;
      categories?: string[];
      projectIds?: string[];
      trainerCeaNumbers?: string[];
      locationRequired?: boolean | null;
      qrRefreshIntervalSeconds?: number;
    },
  ): Promise<ManagedTransformationEventSummary> {
    const response = await signedFetch(
      `${API_BASE}/admin/transformation-events/${encodeURIComponent(String(id))}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update transformation event');
    }
    const data = await response.json();
    return data.event as ManagedTransformationEventSummary;
  },

  async deleteTransformationManagedEvent(id: number): Promise<void> {
    const response = await signedFetch(
      `${API_BASE}/admin/transformation-events/${encodeURIComponent(String(id))}`,
      { method: 'DELETE', headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to delete transformation event');
    }
  },

  async getEventCategories(): Promise<string[]> {
    const response = await signedFetch(`${API_BASE}/admin/events/categories`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch event categories');
    }
    const data = await response.json();
    return (data.categories || []) as string[];
  },

  async listFeatureAccess(): Promise<FeatureAccessData> {
    const response = await signedFetch(`${API_BASE}/admin/feature-access`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch feature access mappings');
    }
    const data = await response.json();
    return {
      mappings: (data.mappings || []) as FeatureAccessMapping[],
      trainings: (data.trainings || []) as MappableTraining[],
      features: (data.features || []) as string[],
    };
  },

  async createFeatureAccess(input: {
    trainingId: number;
    feature: string;
  }): Promise<FeatureAccessMapping> {
    const response = await signedFetch(`${API_BASE}/admin/feature-access`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to create feature access mapping');
    }
    const data = await response.json();
    return data.mapping as FeatureAccessMapping;
  },

  async deleteFeatureAccess(id: number): Promise<void> {
    const response = await signedFetch(
      `${API_BASE}/admin/feature-access/${encodeURIComponent(String(id))}`,
      { method: 'DELETE', headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to remove feature access mapping');
    }
  },

  /**
   * Record that the current agent shared/downloaded a Distinctive design. This
   * grants them Price Promo access (Tab/Table view + Share designer). The CEA is
   * derived from the auth token server-side.
   */
  async recordDistinctiveShare(template: string): Promise<void> {
    const response = await signedFetch(`${API_BASE}/distinctive-shares`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ template }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to record distinctive share');
    }
  },

  // ── Super Connector — Register Interest ──────────────────────────────────

  /**
   * Whether the current agent can register interest in Super Connector — i.e.
   * has shared/downloaded the Super Connector Distinctive design. `registered`
   * indicates they already have an active registration.
   */
  async getConnectorInterestEligibility(): Promise<{ eligible: boolean; registered: boolean }> {
    const response = await signedFetch(`${API_BASE}/connector-interest/eligibility`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to check eligibility');
    }
    return response.json();
  },

  /** Available Super Connector setup-appointment slots (from the admin schedule). */
  async getConnectorInterestSlots(): Promise<ConnectorInterestSlot[]> {
    const response = await signedFetch(`${API_BASE}/connector-interest/slots`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load slots');
    }
    const data = await response.json();
    return Array.isArray(data.slots) ? data.slots : [];
  },

  /** Current one-time Super Connector setup fee configured by the backend. */
  async getSuperConnectorPaymentConfig(): Promise<{ amountCents: number; amountSgd: number }> {
    const response = await signedFetch(`${API_BASE}/super-connector/payment/config`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load the Super Connector setup fee');
    }
    return response.json();
  },

  /** Accept the T&C and book a Super Connector setup appointment. */
  async registerConnectorInterest(payload: {
    name: string;
    mobile: string;
    email: string;
    acknowledged: boolean;
    appointmentAt: string;
  }): Promise<ConnectorInterestRegisterResult> {
    const response = await signedFetch(`${API_BASE}/connector-interest/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to register interest');
    }
    return response.json();
  },

  /** Admin (CEA HT260114): read the weekly availability config. */
  async getConnectorSchedule(): Promise<ConnectorScheduleConfig> {
    const response = await signedFetch(`${API_BASE}/connector-interest/schedule`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load schedule');
    }
    const data = await response.json();
    return {
      days: Array.isArray(data.days) ? data.days : [],
      excludedDates: Array.isArray(data.excludedDates) ? data.excludedDates : [],
    };
  },

  /** Admin (CEA HT260114): save the weekly availability config. */
  async saveConnectorSchedule(days: ConnectorScheduleDay[], excludedDates: string[]): Promise<ConnectorScheduleConfig> {
    const response = await signedFetch(`${API_BASE}/connector-interest/schedule`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ days, excludedDates }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save schedule');
    }
    const data = await response.json();
    return {
      days: Array.isArray(data.days) ? data.days : [],
      excludedDates: Array.isArray(data.excludedDates) ? data.excludedDates : [],
    };
  },

  /** Admin (CEA HT260114): list active connector setup appointments. */
  async getConnectorAppointments(): Promise<ConnectorAppointment[]> {
    const response = await signedFetch(`${API_BASE}/connector-interest/appointments`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load appointments');
    }
    const data = await response.json();
    return Array.isArray(data.appointments) ? data.appointments : [];
  },

  async getRegisteredEventMembers(eventId: string): Promise<RegisteredEventMemberSummary[]> {
    const response = await signedFetch(`${API_BASE}/admin/events/${encodeURIComponent(eventId)}/registered-members`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch registered members');
    }
    const data = await response.json();
    return (data.members || []) as RegisteredEventMemberSummary[];
  },

  async getEventECBInvites(eventId: string): Promise<TrainingECBInviteSummary[]> {
    const response = await signedFetch(`${API_BASE}/admin/events/${encodeURIComponent(eventId)}/ecbs`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch ECBs');
    }
    const data = await response.json();
    return (data.ecbs || []) as TrainingECBInviteSummary[];
  },

  async getEventECBInvitesForInviter(
    eventId: string,
    ceaNumber: string,
  ): Promise<TrainingECBInviteSummary[]> {
    const response = await signedFetch(
      `${API_BASE}/admin/events/${encodeURIComponent(eventId)}/ecbs/${encodeURIComponent(ceaNumber)}`,
      {
        headers: getAuthHeaders(),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch ECBs');
    }
    const data = await response.json();
    return (data.ecbs || []) as TrainingECBInviteSummary[];
  },

  async getTrainingReviewEvents(): Promise<TrainingReviewEventSummary[]> {
    const response = await signedFetch(`${API_BASE}/admin/events/training-reviews/events`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch training review events');
    }
    const data = await response.json();
    return (data.events || []) as TrainingReviewEventSummary[];
  },

  async getTrainingReviewsForEvent(eventId: string): Promise<AdminTrainingReviewSummary[]> {
    const response = await signedFetch(
      `${API_BASE}/admin/events/training-reviews/${encodeURIComponent(eventId)}/reviews`,
      {
        headers: getAuthHeaders(),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch training reviews');
    }
    const data = await response.json();
    return (data.reviews || []) as AdminTrainingReviewSummary[];
  },

  async approveTrainingReviews(reviewIds: number[]): Promise<{ success: boolean; approvedCount: number }> {
    const response = await signedFetch(`${API_BASE}/admin/events/training-reviews/approve`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reviewIds }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to approve training reviews');
    }
    return response.json();
  },

  async getMyEventECBInvites(eventId: string): Promise<TrainingECBInviteSummary[]> {
    const response = await signedFetch(`${API_BASE}/events/registration/${encodeURIComponent(eventId)}/ecbs`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch ECBs');
    }
    const data = await response.json();
    return (data.ecbs || []) as TrainingECBInviteSummary[];
  },

  async updateMyEventECBInvites(
    eventId: string,
    ecbInvites: Array<{ name: string; agency: string; mobile?: string | null }>,
  ): Promise<TrainingECBInviteSummary[]> {
    const response = await signedFetch(`${API_BASE}/events/registration/${encodeURIComponent(eventId)}/ecbs`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ecbInvites }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update ECBs');
    }
    const data = await response.json();
    return (data.ecbs || []) as TrainingECBInviteSummary[];
  },

  async categorizeEvent(
    eventId: string,
    categories: string[],
    projectIds: string[] = [],
    trainerCeaNumbers?: string[],
  ): Promise<{
    success: boolean;
    transformationEvent: {
      eventId: string;
      categories: string[];
      projectIds: string[];
      trainerCeaNumbers?: string[];
    };
  }> {
    const response = await signedFetch(`${API_BASE}/admin/events/categorize`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        eventId,
        categories,
        projectIds,
        ...(trainerCeaNumbers !== undefined ? { trainerCeaNumbers } : {}),
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save event categories');
    }
    return response.json();
  },

  async bulkCategorizeEvents(
    eventIds: string[],
    categories: string[] | undefined,
    projectIds: string[] = [],
    trainerCeaNumbers?: string[],
    locationRequired?: boolean | null,
    qrRefreshIntervalSeconds?: number,
  ): Promise<{
    success: boolean;
    transformationEvents: Array<{
      eventId: string;
      categories: string[];
      projectIds: string[];
      trainerCeaNumbers?: string[];
      locationRequired?: boolean | null;
      qrRefreshIntervalSeconds?: number;
    }>;
  }> {
    const response = await signedFetch(`${API_BASE}/admin/events/categorize/bulk`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        eventIds,
        ...(categories !== undefined ? { categories } : {}),
        projectIds,
        ...(trainerCeaNumbers !== undefined ? { trainerCeaNumbers } : {}),
        ...(locationRequired !== undefined ? { locationRequired } : {}),
        ...(qrRefreshIntervalSeconds !== undefined ? { qrRefreshIntervalSeconds } : {}),
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to save event categories');
    }
    return response.json();
  },


  async getProjectVideos(category: string, limit?: number, search: string = ''): Promise<{ items: ProjectVideoItem[]; total: number }> {
    try {
      const params = new URLSearchParams();
      params.set('category', category);
      if (limit != null) params.set('limit', String(limit));
      if (search) params.set('search', search);

      const response = await signedFetch(`${API_BASE}/projects/videos?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch project videos');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  async getProjectVideoCounts(search: string = ''): Promise<{ counts: Record<string, number>; total: number }> {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const url = `${API_BASE}/projects/video-counts${params.toString() ? '?' + params.toString() : ''}`;

      const response = await signedFetch(url, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch video counts');
      }
      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Connection error: Unable to connect to the server. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  // Server-side Open Graph / oEmbed scraper. Used by the Add Podcast
  // Video dialog to preview a thumbnail + title before submission.
  // Accepts an `AbortSignal` so the caller can cancel stale requests
  // while the user is still typing.
  async fetchVideoMetadata(
    url: string,
    signal?: AbortSignal,
  ): Promise<VideoMetadata> {
    const grant = await ensureScrapeGrant();
    const params = new URLSearchParams({ url });
    const response = await signedFetch(
      `${API_BASE}/video-metadata?${params.toString()}`,
      {
        headers: { ...getAuthHeaders(), 'X-Preview-Grant': grant },
        signal,
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch video metadata');
    }
    return response.json();
  },

  // Persist a new podcast video. The backend re-hosts the supplied
  // `thumbnailUrl` (typically the OG image from `fetchVideoMetadata`)
  // in Firebase Storage and returns the long-lived signed URL in
  // `thumbnailUrl` of the response so the client can stop relying on
  // the upstream platform serving the original.
  // List all active podcast videos with their tagged projects. Used
  // by the Super Picker Podcast tab to populate the feed alongside
  // any client-side mock data.
  async listPodcastVideos(
    options: { videoType?: PodcastVideoType | 'all' } = {},
  ): Promise<PodcastVideoRecord[]> {
    return this._listPodcastVideosInternal({ mine: false, videoType: options.videoType });
  },

  // List only the current caller's podcast videos. Backs the "My
  // Podcasts" management dialog under Others.
  async listMyPodcastVideos(): Promise<PodcastVideoRecord[]> {
    return this._listPodcastVideosInternal({ mine: true });
  },

  // Admin-only listing scoped by review status. Backs the "Manage
  // Podcast Videos" dialog's three tabs (Pending / Verified /
  // Rejected).
  async listPodcastVideosByStatus(
    status: PodcastVideoStatus,
  ): Promise<PodcastVideoRecord[]> {
    return this._listPodcastVideosInternal({ mine: false, status });
  },

  async _listPodcastVideosInternal({
    mine,
    status,
    videoType,
  }: {
    mine: boolean;
    status?: PodcastVideoStatus;
    // Surface-partition filter. Defaults server-side to `'podcast'`
    // for non-mine, non-admin callers so the public Podcast Videos
    // tab continues to render only podcast rows without an explicit
    // opt-in. Pass `'training'` to scope to the per-project Training
    // Videos dialog, or `'all'` to disable the filter (admins).
    videoType?: PodcastVideoType | 'all';
  }): Promise<PodcastVideoRecord[]> {
    const params = new URLSearchParams();
    if (mine) params.set('mine', '1');
    if (status) params.set('status', status);
    if (videoType) params.set('type', videoType);
    const qs = params.toString();
    const url = qs
      ? `${API_BASE}/projects/podcast-videos?${qs}`
      : `${API_BASE}/projects/podcast-videos`;
    const response = await signedFetch(url, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to load podcast videos');
    }
    const payload = (await response.json()) as { videos?: PodcastVideoRecord[] };
    return Array.isArray(payload.videos) ? payload.videos : [];
  },

  async createPodcastVideo(payload: {
    videoUrl: string;
    videoSource: string;
    thumbnailUrl?: string | null;
    title?: string | null;
    projectIds: string[];
    // Surface partition — defaults server-side to `'podcast'`.
    // Pass `'training'` when submitting from the per-project
    // Training Videos dialog so the row drops out of the public
    // Podcast Videos feed.
    videoType?: PodcastVideoType;
  }): Promise<PodcastVideoRecord> {
    const response = await signedFetch(`${API_BASE}/projects/podcast-videos`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        videoUrl: payload.videoUrl,
        videoSource: payload.videoSource,
        thumbnailUrl: payload.thumbnailUrl ?? null,
        title: payload.title ?? null,
        projectIds: payload.projectIds,
        videoType: payload.videoType ?? 'podcast',
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create podcast video');
    }
    return response.json();
  },

  // Replace the tagged projects on an existing podcast video. The
  // URL / source / thumbnail are immutable server-side; only the
  // project membership changes.
  async updatePodcastVideoProjects(
    id: number,
    projectIds: string[],
  ): Promise<{ id: number; projectIds: string[]; updatedAt: string | null }> {
    const response = await signedFetch(`${API_BASE}/projects/podcast-videos/${id}`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ projectIds }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to update podcast video');
    }
    return response.json();
  },

  // Soft-delete a podcast video the caller owns. The backend stamps
  // `DefunctAt` and the row drops out of subsequent feed loads.
  async deletePodcastVideo(id: number): Promise<{ id: number; defunctAt: string }> {
    const response = await signedFetch(`${API_BASE}/projects/podcast-videos/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to delete podcast video');
    }
    return response.json();
  },

  async incrementPodcastVideoView(id: number): Promise<{ id: number; views: number }> {
    const response = await signedFetch(`${API_BASE}/projects/podcast-videos/${id}/view`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to increment view count');
    }
    return response.json();
  },

  async incrementPodcastVideoShare(id: number): Promise<{ id: number; shares: number }> {
    const response = await signedFetch(`${API_BASE}/projects/podcast-videos/${id}/share`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to increment share count');
    }
    return response.json();
  },

  // Admin-only bulk verify. Transitions every active, currently
  // `pending` row whose ID is in the list to `Status = 'verified'`.
  // Backs the "Manage Podcast Videos" admin dialog's Verify button.
  async verifyPodcastVideos(
    ids: number[],
    isPodcastRoom: boolean = false,
  ): Promise<PodcastReviewResponse> {
    const response = await signedFetch(`${API_BASE}/admin/podcast-videos/verify`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids, isPodcastRoom }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to verify podcast videos');
    }
    return response.json();
  },

  // Admin-only bulk reject. Transitions every active, currently
  // `pending` row to `Status = 'rejected'`, stamping `note` as the
  // ReviewNote. The note is required (non-empty) and shown back to
  // the submitter on their My Podcasts Rejected tab.
  async rejectPodcastVideos(
    ids: number[],
    note: string,
  ): Promise<PodcastReviewResponse> {
    const response = await signedFetch(`${API_BASE}/admin/podcast-videos/reject`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids, note }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to reject podcast videos');
    }
    return response.json();
  },

  async searchAgentsByCea(query: string): Promise<AgentCeaSearchResult[]> {
    const q = (query || '').trim();
    if (q.length <= 2) return [];
    const params = new URLSearchParams({ q });
    const response = await signedFetch(
      `${API_BASE}/admin/agents/cea-search?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to search agents');
    }
    const data = await response.json();
    return (data.agents || []) as AgentCeaSearchResult[];
  },

  async getTrainerStatus(): Promise<{ isTrainer: boolean }> {
    const response = await signedFetch(`${API_BASE}/events/trainer/status`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to check trainer status');
    }
    return response.json();
  },

  async getTrainerEvents(): Promise<UpcomingEventSummary[]> {
    const response = await signedFetch(`${API_BASE}/events/trainer/events`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load trainer events');
    }
    const data = await response.json();
    return (data.events || []) as UpcomingEventSummary[];
  },

  // ── Training Sessions (Admin) ─────────────────────────────────────
  async listEventSessions(eventId: string): Promise<TrainingSessionSummary[]> {
    const response = await signedFetch(
      `${API_BASE}/admin/events/${encodeURIComponent(eventId)}/sessions`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load sessions');
    }
    const data = await response.json();
    return (data.sessions || []) as TrainingSessionSummary[];
  },

  async createEventSession(
    eventId: string,
    input: { name: string; sessionDate: string; startTime: string; endTime: string },
  ): Promise<TrainingSessionSummary> {
    const response = await signedFetch(
      `${API_BASE}/admin/events/${encodeURIComponent(eventId)}/sessions`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to create session');
    }
    const data = await response.json();
    return data.session as TrainingSessionSummary;
  },

  async updateEventSession(
    eventId: string,
    sessionId: number,
    input: { name?: string; sessionDate?: string; startTime?: string; endTime?: string },
  ): Promise<TrainingSessionSummary> {
    const response = await signedFetch(
      `${API_BASE}/admin/events/${encodeURIComponent(eventId)}/sessions/${sessionId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update session');
    }
    const data = await response.json();
    return data.session as TrainingSessionSummary;
  },

  async deleteEventSession(eventId: string, sessionId: number): Promise<void> {
    const response = await signedFetch(
      `${API_BASE}/admin/events/${encodeURIComponent(eventId)}/sessions/${sessionId}`,
      { method: 'DELETE', headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to delete session');
    }
  },

  /**
   * Admin: fetch a fresh short-lived QR token for a specific training session.
   * Rejects with 403 `ATTENDANCE_WINDOW_CLOSED` if the session's scan window
   * (session StartTime/EndTime ± configured buffers) is not currently open.
   */
  async getSessionAttendanceQrToken(
    sessionId: number,
    options?: { clockIn?: boolean; clockOut?: boolean },
  ): Promise<{
    token: string;
    expiresAt: string;
    refreshIntervalSeconds: number;
    eventId: string;
    sessionId: number;
    window: { opensAt: string; closesAt: string };
  }> {
    const qs = (options?.clockIn === false || options?.clockOut === true) ? '?clockOut=1' : '';
    const response = await signedFetch(
      `${API_BASE}/admin/sessions/${sessionId}/attendance-qr-token${qs}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch session attendance QR token');
    }
    return response.json();
  },

  async getSessionAttendedMembers(sessionId: number): Promise<{
    members: AttendedEventMemberSummary[];
    sessionLatitude: number | null;
    sessionLongitude: number | null;
  }> {
    const response = await signedFetch(
      `${API_BASE}/admin/sessions/${sessionId}/attended-members`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch session attendees');
    }
    const data = await response.json();
    return {
      members: (data.members || []) as AttendedEventMemberSummary[],
      sessionLatitude:
        typeof data.sessionLatitude === 'number' && Number.isFinite(data.sessionLatitude)
          ? data.sessionLatitude
          : null,
      sessionLongitude:
        typeof data.sessionLongitude === 'number' && Number.isFinite(data.sessionLongitude)
          ? data.sessionLongitude
          : null,
    };
  },

  async updateSessionQrHostLocation(
    sessionId: number,
    coords: { latitude: number; longitude: number },
  ): Promise<{ updated: boolean; latitude: number | null; longitude: number | null }> {
    const response = await signedFetch(
      `${API_BASE}/admin/sessions/${sessionId}/qr-host-location`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(coords),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update session QR host location');
    }
    return response.json();
  },

  async getSessionAgentNames(sessionId: number, ceaNumbers: string[]): Promise<AgentNamesResponse> {
    const response = await signedFetch(
      `${API_BASE}/admin/sessions/${sessionId}/agent-names`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ceaNumbers }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch agent names');
    }
    return response.json();
  },

  async updateSessionAttendedMemberTimes(
    sessionId: number,
    ceaNumber: string,
    times: {
      clockedInAt?: string | null;
      clockedOutAt?: string | null;
      latitude?: number | null;
      longitude?: number | null;
    },
  ): Promise<{ success: boolean }> {
    const response = await signedFetch(
      `${API_BASE}/admin/sessions/${sessionId}/attendance/${encodeURIComponent(ceaNumber)}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(times),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update attendance times');
    }
    return response.json();
  },

  // ─── Super Picker Token Usage Analytics ─────────────────────────────────
  // Backed by `backend/src/routes/superPickerAnalytics.ts`. Agent endpoints
  // are scoped server-side by `InvitedBy = req.user.cea`, so the SPA never
  // needs to filter by agent here.

  async getSuperPickerAgentAnalyticsOverview(query: {
    startDate?: string;
    endDate?: string;
  }): Promise<TokenUsageOverviewResponse> {
    const params = new URLSearchParams();
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/super-picker-analytics/agent/overview${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load token usage analytics');
    }
    return response.json();
  },

  async searchSuperPickerAgentClients(q: string): Promise<{ clients: TokenUsageClientSearchRow[] }> {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/super-picker-analytics/agent/clients${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to search clients');
    }
    return response.json();
  },

  /**
   * Drill-down for a logical (mobile-deduped) client. Pass the `clientKey`
   * returned in `topClients[].clientKey` / search results.
   */
  async getSuperPickerAgentClientDetail(
    clientKey: string,
    query: { startDate?: string; endDate?: string },
  ): Promise<TokenUsageClientDetailResponse> {
    const params = new URLSearchParams();
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/super-picker-analytics/agent/clients/by-key/${encodeURIComponent(
        clientKey,
      )}${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load client analytics');
    }
    return response.json();
  },

  // ─── Admin variants. Gated server-side by `requireAdmin`. ─────────────

  async getSuperPickerAdminAnalyticsOverview(query: {
    startDate?: string;
    endDate?: string;
    agentCea?: string;
  }): Promise<TokenUsageAdminOverviewResponse> {
    const params = new URLSearchParams();
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    if (query.agentCea) params.set('agentCea', query.agentCea);
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/super-picker-analytics/admin/overview${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load token usage analytics');
    }
    return response.json();
  },

  async searchSuperPickerAdminAgents(
    q: string,
    query: { startDate?: string; endDate?: string } = {},
  ): Promise<{ agents: TokenUsageAgentSummary[] }> {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/super-picker-analytics/admin/agents${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load agents');
    }
    return response.json();
  },

  async searchSuperPickerAdminClients(
    q: string,
    query: { agentCea?: string } = {},
  ): Promise<{ clients: TokenUsageClientSearchRow[] }> {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (query.agentCea) params.set('agentCea', query.agentCea);
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/super-picker-analytics/admin/clients${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to search clients');
    }
    return response.json();
  },

  async getSuperPickerAdminClientDetail(
    clientKey: string,
    query: { startDate?: string; endDate?: string; agentCea?: string },
  ): Promise<TokenUsageClientDetailResponse> {
    const params = new URLSearchParams();
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    if (query.agentCea) params.set('agentCea', query.agentCea);
    const qs = params.toString();
    const response = await signedFetch(
      `${API_BASE}/super-picker-analytics/admin/clients/by-key/${encodeURIComponent(
        clientKey,
      )}${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load client analytics');
    }
    return response.json();
  },

  async getSituationMapDistrictAgentStats(districtCode: string): Promise<{
    districtCode: string;
    projectCount: number;
    agentsWithTransaction: number;
    byDivision: Array<{ divisionName: string; count: number }>;
  }> {
    const params = new URLSearchParams({ districtCode });
    const response = await signedFetch(`${API_BASE}/projects/situation-map/district-agent-stats?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch district agent stats');
    }
    return response.json();
  },

  async getSituationMapAgentStats(projectId: string, radiusKm = 5): Promise<{
    projectId: string;
    radiusKm: number;
    projectCount: number;
    agentsWithTransaction: number;
    byDivision: Array<{
      divisionName: string;
      count: number;
      agents: Array<{
        name: string;
        ceaNumber: string;
        mobile: string | null;
        email: string | null;
        photo: string | null;
        designation: string | null;
        transactionCount: number;
        latestOptionDate: string | null;
      }>;
    }>;
  }> {
    const params = new URLSearchParams({ projectId, radiusKm: String(radiusKm) });
    const response = await signedFetch(`${API_BASE}/projects/situation-map/agent-stats?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch situation map agent stats');
    }
    return response.json();
  },

  /** Admin only. Pass projectId when known; projectName resolves cross-project reference units. */
  async getDeveloperInsights(params: { projectId?: string | null; projectName?: string | null }): Promise<DeveloperInsightsResponse> {
    const query = new URLSearchParams();
    if (params.projectId) query.set('projectId', params.projectId);
    if (params.projectName) query.set('projectName', params.projectName);
    const response = await signedFetch(`${API_BASE}/projects/developer-insights?${query}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load developer insights');
    }
    return response.json();
  },

  async getSituationMapRadiusStats(projectId: string, radiusKm = 5): Promise<{
    projectId: string;
    projectName: string;
    radiusKm: number;
    project: { oursTx: number; eraTx: number; totalMarket: number };
    radius: { oursTx: number; totalMarket: number; projectCount: number };
  }> {
    const params = new URLSearchParams({ projectId, radiusKm: String(radiusKm) });
    const response = await signedFetch(`${API_BASE}/projects/situation-map/radius-stats?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch situation map radius stats');
    }
    return response.json();
  },

  async getSituationMapProjectsWithTransactions(): Promise<{ projectIds: string[] }> {
    const response = await signedFetch(`${API_BASE}/projects/situation-map/projects-with-transactions`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch projects with transactions');
    }
    return response.json();
  },

  async getSituationMapMarketShares(): Promise<{
    shares: Record<string, { oursTx: number; eraTx: number; totalMarket: number }>;
  }> {
    const response = await signedFetch(`${API_BASE}/projects/situation-map/market-shares`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch market shares');
    }
    return response.json();
  },

  async getSituationMapSupplementDetails(projectIds: string[]): Promise<{ projects: Array<{
    id: string; name: string; developer: string | null;
    mapLatitude: number | null; mapLongitude: number | null;
    area: string | null; district: string | null; topDate: number | null;
    totalUnits: number; soldUnits: number;
    projectCategory: string | null; projectCategoryId: string | null;
    tenure: null; completedDate: null; address: null;
  }> }> {
    const response = await signedFetch(`${API_BASE}/projects/situation-map/supplement-details`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectIds }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch supplement details');
    }
    return response.json();
  },

  async getSituationMapProjectTxBreakdown(projectId: string, scope: 'era' | 'market' | 'huttons'): Promise<{
    projectId: string;
    scope: string;
    prelaunch: number;
    postLaunch: number;
    periods: Array<{ label: string; count: number }>;
  }> {
    const params = new URLSearchParams({ projectId, scope });
    const response = await signedFetch(`${API_BASE}/projects/situation-map/project-tx-breakdown?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project tx breakdown');
    }
    return response.json();
  },

  async getSituationMapProjectAgentDetail(projectId: string): Promise<{
    projectId: string;
    agentsWithTransaction: number;
    byDivision: Array<{
      divisionName: string;
      count: number;
      agents: Array<{
        name: string;
        ceaNumber: string;
        mobile: string | null;
        email: string | null;
        photo: string | null;
        designation: string | null;
        transactionCount: number;
        latestOptionDate: string | null;
      }>;
    }>;
    periodBreakdown: Array<{ label: string; count: number }>;
  }> {
    const params = new URLSearchParams({ projectId });
    const response = await signedFetch(`${API_BASE}/projects/situation-map/project-agent-detail?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to fetch project agent detail');
    }
    return response.json();
  },

  // ── Super Showcase ──────────────────────────────────────────────────────

  /** Published showcases only — drafts live in Manage Super Showcase. */
  async getSuperShowcases(): Promise<SuperShowcase[]> {
    const response = await signedFetch(`${API_BASE}/super-showcase/showcases`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load Super Showcase');
    }
    const data = await response.json();
    return Array.isArray(data.items) ? (data.items as SuperShowcase[]) : [];
  },

  async getSuperShowcaseRegionData(region: SuperShowcaseRegion): Promise<{
    region: SuperShowcaseRegion;
    projects: Array<{ projectId: string; projectName: string | null; categoryId: string | null; isEc: boolean; district: string | null; region: string | null; landSaleId: number | null; averagePsf: number | null; psfPpr: number | null; psfPprSource: string | null }>;
    landCosts: Array<{ sourceType: 'PROJECT' | 'GLS'; id: string; pipelineItemId: number; projectId?: string | null; landSaleId?: number | null; name: string; district: string | null; region: string; section?: string | null; isEc: boolean; psfPpr: number | null }>;
  }> {
    const response = await signedFetch(`${API_BASE}/super-showcase/region-data/${encodeURIComponent(region)}`, { headers: getAuthHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw createApiError(response.status, data, 'Failed to load region data');
    return data;
  },

  /**
   * Share link that opens this showcase in Super Picker. The server shortens it
   * on the first call and returns the stored short link after that.
   */
  async getSuperShowcaseShareLink(showcaseId: number): Promise<{ url: string; fullUrl: string }> {
    const response = await signedFetch(`${API_BASE}/super-showcase/showcases/${showcaseId}/share-link`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw createApiError(response.status, data, 'Failed to create the share link');
    return { url: String(data.url || ''), fullUrl: String(data.fullUrl || '') };
  },

  /** Views / unique agents / hotspot clicks per showcase (admin only). */
  async getSuperShowcaseViewStats(options: { refresh?: boolean } = {}): Promise<SuperShowcaseViewStats> {
    const query = options.refresh ? '?refresh=1' : '';
    const response = await signedFetch(`${API_BASE}/super-showcase/admin/stats${query}`, {
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw createApiError(response.status, data, 'Failed to load showcase view counts');
    return {
      since: String(data.since || ''),
      asOf: String(data.asOf || ''),
      items: Array.isArray(data.items) ? (data.items as SuperShowcaseViewStat[]) : [],
    };
  },

  /** Every showcase, published or not (admin only). */
  async getAdminSuperShowcases(): Promise<SuperShowcase[]> {
    const response = await signedFetch(`${API_BASE}/super-showcase/admin/showcases`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load Super Showcase');
    }
    const data = await response.json();
    return Array.isArray(data.items) ? (data.items as SuperShowcase[]) : [];
  },

  async createSuperShowcase(input: SuperShowcaseInput): Promise<SuperShowcase> {
    const response = await signedFetch(`${API_BASE}/super-showcase/admin/showcases`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to create showcase');
    }
    const data = await response.json();
    return data.item as SuperShowcase;
  },

  async updateSuperShowcase(id: number, input: SuperShowcaseInput): Promise<SuperShowcase> {
    const response = await signedFetch(`${API_BASE}/super-showcase/admin/showcases/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update showcase');
    }
    const data = await response.json();
    return data.item as SuperShowcase;
  },

  async deleteSuperShowcase(id: number): Promise<void> {
    const response = await signedFetch(`${API_BASE}/super-showcase/admin/showcases/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to delete showcase');
    }
  },

  /* ---------------- 3D Sitemap ---------------- */

  /** Enabled projects. Agents see published entries; admins see drafts too. */
  async getSitemap3dProjects(): Promise<Sitemap3dProject[]> {
    const response = await signedFetch(`${API_BASE}/picker-3d-sitemap/projects`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to load the 3D Sitemap');
    }
    const data = await response.json();
    return Array.isArray(data.projects) ? (data.projects as Sitemap3dProject[]) : [];
  },

  async getSitemap3dProject(projectId: string): Promise<Sitemap3dDetail> {
    const response = await signedFetch(`${API_BASE}/picker-3d-sitemap/projects/${encodeURIComponent(projectId)}`, {
      cache: 'no-store',
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw createApiError(response.status, data, 'Failed to load the site plan');
    }
    return data as Sitemap3dDetail;
  },

  /** Projects an admin may add (admin only). */
  async getSitemap3dCandidates(search?: string): Promise<Sitemap3dCandidate[]> {
    const query = search && search.trim() ? `?${new URLSearchParams({ q: search.trim() })}` : '';
    const response = await signedFetch(`${API_BASE}/picker-3d-sitemap/candidates${query}`, {
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw createApiError(response.status, data, 'Failed to load selectable projects');
    }
    return Array.isArray(data.candidates) ? (data.candidates as Sitemap3dCandidate[]) : [];
  },

  async addSitemap3dProject(projectId: string): Promise<void> {
    const response = await signedFetch(`${API_BASE}/picker-3d-sitemap/projects`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ projectId }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to add the project');
    }
  },

  async updateSitemap3dProject(
    projectId: string,
    input: { isPublished?: boolean; sortOrder?: number },
  ): Promise<void> {
    const response = await signedFetch(`${API_BASE}/picker-3d-sitemap/projects/${encodeURIComponent(projectId)}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to update the project');
    }
  },

  async removeSitemap3dProject(projectId: string): Promise<void> {
    const response = await signedFetch(`${API_BASE}/picker-3d-sitemap/projects/${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to remove the project');
    }
  },

  /** Upload or replace the site plan. Replacing clears the plotted stacks. */
  async uploadSitemap3dSitePlan(projectId: string, imageDataUrl: string): Promise<void> {
    const response = await signedFetch(`${API_BASE}/picker-3d-sitemap/projects/${encodeURIComponent(projectId)}/site-plan`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ imageDataUrl }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to upload the site plan');
    }
  },

  /** Replace the plotted stacks in one shot — the payload is the full set. */
  async saveSitemap3dBlocks(projectId: string, blocks: Sitemap3dBlockInput[]): Promise<Sitemap3dBlock[]> {
    const response = await signedFetch(`${API_BASE}/picker-3d-sitemap/projects/${encodeURIComponent(projectId)}/blocks`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ blocks }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw createApiError(response.status, data, 'Failed to save the plotted stacks');
    }
    return Array.isArray(data.blocks) ? (data.blocks as Sitemap3dBlock[]) : [];
  },

  /* ---------------- Site plan Drone view ---------------- */

  /** The project's active drone view, or null. Any Super Picker session. */
  async getDroneView(projectId: string): Promise<DroneView | null> {
    const response = await signedFetch(`${API_BASE}/picker-drone-view/projects/${encodeURIComponent(projectId)}`, {
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw createApiError(response.status, data, 'Failed to load the drone view');
    }
    return (data.droneView as DroneView | null) ?? null;
  },

  /** Every drone view, inactive included (admin only). */
  async getDroneViews(): Promise<DroneViewEntry[]> {
    const response = await signedFetch(`${API_BASE}/picker-drone-view/projects`, {
      cache: 'no-store',
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw createApiError(response.status, data, 'Failed to load drone views');
    }
    return Array.isArray(data.droneViews) ? (data.droneViews as DroneViewEntry[]) : [];
  },

  /** Projects a drone view can be attached to (admin only). */
  async getDroneViewCandidates(search?: string): Promise<DroneViewCandidate[]> {
    const query = search && search.trim() ? `?${new URLSearchParams({ q: search.trim() })}` : '';
    const response = await signedFetch(`${API_BASE}/picker-drone-view/candidates${query}`, {
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw createApiError(response.status, data, 'Failed to load projects');
    }
    return Array.isArray(data.candidates) ? (data.candidates as DroneViewCandidate[]) : [];
  },

  async saveDroneView(
    projectId: string,
    input: { url: string; label?: string | null; isActive: boolean },
  ): Promise<DroneViewEntry> {
    const response = await signedFetch(`${API_BASE}/picker-drone-view/projects/${encodeURIComponent(projectId)}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw createApiError(response.status, data, 'Failed to save the drone view');
    }
    return data.droneView as DroneViewEntry;
  },

  async removeDroneView(projectId: string): Promise<void> {
    const response = await signedFetch(`${API_BASE}/picker-drone-view/projects/${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw createApiError(response.status, errorData, 'Failed to remove the drone view');
    }
  },

  /** Can `url` be iframed? Suggests the embeddable tour inside it when not (admin only). */
  async probeDroneViewUrl(url: string): Promise<DroneViewProbe> {
    const response = await signedFetch(`${API_BASE}/picker-drone-view/probe`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ url }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw createApiError(response.status, data, 'Failed to check the URL');
    }
    return data as DroneViewProbe;
  },

};

/* ------------------------------------------------------------------ *
 * Site plan Drone view — an admin-entered aerial / 360° tour URL,     *
 * embedded as the Drone mode of the site-plan card.                   *
 * ------------------------------------------------------------------ */

export interface DroneView {
  url: string;
  label: string | null;
}

export interface DroneViewEntry extends DroneView {
  projectId: string;
  projectName: string;
  isActive: boolean;
  updatedAt?: string;
}

export interface DroneViewCandidate {
  projectId: string;
  projectName: string;
  district: string | null;
}

export interface DroneViewProbe {
  url: string;
  /** null when the page could not be reached — the check is advisory. */
  embeddable: boolean | null;
  reason: string | null;
  /** An embeddable tour iframed inside a page that blocks framing itself. */
  suggestedUrl: string | null;
  title: string | null;
}

/* ------------------------------------------------------------------ *
 * 3D Sitemap — admin-curated site plans for projects the Top Viewing  *
 * Site Plans tab does not carry (completed / non-new-launch).         *
 * ------------------------------------------------------------------ */

/** One project in the 3D Sitemap list. */
export interface Sitemap3dProject {
  projectId: string;
  projectName: string;
  district: string | null;
  region: string | null;
  area: string | null;
  location: string | null;
  topDate: string | null;
  totalUnits: number | null;
  mapLatitude: number | null;
  mapLongitude: number | null;
  /** Site plan image, used as the card thumbnail. Null until one is uploaded. */
  thumbnailUrl: string | null;
  stackCount: number;
  /** Set once an admin has calibrated north; 3DS needs it to place the sun. */
  northAngle: number | null;
  isPublished: boolean;
  sortOrder: number;
}

/** A plotted stack. `floors` drives the massing height in 3D / 3DS. */
export interface Sitemap3dBlock {
  id?: number;
  blockNumber: string | null;
  stack: string | null;
  facing: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  floors: number | null;
}

/** A block marker (blockNumber only) or a stack. One of the two is required. */
export interface Sitemap3dBlockInput {
  blockNumber?: string | null;
  stack?: string | null;
  facing?: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  floors?: number | null;
}

/**
 * `SitePlanData` with the module's richer block rows. Identical on the wire —
 * `toSitePlanData` drops the extra fields when handing one to a shared viewer.
 */
export interface Sitemap3dSitePlan {
  imageUrl: string;
  blocks: Sitemap3dBlock[];
  blockNumbers?: string[];
  bounds?: { x: number; y: number; width: number; height: number } | null;
  mediaSource?: 'projectPickerMedia' | 'projectMedia' | null;
  projectMediaId?: number | null;
  projectPickerMediaId?: number | null;
}

/** The media identity a shared site-plan viewer (or the north calibration
 *  endpoint) needs. Blocks are dropped: those callers take towers separately. */
export function sitemap3dSitePlanToSitePlanData(sitePlan: Sitemap3dSitePlan): SitePlanData {
  return {
    imageUrl: sitePlan.imageUrl,
    blocks: [],
    blockNumbers: sitePlan.blockNumbers ?? [],
    bounds: sitePlan.bounds ?? null,
    mediaSource: sitePlan.mediaSource ?? null,
    projectMediaId: sitePlan.projectMediaId ?? null,
    projectPickerMediaId: sitePlan.projectPickerMediaId ?? null,
  };
}

export interface Sitemap3dDetail {
  project: Omit<Sitemap3dProject, 'thumbnailUrl' | 'stackCount' | 'northAngle' | 'sortOrder'>;
  sitePlan: Sitemap3dSitePlan | null;
  northAngle: number | null;
}

/** A project an admin may add: SG, residential, not already a new launch. */
export interface Sitemap3dCandidate {
  projectId: string;
  projectName: string;
  district: string | null;
  region: string | null;
  area: string | null;
  location: string | null;
  topDate: string | null;
  categoryId: string | null;
}

export interface PodcastVideoProjectTag {
  id: string;
  name: string;
}

/**
 * Review lifecycle:
 *   - `pending`  → new submission awaiting admin decision
 *   - `verified` → admin approved; appears in the public Podcast Videos feed
 *   - `rejected` → admin rejected with a required `reviewNote`. Terminal:
 *                  the submitter must delete and re-submit to try again.
 */

export type PodcastVideoStatus = 'pending' | 'verified' | 'rejected';

/**
 * Surface partition for podcast videos:
 *   - `'podcast'`  → public Podcast Videos tab
 *   - `'training'` → per-project Training Videos dialog
 * Used as both a filter (when listing) and a request body field
 * (when creating).
 */

export type PodcastVideoType = 'podcast' | 'training';

/**
 * Response shape for the admin verify / reject endpoints. Both
 * actions echo back the transition + the stamped audit fields so
 * the client can optimistically reconcile its tab caches without a
 * full reload.
 */

export interface PodcastReviewResponse {
  reviewedIds: number[];
  status: 'verified' | 'rejected';
  reviewedAt: string;
  reviewedBy: string;
  reviewedByName: string | null;
  // Only present on the reject response.
  reviewNote?: string;
  // Only present on the verify response — echoes the
  // podcast-room flag the admin selected in the verify-confirmation
  // dialog so callers can patch their cached row without a refetch.
  isPodcastRoom?: boolean;
  affected: number | null;
}

export interface PodcastVideoRecord {
  id: number;
  videoUrl: string;
  videoSource: string;
  // OG / oEmbed title scraped at submit time. Nullable when the
  // upstream provider didn't expose one.
  title?: string | null;
  thumbnailUrl: string | null;
  views: number;
  shares: number;
  createdBy: string;
  // Joined from `User` on `CEANumber = CreatedBy`. Only set on the
  // `listPodcastVideos` response (the create response returns just
  // the CEA in `createdBy`).
  createdByName?: string | null;
  createdByPhoto?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  // Present on `listPodcastVideos` responses; absent on the
  // `createPodcastVideo` response (which returns `projectIds` only).
  projects?: PodcastVideoProjectTag[];
  // Present on the `createPodcastVideo` response so callers can
  // optimistically slot the new video into a project-filtered view.
  projectIds?: string[];
  // Review lifecycle state. New rows come back as `pending`; the
  // public Podcast Videos tab only renders `verified` rows; the
  // Manage Podcast Videos dialog operates exclusively on `pending`.
  status?: PodcastVideoStatus;
  // ISO timestamp of the admin review action (verify or reject).
  // Surfaced on the public Podcast Videos card in place of
  // `createdAt` so users see when the video became visible.
  // `null` while the row is still pending review.
  reviewedAt?: string | null;
  // CEANumber of the reviewing admin. Same nullability rule as
  // `reviewedAt` — only set after a decision was made.
  reviewedBy?: string | null;
  // Display name of the reviewing admin, resolved from the joined
  // `User` row. Allows the card to render "Verified/Rejected by
  // {name}" without an extra lookup. Falls back to `reviewedBy`
  // (CEA) when the join can't resolve a User row.
  reviewedByName?: string | null;
  // Free-text rejection reason. Required (non-empty) when
  // `status === 'rejected'`; null otherwise. Surfaced to the
  // submitter on the My Podcasts "Rejected" tab so they know what
  // to fix before re-submitting.
  reviewNote?: string | null;
  // `true` when an admin marked the video as filmed in the company
  // podcast room during verification. The public Podcast Videos tab
  // sorts these ahead of regular submissions; defaults to `false`
  // server-side so absence is equivalent to "not a podcast-room
  // recording".
  isPodcastRoom?: boolean;
  // Surface partition. `'podcast'` rows back the public Podcast
  // Videos tab; `'training'` rows back the per-project Training
  // Videos dialog opened from `TopViewingSitePlanDetail`. Defaults
  // server-side to `'podcast'` for legacy rows.
  videoType?: PodcastVideoType;
}

export interface VideoMetadata {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  video: string | null;
  siteName: string | null;
  type: string | null;
}

export interface TrainingSessionSummary {
  id: number;
  eventId: string;
  transformationTrainingId: number;
  name: string;
  sessionDate: string; // YYYY-MM-DD
  startTime: string;   // HH:MM:SS
  endTime: string;     // HH:MM:SS
  hasAttendance: boolean;
  attendeeCount: number;
  latitude?: number | null;
  longitude?: number | null;
}

// ─── Token Usage Analytics types ─────────────────────────────────────────
// Mirrors the response shapes defined in
// `backend/src/routes/superPickerAnalytics.ts`. Kept in this file (rather
// than a shared `types.ts`) because the existing SPA pattern colocates
// response types next to their api.ts methods.

export interface TokenUsageClientSummary {
  /** Stable drill-down key (normalized mobile, or `eu:<id>` fallback). */
  clientKey: string;
  externalUserId: number;
  uuid: string;
  name: string | null;
  mobile: string;
  module: string;
  projectId: string | null;
  /** Sum of MaxTokens across every share link in the logical client. */
  maxTokens: number;
  /** Sum of RemainingTokens across every share link in the logical client. */
  remainingTokens: number;
  tokensSpent: number;
  lastSpendAt: string | null;
  activatedAt: string | null;
  createdAt: string | null;
  /** Number of distinct share links represented by this logical client. */
  shareLinkCount: number;
  /** Distinct InvitedBy CEAs across share links (admin scope only). */
  invitedByCeas: string[];
}

export interface TokenUsageClientSearchRow {
  clientKey: string;
  externalUserId: number;
  uuid: string;
  name: string | null;
  mobile: string;
  module: string;
  projectId: string | null;
  maxTokens: number;
  remainingTokens: number;
  shareLinkCount: number;
  invitedByCeas: string[];
  activatedAt: string | null;
  createdAt: string | null;
}

export interface TokenUsageAgentSummary {
  ceaNumber: string;
  name: string | null;
  totalClients: number;
  activeClients: number;
  tokensSpent: number;
  lastSpendAt: string | null;
}

export interface TokenUsageProjectSummary {
  projectId: string;
  projectName: string | null;
  tokensSpent: number;
  uniqueClients: number;
}

export interface TokenUsageBlockSummary {
  projectId: string;
  projectName: string | null;
  block: string;
  tokensSpent: number;
}

export interface TokenUsageUnitSummary {
  projectId: string;
  projectName: string | null;
  unitKey: string;
  tokensSpent: number;
}

export interface TokenUsageSectionBreakdown {
  projectDetails: number;
  projectResearch: number;
  unknown: number;
}

export interface TokenUsageDayPoint {
  date: string;
  tokens: number;
}

export interface TokenUsageOverviewResponse {
  startDate: string | null;
  endDate: string | null;
  totals: {
    tokensSpent: number;
    activeClients: number;
    totalClients: number;
    avgTokensPerActiveClient: number;
  };
  sectionBreakdown: TokenUsageSectionBreakdown;
  spendByDay: TokenUsageDayPoint[];
  topProjects: TokenUsageProjectSummary[];
  topBlocks: TokenUsageBlockSummary[];
  topUnits: TokenUsageUnitSummary[];
  topClients: TokenUsageClientSummary[];
}

export interface TokenUsageClientEvent {
  id: number;
  externalUserId: number;
  createdAt: string | null;
  section: string | null;
  projectId: string | null;
  projectName: string | null;
  block: string | null;
  unitKey: string | null;
  rawRevealKey: string;
}

export interface TokenUsageClientShareLink {
  externalUserId: number;
  uuid: string;
  name: string | null;
  module: string;
  projectId: string | null;
  invitedBy: string;
  maxTokens: number;
  remainingTokens: number;
  tokensSpent: number;
  activatedAt: string | null;
  createdAt: string | null;
}

export interface TokenUsageClientDetailResponse {
  client: TokenUsageClientSummary;
  invitedBy: string;
  shareLinks: TokenUsageClientShareLink[];
  events: TokenUsageClientEvent[];
  sectionBreakdown: TokenUsageSectionBreakdown;
  projectBreakdown: TokenUsageProjectSummary[];
  blockBreakdown: TokenUsageBlockSummary[];
  spendByDay: TokenUsageDayPoint[];
}

export interface TokenUsageAdminOverviewResponse extends TokenUsageOverviewResponse {
  topAgents: TokenUsageAgentSummary[];
}

// ---------------------------------------------------------------------------
// Super Picker AI chatbot
// ---------------------------------------------------------------------------

export interface SuperPickerChatHistoryTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface SuperPickerChatProjectCard {
  projectId: string;
  name: string;
  developer?: string | null;
  category?: string | null;
  district?: string | null;
  region?: string | null;
  address?: string | null;
  tenure?: string | null;
  topYear?: number | null;
  totalUnits?: number | null;
  soldUnits?: number | null;
  nearestMrt?: string | null;
  walkingTimeToMrtMin?: number | null;
  distanceKm?: number | null;
  matchedUnitCount?: number;
  availableByBedroom?: Record<string, { availableUnits: number; fromPrice: number | null; fromPsf: number | null } | null>;
  nearbyAmenities?: { count: number; nearest: Array<{ name: string; level?: string | null; distanceMeters: number }> };
}

export interface SuperPickerChatUnitCard {
  unitId: number | string;
  projectId?: string | null;
  projectName: string;
  unitNumber: string;
  blockNumber?: string | null;
  bedroom?: number | null;
  bathroom?: number | null;
  areaSqft?: number | string | null;
  price?: number | null;
  priceDps?: number | null;
  psf?: number | null;
  floor?: number | null;
  unitTypeDescription?: string | null;
  floorPlanUrl?: string | null;
  discountPercent?: number | null;
  betterThanCount?: number | null;
  unitStatus?: string | null;
}

export interface SuperPickerChatRecommendationCard extends Omit<SuperPickerChatUnitCard, 'unitId'> {
  unitId: number;
  rank: number;
  score: number;
  district?: string | null;
  region?: string | null;
  isFacingPool?: boolean;
  isFacingBinSubstation?: boolean;
  walkingTimeToMrtMin?: number | null;
  primarySchoolsWithin1km?: number;
  projectSoldPercent?: number | null;
  componentScores?: Record<string, number>;
  factors?: string[];
}

export interface SuperPickerChatResults {
  projects?: SuperPickerChatProjectCard[];
  units?: SuperPickerChatUnitCard[];
  recommendations?: SuperPickerChatRecommendationCard[];
}

export interface SuperPickerChatResponse {
  reply: string;
  /** Human-readable labels of the tools the assistant used this turn. */
  toolSummary: string[];
  conversationId: number;
  messageId: number;
  /** Card data extracted from the turn's tool results. */
  results?: SuperPickerChatResults;
}

export async function sendSuperPickerChatMessage(params: {
  message: string;
  conversationId?: number | null;
  history?: SuperPickerChatHistoryTurn[];
}): Promise<SuperPickerChatResponse> {
  const response = await signedFetch(`${API_BASE}/super-picker-chat/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: params.message,
      conversationId: params.conversationId ?? undefined,
      history: params.history ?? [],
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw createApiError(response.status, payload, 'Chat request failed');
  }
  return payload as SuperPickerChatResponse;
}

export async function downloadSuperPickerChatReport(conversationId: number): Promise<Blob> {
  const response = await signedFetch(`${API_BASE}/super-picker-chat/report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationId }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw createApiError(response.status, payload, 'Report generation failed');
  }
  return response.blob();
}

export async function sendSuperPickerChatFeedback(params: {
  messageId: number;
  feedback: 'up' | 'down';
  reason?: string;
  comment?: string;
}): Promise<void> {
  const response = await signedFetch(`${API_BASE}/super-picker-chat/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw createApiError(response.status, payload, 'Failed to record feedback');
  }
}

export async function transcribeSuperPickerChatAudio(audioDataUrl: string): Promise<string> {
  const response = await signedFetch(`${API_BASE}/super-picker-chat/transcribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ audio: audioDataUrl }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw createApiError(response.status, payload, 'Transcription failed');
  }
  return typeof payload?.text === 'string' ? payload.text : '';
}
